import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { TeamSorterView, ScorecardView, PlayoffsView, FinalStandingsView } from "./AthleticsDashboard";
import { SupervisorNotesEditor, IncidentReportEditor } from "./SupervisorDashboard";
import { NavigatorIntramuralsLanding } from "./NavigatorIntramurals";
import SchedulesPicker from "./SchedulesPicker";
import LoginsView from "./LoginsView";
import AdminGroupLoginsView from "./AdminGroupLoginsView";
import AdminIntramuralUploadsView from "./AdminIntramuralUploadsView";
import MenuSearchBar from "../components/MenuSearchBar";
import MissingLoginAlert from "./MissingLoginAlert";
import ColorWarRevealEmailPanel from "./ColorWarRevealEmailPanel";
import ColorWarLeadershipPanel from "./admin/ColorWarLeadershipPanel";
import TransportAuditView from "./TransportAuditView";
import DivisionNotificationsView from "./DivisionNotificationsView";
import DismissalReportsView from "./DismissalReportsView";
import ThreadedMessagesInbox from "./ThreadedMessagesInbox";
import QuickAttendanceWidget from "../components/QuickAttendanceWidget";
import PlannedAbsencesPanel from "../components/PlannedAbsencesPanel";
import TripRostersView from "../components/TripRostersView";
import CamperPhotosView from "./CamperPhotosView";
import CamperPhoto from "../components/CamperPhoto";
import ActivityCapsView from "./admin/ActivityCapsView";
import CapacityAccordion from "./admin/CapacityAccordion";
import CamperLocationsView from "./admin/CamperLocationsView";
import ServiceProvidersView from "./admin/ServiceProvidersView";
import ParentSurveyAdminView from "./admin/ParentSurveyAdminView";
import MilestoneCertificatesView from "./admin/MilestoneCertificatesView";
import TeamsAdminView from "./admin/TeamsAdminView";
import CarnivalSnackAdmin from "./admin/CarnivalSnackAdmin";
import SafeModePanel from "./admin/SafeModePanel";
import SmsSettingsPanel, { SmsOffBanner } from "./admin/SmsSettingsPanel";
import ReorderableGrid from "../components/ReorderableGrid";
import { 
  LogOut, Plus, Trash2, Edit, Upload, RefreshCw, 
  Settings, Database, Users, FileSpreadsheet, 
  CheckCircle, XCircle, Clock, Search, Download, Eye, EyeOff,
  ClipboardCheck, ChevronDown, ChevronUp, MapPin, Droplets,
  UserX, ArrowRightLeft, ArrowLeft, Bus, Star, ClipboardList,
  ExternalLink, AlertTriangle, Utensils, Trophy, Flag, ShieldCheck, BarChart3, Waves,
  User, Phone, Mail, Stethoscope, CalendarDays, Heart, Brain, FileText, Calendar, MessageSquare, Save, X, GraduationCap, Swords, Sparkles, Bell, BellOff, LogIn, Shirt, Sun, Moon, KeyRound, CheckCircle2, Flame, Cake, Send, Crown
} from "lucide-react";
import { ScrollArea } from "../components/ui/scroll-area";
import { Progress } from "../components/ui/progress";

function formatPhone(raw) {
  if (!raw) return "";
  const d = raw.replace(/\D/g, "");
  if (d.length === 10) return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
  if (d.length === 11 && d[0] === "1") return `(${d.slice(1,4)}) ${d.slice(4,7)}-${d.slice(7)}`;
  return raw;
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = `${process.env.REACT_APP_BACKEND_URL}/api/assets/rrdc-logo.png`;

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [customFields, setCustomFields] = useState([]);
  const [groups, setGroups] = useState([]);
  const [campers, setCampers] = useState([]);
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [camperSearch, setCamperSearch] = useState("");
  const [currentView, setCurrentView] = useState("menu");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([new Date().getFullYear()]);
  const [alumniCount, setAlumniCount] = useState(0);
  const [alumni, setAlumni] = useState([]);
  const [selectedAlumni, setSelectedAlumni] = useState(null);
  const [rosterSubView, setRosterSubView] = useState("groups");
  const [rosterGroupFilter, setRosterGroupFilter] = useState("");
  const [rosterGroupSearch, setRosterGroupSearch] = useState("");
  const [rosterCamperSearch, setRosterCamperSearch] = useState("");
  const [rosterData, setRosterData] = useState(null);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [rosterSelectedDate, setRosterSelectedDate] = useState("");
  const [fireDrillOpen, setFireDrillOpen] = useState(false);
  const [fireDrillDate, setFireDrillDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [fireDrillLoading, setFireDrillLoading] = useState(false);
  const [playdateLoading, setPlaydateLoading] = useState(false);
  const [hcDeliveryLoading, setHcDeliveryLoading] = useState(false);

  const handleHobbyReport = async (scope) => {
    const labels = {
      mariner_boys: "Mariner Boys",
      mariner_girls: "Mariner Girls",
      navigator_hobbies: "Navigator Hobbies",
      navigator_intensives: "Navigator Intensives",
    };
    const label = labels[scope] || scope;
    try {
      toast.info(`Generating ${label}…`);
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/admin/hobbies-report.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { scope },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `hobbies_${scope}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${label}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to download ${label}`);
    }
  };

  const handleHcDeliverability = async () => {
    setHcDeliveryLoading(true);
    try {
      toast.info("Pulling HC delivery log from Twilio…");
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/admin/hc-deliverability.xlsx`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { days: 7 },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `hc_deliverability_last7d.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Downloaded HC Delivery Log");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Twilio fetch failed");
    } finally {
      setHcDeliveryLoading(false);
    }
  };
  const [townBackfillRunning, setTownBackfillRunning] = useState(false);

  const handlePullTowns = async () => {
    try {
      const startRes = await axios.post(
        `${API}/admin/playdate-list/backfill-towns`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const startStatus = startRes.data?.status;
      if (startStatus === "already_running") {
        toast.info("Town pull is already in progress — see status badge");
      } else {
        toast.success("Town pull started — runs in background, ~7 min for ~900 campers");
      }
      setTownBackfillRunning(true);
      // Poll status every 8s until done
      const poll = setInterval(async () => {
        try {
          const r = await axios.get(`${API}/admin/playdate-list/backfill-towns/status`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const s = r.data;
          if (!s?.running) {
            clearInterval(poll);
            setTownBackfillRunning(false);
            const u = s?.updated || 0;
            const sk = s?.skipped || 0;
            const er = s?.errors || 0;
            toast.success(`Town pull complete — updated ${u}, skipped ${sk}, errors ${er}. You can regenerate the Playdate List now.`);
          }
        } catch (_e) { /* ignore transient poll errors */ }
      }, 8000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start town pull");
    }
  };

  const handleDownloadPlaydateList = async () => {
    setPlaydateLoading(true);
    try {
      const res = await axios.get(`${API}/admin/playdate-list.pdf`, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const yr = new Date().getFullYear();
      a.download = `Playdate_List_${yr}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Playdate List PDF downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate Playdate List PDF");
    } finally {
      setPlaydateLoading(false);
    }
  };

  const handleDownloadFireDrillPDF = async () => {
    if (!fireDrillDate) { toast.error("Pick a date first"); return; }
    setFireDrillLoading(true);
    try {
      const res = await axios.get(`${API}/admin/fire-drill-pdf`, {
        params: { date: fireDrillDate },
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Fire_Drill_${fireDrillDate}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Fire Drill PDF for ${fireDrillDate} downloaded`);
      setFireDrillOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate Fire Drill PDF");
    } finally {
      setFireDrillLoading(false);
    }
  };
  const [dietaryReports, setDietaryReports] = useState([]);
  const [dietaryDetail, setDietaryDetail] = useState(null);
  const [dietaryLoading, setDietaryLoading] = useState(false);
  
  // Messages state
  const [adminMsgGroups, setAdminMsgGroups] = useState([]);
  const [adminMsgSelectedGroup, setAdminMsgSelectedGroup] = useState(null);
  const [adminMsgMessages, setAdminMsgMessages] = useState([]);
  const [adminMsgLoading, setAdminMsgLoading] = useState(false);
  const [adminMsgText, setAdminMsgText] = useState("");
  const [adminMsgChannel, setAdminMsgChannel] = useState("sms");
  const [adminMsgSending, setAdminMsgSending] = useState(false);
  const [adminMsgCamperFilter, setAdminMsgCamperFilter] = useState("");
  const [adminMsgDateFrom, setAdminMsgDateFrom] = useState("");
  const [adminMsgDateTo, setAdminMsgDateTo] = useState("");
  const [adminMsgSelectedCamperIds, setAdminMsgSelectedCamperIds] = useState(new Set());
  const [stuckMessages, setStuckMessages] = useState([]);
  const [showStuckModal, setShowStuckModal] = useState(false);
  const [reassignTargetMsg, setReassignTargetMsg] = useState(null);
  const [reassignSearch, setReassignSearch] = useState("");
  
  // Get token for API calls
  const token = localStorage.getItem("token");

  // Trip rosters (Overnight & Broadway) state
  const [tripRostersData, setTripRostersData] = useState(null);
  const [tripExceptionsPrinting, setTripExceptionsPrinting] = useState(false);
  const [tripRostersSyncing, setTripRostersSyncing] = useState(false);
  const [tripRostersLoading, setTripRostersLoading] = useState(false);

  // Dialog states
  const [fieldDialog, setFieldDialog] = useState({ open: false, field: null });
  const [groupDialog, setGroupDialog] = useState({ open: false, group: null });

  const fetchData = useCallback(async () => {
    try {
      const [fieldsRes, groupsRes, syncRes] = await Promise.all([
        axios.get(`${API}/custom-fields`),
        axios.get(`${API}/groups`),
        axios.get(`${API}/sync/status`)
      ]);
      setCustomFields(fieldsRes.data);
      setGroups(groupsRes.data);
      setSyncStatus(syncRes.data);
      
      // Auto-detect if sync is already running
      if (syncRes.data.status === "running") {
        setSyncing(true);
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCampers = useCallback(async (search = "") => {
    try {
      const res = await axios.get(`${API}/campers`, { params: { search, year: selectedYear } });
      setCampers(res.data);
    } catch (error) {
      toast.error("Failed to load campers");
    }
  }, [selectedYear]);

  const fetchYears = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sessions/years`);
      setAvailableYears(res.data.years || [new Date().getFullYear()]);
      setAlumniCount(res.data.alumni_count || 0);
    } catch { /* non-critical */ }
  }, []);

  const fetchAlumni = useCallback(async (year) => {
    try {
      const params = year ? { year } : {};
      const res = await axios.get(`${API}/sessions/alumni`, { params });
      setAlumni(res.data || []);
    } catch { toast.error("Failed to load alumni"); }
  }, []);

  const fetchRosterForGroup = useCallback(async (groupCode, date) => {
    setRosterLoading(true);
    try {
      const params = { group_code: groupCode };
      if (date) params.date = date;
      const res = await axios.get(`${API}/roster/today`, { params });
      setRosterData(res.data);
    } catch {
      toast.error("Failed to load roster");
    } finally {
      setRosterLoading(false);
    }
  }, []);

  const [pipData, setPipData] = useState(null);
  const [pipExpanded, setPipExpanded] = useState(false);

  const fetchPipCompletion = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/pip-completion`);
      setPipData(res.data);
    } catch (error) {
      // non-critical
    }
  }, []);

  const [evalsData, setEvalsData] = useState(null);
  const [evalSummary, setEvalSummary] = useState(null);
  const [summarizing, setSummarizing] = useState(false);
  const [tripForms, setTripForms] = useState([]);
  const [tripFilterGroup, setTripFilterGroup] = useState("");
  const [tripFilterDate, setTripFilterDate] = useState("");
  const [buddySheets, setBuddySheets] = useState([]);
  const [buddyFilterGroup, setBuddyFilterGroup] = useState("");
  const [buddyFilterDate, setBuddyFilterDate] = useState("");
  const [buddyFilterPeriod, setBuddyFilterPeriod] = useState("");
  const [viewingSheet, setViewingSheet] = useState(null);
  const [unassignedCampers, setUnassignedCampers] = useState([]);
  const [unassignedSearch, setUnassignedSearch] = useState("");
  const [assigningGroup, setAssigningGroup] = useState("");
  const [selectedUnassigned, setSelectedUnassigned] = useState(new Set());
  const [assigning, setAssigning] = useState(false);
  const [selectedCamper, setSelectedCamper] = useState(null);
  const [groupFilter, setGroupFilter] = useState("");
  const [camperSubView, setCamperSubView] = useState("groups"); // "groups" | "list"
  const [messagingEnabled, setMessagingEnabled] = useState(true);
  const [featureToggles, setFeatureToggles] = useState({
    mariner_hobbies_enabled: true,
    navigator_hobbies_enabled: true,
    navigator_intramurals_enabled: true,
  });
  const [hobbyTestDate, setHobbyTestDate] = useState("");
  const [counselorContacts, setCounselorContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactUploading, setContactUploading] = useState(false);
  const [staffSyncing, setStaffSyncing] = useState(false);
  const [incidentReports, setIncidentReports] = useState([]);
  const [incidentSearch, setIncidentSearch] = useState("");
  const [incidentDateFilter, setIncidentDateFilter] = useState("");
  const incidentFilterActive = incidentSearch.trim() !== "" || incidentDateFilter !== "";
  const filteredIncidents = incidentReports.filter((r) => {
    const q = incidentSearch.trim().toLowerCase();
    const nameOk = !q || (r.camper_names || []).join(" ").toLowerCase().includes(q) || (r.group_code || "").toLowerCase().includes(q);
    const dateOk = !incidentDateFilter || r.date === incidentDateFilter;
    return nameOk && dateOk;
  });
  const [expandedIncidentId, setExpandedIncidentId] = useState(null);
  const [editingReport, setEditingReport] = useState(null);

  const fetchEvaluations = useCallback(async () => {
    try {
      const evalsRes = await axios.get(`${API}/evaluations`, { params: { year: selectedYear } });
      setEvalsData(evalsRes.data);
      try {
        const summaryRes = await axios.get(`${API}/evaluations/summary/latest`);
        setEvalSummary(summaryRes.data);
      } catch (e) { /* no summary yet */ }
    } catch (e) { /* non-critical */ }
  }, [selectedYear]);

  const fetchTripForms = useCallback(async (group = "", date = "") => {
    try {
      const params = { year: selectedYear };
      if (group) params.group_code = group;
      if (date) params.date = date;
      const res = await axios.get(`${API}/trip-forms`, { params });
      setTripForms(res.data);
    } catch (e) { /* non-critical */ }
  }, [selectedYear]);

  const fetchBuddySheets = useCallback(async (group = "", date = "", period = "") => {
    try {
      const params = { year: selectedYear };
      if (group) params.group_code = group;
      if (date) params.date = date;
      if (period) params.period = period;
      const res = await axios.get(`${API}/buddy-sheets/archive`, { params });
      setBuddySheets(res.data);
    } catch (e) { /* non-critical */ }
  }, [selectedYear]);

  const fetchUnassignedCampers = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/campers/unassigned`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnassignedCampers(res.data);
    } catch (e) { /* non-critical */ }
  }, []);

  const fetchCounselorContacts = useCallback(async () => {
    setContactsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/admin/counselor-contacts`, { headers: { Authorization: `Bearer ${token}` } });
      setCounselorContacts(res.data || []);
    } catch { /* non-critical */ }
    finally { setContactsLoading(false); }
  }, []);

  const handleContactUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setContactUploading(true);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/admin/counselor-contacts/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${res.data.imported} contacts`);
      if (res.data.errors?.length > 0) {
        toast.error(`${res.data.errors.length} rows had errors`);
      }
      fetchCounselorContacts();
    } catch {
      toast.error("Failed to upload contacts");
    } finally {
      setContactUploading(false);
      e.target.value = "";
    }
  };

  const deleteContact = async (groupCode) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/admin/counselor-contacts/${groupCode}`, { headers: { Authorization: `Bearer ${token}` } });
      setCounselorContacts(prev => prev.filter(c => c.group_code !== groupCode));
      toast.success("Contact removed");
    } catch { toast.error("Failed to delete"); }
  };

  const assignContactGroup = async (personId, newGroupCode) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/admin/counselor-contacts/${newGroupCode}`, {
        counselor_name: counselorContacts.find(c => c.person_id === personId)?.counselor_name || "",
        phone_number: counselorContacts.find(c => c.person_id === personId)?.phone_number || "",
        person_id: personId,
      }, { headers: { Authorization: `Bearer ${token}` } });
      // Remove old UNASSIGNED entry
      await axios.delete(`${API}/admin/counselor-contacts/UNASSIGNED-${personId}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
      fetchCounselorContacts();
      toast.success(`Assigned to ${newGroupCode}`);
    } catch { toast.error("Failed to assign"); }
  };

  const vacateGroup = async (groupCode) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/admin/counselor-contacts/${groupCode}`, {
        counselor_name: "(Vacant)", phone_number: "", person_id: null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      fetchCounselorContacts();
      toast.success(`${groupCode} is now vacant — previous counselor moved to Reserve`);
    } catch { toast.error("Failed to unassign"); }
  };

  const syncStaffFromCM = async () => {
    setStaffSyncing(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/admin/counselor-contacts/sync-staff`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.error) {
        toast.error(res.data.error);
        if (res.data.available_positions) {
          console.log("Available positions:", res.data.available_positions);
        }
      } else {
        toast.success(`Synced ${res.data.imported} head counselor contacts from CampMinder`);
        if (res.data.errors?.length > 0) {
          toast.error(`${res.data.errors.length} staff had issues`);
          console.log("Sync errors:", res.data.errors);
        }
      }
      fetchCounselorContacts();
    } catch (e) {
      toast.error("Failed to sync staff: " + (e.response?.data?.detail || e.message));
    } finally {
      setStaffSyncing(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchCampers();
    fetchPipCompletion();
    fetchEvaluations();
    fetchTripForms();
    fetchBuddySheets();
    fetchUnassignedCampers();
    fetchYears();
    fetchCounselorContacts();
    // Fetch messaging status
    const token = localStorage.getItem("token");
    axios.get(`${API}/admin/messaging-status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setMessagingEnabled(res.data.enabled))
      .catch(() => {});
    // Fetch feature toggles
    axios.get(`${API}/admin/feature-toggles`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setFeatureToggles(prev => ({ ...prev, ...res.data })))
      .catch(() => {});
    // Fetch hobby test date
    axios.get(`${API}/admin/hobby-test-date`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { if (res.data.test_date) setHobbyTestDate(res.data.test_date.slice(0, 16)); })
      .catch(() => {});
    // Fetch incident reports
    axios.get(`${API}/incident-reports`, { params: { year: selectedYear }, headers: { Authorization: `Bearer ${token}` } })
      .then(res => setIncidentReports(res.data))
      .catch(() => {});
  }, [fetchData, fetchCampers, fetchPipCompletion, fetchEvaluations, fetchTripForms, fetchBuddySheets, fetchUnassignedCampers, fetchYears, selectedYear]);

  // Refetch when year changes
  useEffect(() => {
    fetchCampers();
    fetchTripForms();
    fetchEvaluations();
    fetchBuddySheets();
    const token = localStorage.getItem("token");
    axios.get(`${API}/incident-reports`, { params: { year: selectedYear }, headers: { Authorization: `Bearer ${token}` } })
      .then(res => setIncidentReports(res.data))
      .catch(() => {});
  }, [selectedYear, fetchCampers, fetchTripForms, fetchEvaluations, fetchBuddySheets]);

  // Auto-poll sync status while syncing
  useEffect(() => {
    if (!syncing) return;
    const pollInterval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/sync/status`);
        setSyncStatus(res.data);
        if (res.data.status !== "running") {
          clearInterval(pollInterval);
          setSyncing(false);
          if (res.data.status === "completed") {
            toast.success("Sync completed successfully!");
            fetchData();
            fetchCampers(camperSearch);
            fetchPipCompletion();
          } else {
            toast.error("Sync failed. Check status for details.");
          }
        }
      } catch (err) {
        // Don't stop polling on transient errors
      }
    }, 5000);
    return () => clearInterval(pollInterval);
  }, [syncing, fetchData, fetchCampers, camperSearch]);

  const handleCamperSearch = (e) => {
    setCamperSearch(e.target.value);
    // Only fetch from API when no group is selected (group cards view)
    if (!groupFilter && (e.target.value.length >= 2 || e.target.value === "")) {
      fetchCampers(e.target.value);
    }
  };

  const handleDownloadPDF = async (camper) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/campers/${camper.id}/pdf`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (!response.ok) throw new Error("Failed to generate PDF");
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${camper.last_name}_${camper.first_name}_medical.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("PDF downloaded!");
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/sync/trigger`);
      if (res.data.status === "running") {
        toast.info("A sync is already in progress.");
      } else {
        toast.success("Sync started! This may take several minutes.");
      }
    } catch (error) {
      toast.error("Failed to start sync");
      setSyncing(false);
    }
  };

  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [prevGroupResult, setPrevGroupResult] = useState(null);
  const [notesImportResult, setNotesImportResult] = useState(null);
  const [notesImporting, setNotesImporting] = useState(false);
  const [busSyncResult, setBusSyncResult] = useState(null);

  // Load last bus sync result on mount so the unmatched lists are visible immediately
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API}/sync/bus-status`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.data && res.data.status === "success") setBusSyncResult(res.data);
      } catch { /* silent */ }
    })();
  }, []);
  const [legacyNotesImportResult, setLegacyNotesImportResult] = useState(null);
  const [legacyNotesImporting, setLegacyNotesImporting] = useState(false);
  const [legacyNotesReplace, setLegacyNotesReplace] = useState(true);
  const [legacyNotesYear, setLegacyNotesYear] = useState("");
  const [legacyNotesStats, setLegacyNotesStats] = useState(null);

  const loadLegacyNotesStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/import/legacy-notes/stats`);
      setLegacyNotesStats(res.data);
    } catch {
      // best-effort
    }
  }, []);

  useEffect(() => { loadLegacyNotesStats(); }, [loadLegacyNotesStats]);

  const handleLegacyNotesImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLegacyNotesImporting(true);
    setLegacyNotesImportResult(null);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("replace", legacyNotesReplace ? "true" : "false");
    if (legacyNotesYear && /^20\d{2}$/.test(legacyNotesYear.trim())) {
      formData.append("default_year", legacyNotesYear.trim());
    }
    try {
      const res = await axios.post(`${API}/import/legacy-notes`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setLegacyNotesImportResult(res.data);
      toast.success(res.data.message);
      loadLegacyNotesStats();
    } catch (error) {
      setLegacyNotesImportResult({ error: error.response?.data?.detail || "Import failed" });
      toast.error("Legacy Notes import failed");
    } finally {
      setLegacyNotesImporting(false);
    }
    e.target.value = "";
  };

  const handleClearLegacyNotes = async () => {
    if (!window.confirm("Wipe ALL imported legacy notes? Camper records and alumni log are not touched.")) return;
    try {
      const res = await axios.delete(`${API}/import/legacy-notes`);
      toast.success(`Cleared ${res.data.deleted} legacy note row(s).`);
      setLegacyNotesImportResult(null);
      loadLegacyNotesStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Clear failed");
    }
  };

  const handleNotesImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setNotesImporting(true);
    setNotesImportResult(null);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/import/incident-notes-2025`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setNotesImportResult(res.data);
      toast.success(res.data.message);
    } catch (error) {
      setNotesImportResult({ error: error.response?.data?.detail || "Import failed" });
      toast.error("2025 Incident Notes import failed");
    } finally {
      setNotesImporting(false);
    }
    e.target.value = "";
  };

  const handlePrevGroupUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/campers/previous-groups`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setPrevGroupResult(res.data);
      toast.success(res.data.message);
      fetchCampers(camperSearch);
    } catch (error) {
      setPrevGroupResult({ error: error.response?.data?.detail || "Upload failed" });
      toast.error("Previous group upload failed");
    }
    e.target.value = "";
  };

  const handleGenerateSummary = async () => {
    setSummarizing(true);
    try {
      await axios.post(`${API}/evaluations/summarize`);
      toast.success("AI summary is being generated. Email will be sent when ready.");
      // Poll for summary after a delay
      setTimeout(async () => {
        try {
          const res = await axios.get(`${API}/evaluations/summary/latest`);
          setEvalSummary(res.data);
          setSummarizing(false);
        } catch (e) { setSummarizing(false); }
      }, 15000);
    } catch (error) {
      toast.error("Failed to generate summary");
      setSummarizing(false);
    }
  };

  const handleDeleteEvaluation = async (groupCode) => {
    if (!window.confirm(`Delete evaluation for ${groupCode}?`)) return;
    try {
      await axios.delete(`${API}/evaluations/${groupCode}`);
      toast.success(`Evaluation for ${groupCode} deleted`);
      fetchEvaluations();
    } catch (error) {
      toast.error("Failed to delete evaluation");
    }
  };

  const handleDownloadEvalPDF = async (groupCode) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/evaluations/${groupCode}/pdf`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (!response.ok) throw new Error("Failed to generate PDF");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Evaluation_${groupCode}_2026.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Evaluation PDF downloaded!");
    } catch (error) {
      toast.error("Failed to download evaluation PDF");
    }
  };

  const handleDownloadTripPDF = async (formId, tripName, groupCode, date) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/trip-forms/${formId}/pdf`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (!response.ok) throw new Error("Failed to generate PDF");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Trip_${groupCode}_${date}_${tripName.replace(/\s+/g, '_').substring(0, 20)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Trip form PDF downloaded!");
    } catch (error) {
      toast.error("Failed to download trip form PDF");
    }
  };

  const handleDeleteTripForm = async (formId) => {
    if (!window.confirm("Delete this trip form?")) return;
    try {
      await axios.delete(`${API}/trip-forms/${formId}`);
      toast.success("Trip form deleted");
      fetchTripForms(tripFilterGroup, tripFilterDate);
    } catch (error) {
      toast.error("Failed to delete trip form");
    }
  };

  const handleDownloadBuddyPDF = async (sheetId, groupCode, date, period) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/buddy-sheets/${sheetId}/pdf`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (!response.ok) throw new Error();
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Buddy_Sheet_${groupCode}_${date}_${period}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Buddy sheet PDF downloaded!");
    } catch (error) {
      toast.error("Failed to download buddy sheet PDF");
    }
  };

  const handleDeleteBuddySheet = async (sheetId) => {
    if (!window.confirm("Delete this buddy sheet?")) return;
    try {
      await axios.delete(`${API}/buddy-sheets/${sheetId}`);
      toast.success("Buddy sheet deleted");
      fetchBuddySheets(buddyFilterGroup, buddyFilterDate, buddyFilterPeriod);
    } catch (error) {
      toast.error("Failed to delete buddy sheet");
    }
  };

  const handleAssignGroup = async () => {
    if (!assigningGroup || selectedUnassigned.size === 0) {
      toast.error("Select campers and a group to assign");
      return;
    }
    setAssigning(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/campers/assign-group`, {
        camper_ids: Array.from(selectedUnassigned),
        group_code: assigningGroup
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Assigned ${selectedUnassigned.size} camper(s) to ${assigningGroup}`);
      setSelectedUnassigned(new Set());
      setAssigningGroup("");
      fetchUnassignedCampers();
      fetchCampers(camperSearch);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign group");
    } finally {
      setAssigning(false);
    }
  };

  const toggleUnassignedSelection = (id) => {
    setSelectedUnassigned(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllUnassigned = (filtered) => {
    if (selectedUnassigned.size === filtered.length) {
      setSelectedUnassigned(new Set());
    } else {
      setSelectedUnassigned(new Set(filtered.map(c => c.id)));
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    setImportResult(null);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const res = await axios.post(`${API}/import/excel`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setImportResult(res.data);
      toast.success(res.data.message);
      fetchData();
      fetchCampers(camperSearch);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
      setImportResult({ error: error.response?.data?.detail || "Import failed" });
    } finally {
      setImporting(false);
    }
    
    e.target.value = "";
  };

  const handleSaveField = async (fieldData) => {
    try {
      if (fieldDialog.field?.id) {
        await axios.put(`${API}/custom-fields/${fieldDialog.field.id}`, fieldData);
        toast.success("Field updated");
      } else {
        await axios.post(`${API}/custom-fields`, fieldData);
        toast.success("Field created");
      }
      setFieldDialog({ open: false, field: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save field");
    }
  };

  const handleDeleteField = async (fieldId) => {
    if (!window.confirm("Delete this field?")) return;
    try {
      await axios.delete(`${API}/custom-fields/${fieldId}`);
      toast.success("Field deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete field");
    }
  };

  const handleSaveGroup = async (groupData) => {
    try {
      await axios.post(`${API}/groups`, groupData);
      toast.success("Group created");
      setGroupDialog({ open: false, group: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save group");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            {currentView !== "menu" && (
              <Button variant="ghost" size="sm" onClick={() => { setCurrentView("menu"); setGroupFilter(""); setCamperSearch(""); setCamperSubView("groups"); setAdminMsgSelectedGroup(null); setAdminMsgMessages([]); setAdminMsgText(""); setAdminMsgCamperFilter(""); setAdminMsgDateFrom(""); setAdminMsgDateTo(""); }} className="px-2 gap-1" data-testid="admin-back-menu">
                <ArrowLeft className="w-4 h-4" />
                <span className="text-xs font-medium">Dashboard</span>
              </Button>
            )}
            <img src={LOGO_URL} alt="Logo" className="w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0" />
            <div className="min-w-0">
              <h1 className="font-heading font-bold text-primary-600 text-sm sm:text-lg truncate">
                Admin Dashboard
              </h1>
              <p className="text-xs text-muted-foreground">Camper Snapshot</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {/* Year Selector */}
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="h-8 px-2 text-xs border rounded-md bg-white font-semibold text-primary-700 cursor-pointer"
              data-testid="year-selector"
            >
              {availableYears.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            {currentView === "menu" && (
              <Button variant="outline" size="sm" onClick={() => setCurrentView("settings")} className="text-xs h-8" data-testid="admin-settings-btn">
                <Settings className="w-3.5 h-3.5 sm:mr-1" /><span className="hidden sm:inline">Settings</span>
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={logout} className="text-muted-foreground flex-shrink-0 h-8" data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline text-xs">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-3 md:p-6">
        {/* ═══ MENU VIEW ═══ */}
        {currentView === "menu" && (
          <div className="space-y-4">
            <QuickAttendanceWidget campers={campers} />
            <PlannedAbsencesPanel />
            <MissingLoginAlert onClickGroup={() => setCurrentView("logins")} />
            <SmsOffBanner token={token} onOpenSettings={() => setCurrentView("sms-settings")} />
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-white rounded-lg border p-3 text-center">
                <p className="text-lg font-bold text-primary-600">{syncStatus?.camper_count || 0}</p>
                <p className="text-[10px] text-muted-foreground">Campers</p>
              </div>
              <div className="bg-white rounded-lg border p-3 text-center">
                <p className="text-lg font-bold text-primary-600">{syncStatus?.group_count || 0}</p>
                <p className="text-[10px] text-muted-foreground">Groups</p>
              </div>
              <div className="bg-white rounded-lg border p-3 text-center">
                <p className={`text-lg font-bold ${syncStatus?.status === "completed" ? "text-green-600" : "text-amber-600"}`}>
                  {syncStatus?.status === "completed" ? "OK" : syncStatus?.status || "—"}
                </p>
                <p className="text-[10px] text-muted-foreground">Sync</p>
              </div>
            </div>
            <MenuSearchBar containerSelector="[data-menu-grid='admin']" placeholder="Search admin tools…" testId="admin-menu-search" className="max-w-md" />
            <ReorderableGrid canReorder={user?.user_type === "admin"} menuGridId="admin" gridClassName="grid grid-cols-3 sm:grid-cols-4 gap-3">
              <AdminMenuCard icon={Users} label="Campers" color="blue" onClick={() => setCurrentView("campers")} testId="admin-card-campers" />
              <AdminMenuCard icon={CalendarDays} label="Daily Roster" color="cyan" onClick={() => { setCurrentView("daily-roster"); setRosterSubView("groups"); setRosterGroupFilter(""); setRosterGroupSearch(""); setRosterCamperSearch(""); setRosterData(null); setRosterSelectedDate(""); }} testId="admin-card-daily-roster" />
              <AdminMenuCard icon={Flame} label="Fire Drill" color="red" onClick={() => setFireDrillOpen(true)} testId="admin-card-fire-drill" />
              <AdminMenuCard icon={Cake} label={playdateLoading ? "Generating…" : "Playdate List"} color="pink" onClick={handleDownloadPlaydateList} testId="admin-card-playdate-list" />
              <AdminMenuCard icon={MapPin} label={townBackfillRunning ? "Pulling Towns…" : "Pull Towns"} color="fuchsia" disabled={townBackfillRunning} onClick={handlePullTowns} testId="admin-card-pull-towns" />
              <AdminMenuCard icon={ClipboardList} label="Buddy Sheets" color="amber" onClick={() => setCurrentView("buddy-sheets")} testId="admin-card-buddy" />
              <AdminMenuCard icon={Star} label="Evaluations" color="purple" onClick={() => setCurrentView("evaluations")} testId="admin-card-eval" />
              <AdminMenuCard icon={Bus} label="Trip Forms" color="green" onClick={() => setCurrentView("trip-forms")} testId="admin-card-trips" />
              <AdminMenuCard icon={UserX} label="Unassigned" color="red" badge={unassignedCampers.length > 0 ? unassignedCampers.length : null} onClick={() => setCurrentView("unassigned")} testId="admin-card-unassigned" />
              <AdminMenuCard icon={ArrowRightLeft} label="Grouping" color="indigo" href="http://rrdcgrouping.com" testId="admin-card-grouping" />
              <AdminMenuCard icon={Bus} label="Bus System" color="teal" href="http://rrdcbusmapping.com" testId="admin-card-bus" />
              <AdminMenuCard icon={BarChart3} label="Flex Reporting" color="orange" href="http://rrdcflexerror.com" testId="admin-card-flex" />
              <AdminMenuCard icon={ShieldCheck} label="Certifications" color="emerald" href="http://rrdccerts.com" testId="admin-card-certs" />
              <AdminMenuCard icon={Waves} label="Swim Lessons" color="cyan" href="https://rrdcswimlessons.com/admin" testId="admin-card-swim-lessons" />
              <AdminMenuCard icon={Droplets} label="Swim Colors" color="sky" href="http://rrdcaquatics.com" testId="admin-card-swim-color" />
              <AdminMenuCard icon={AlertTriangle} label="Incident Reports" color="rose" onClick={() => setCurrentView("incidents")} testId="admin-card-incidents" badge={incidentReports.length || null} />
              <AdminMenuCard icon={Utensils} label="Dining" color="yellow" href="https://rrdcdining.com/admin" testId="admin-card-lunch" />
              <AdminMenuCard icon={Trophy} label="Intramurals" color="lime" disabled testId="admin-card-intramurals-coming-soon" />
              <AdminMenuCard icon={Flag} label="Color War" color="fuchsia" onClick={() => setCurrentView("color-war")} testId="admin-card-colorwar" />
              <AdminMenuCard icon={Trophy} label="Intramurals" color="blue" onClick={() => setCurrentView("intramurals")} testId="admin-card-intramurals" />
              <AdminMenuCard icon={Users} label="Nav Intramurals" color="teal" onClick={() => setCurrentView("nav-intramurals")} testId="admin-card-nav-intramurals" />
              <AdminMenuCard icon={ClipboardCheck} label="Attendance" color="violet" onClick={() => setCurrentView("attendance-report")} testId="admin-card-attendance" />
              <AdminMenuCard icon={AlertTriangle} label="Admin Conflicts" color="red" onClick={() => setCurrentView("admin-conflicts")} testId="admin-card-conflicts" />
              <AdminMenuCard icon={Mail} label="Send Incident Digest" color="fuchsia" onClick={() => setCurrentView("incident-digest")} testId="admin-card-incident-digest" />
              <AdminMenuCard icon={MessageSquare} label="Messages" color="blue" onClick={() => {
                setCurrentView("messages");
                setAdminMsgLoading(true);
                const h = { Authorization: `Bearer ${token}` };
                axios.get(`${API}/supervisor/messages/groups`, { headers: h, params: { year: selectedYear } }).then(res => setAdminMsgGroups(res.data)).catch(() => toast.error("Failed to load")).finally(() => setAdminMsgLoading(false));
              }} testId="admin-card-messages" />
              <AdminMenuCard icon={CalendarDays} label="Schedules" color="sky" onClick={() => setCurrentView("schedules")} testId="admin-card-schedules" />
              <AdminMenuCard icon={LogIn} label="Logins" color="indigo" onClick={() => setCurrentView("logins")} testId="admin-card-logins" />
              <AdminMenuCard icon={KeyRound} label="Group Logins" color="rose" onClick={() => setCurrentView("group-logins")} testId="admin-card-group-logins" />
              <AdminMenuCard icon={Trophy} label="Intramural Uploads" color="rose" onClick={() => setCurrentView("intramural-uploads")} testId="admin-card-intramural-uploads" />
              <AdminMenuCard icon={Settings} label="Activity Caps" color="amber" onClick={() => setCurrentView("activity-caps")} testId="admin-card-activity-caps" />
              <AdminMenuCard icon={Bus} label="Transport Audit" color="rose" onClick={() => setCurrentView("transport-audit")} testId="admin-card-transport-audit" />
              <AdminMenuCard icon={Bell} label="Division Notifications" color="rose" onClick={() => setCurrentView("division-notifications")} testId="admin-card-division-notifications" />
              <AdminMenuCard icon={Sun} label="End-of-Day Dismissal" color="amber" onClick={() => setCurrentView("dismissal-reports")} testId="admin-card-dismissal-reports" />
              <AdminMenuCard icon={Moon} label="Overnight & Broadway" color="indigo" onClick={() => {
                setTripRostersLoading(true);
                axios.get(`${API}/cit/overnight`, { headers: { Authorization: `Bearer ${token}` } })
                  .then(res => setTripRostersData(res.data))
                  .catch(() => toast.error("Failed to load trip rosters"))
                  .finally(() => setTripRostersLoading(false));
                setCurrentView("trip-rosters");
              }} testId="admin-card-trip-rosters" />
              <AdminMenuCard icon={FileText} label="Mariner Boys Hobbies" color="teal" onClick={() => handleHobbyReport("mariner_boys")} testId="admin-card-hobbies-mb" />
              <AdminMenuCard icon={FileText} label="Mariner Girls Hobbies" color="pink" onClick={() => handleHobbyReport("mariner_girls")} testId="admin-card-hobbies-mg" />
              <AdminMenuCard icon={FileText} label="Navigator Hobbies" color="cyan" onClick={() => handleHobbyReport("navigator_hobbies")} testId="admin-card-hobbies-nav" />
              <AdminMenuCard icon={FileText} label="Navigator Intensives" color="purple" onClick={() => handleHobbyReport("navigator_intensives")} testId="admin-card-hobbies-nav-int" />
              <AdminMenuCard icon={MessageSquare} label={hcDeliveryLoading ? "Fetching…" : "HC Delivery Log"} color="emerald" disabled={hcDeliveryLoading} onClick={handleHcDeliverability} testId="admin-card-hc-deliverability" />
              <AdminMenuCard icon={BellOff} label="SMS Settings" color="orange" onClick={() => setCurrentView("sms-settings")} testId="admin-card-sms-settings" />
              <AdminMenuCard icon={User} label="Camper Photos" color="purple" onClick={() => setCurrentView("camper-photos")} testId="admin-card-camper-photos" />
              <AdminMenuCard icon={Calendar} label="Rentals & Events" color="pink" href="https://rentalandevent.com/login" testId="admin-card-rentals" />
              <AdminMenuCard icon={Shirt} label="Staff Snapshot" color="emerald" href="https://rrdcstaffsnapshot.com" testId="admin-card-staff-snapshot" />
              <AdminMenuCard icon={Stethoscope} label="Services" color="indigo" onClick={() => setCurrentView("service-providers")} testId="admin-card-service-providers" />
              <AdminMenuCard icon={Sparkles} label="Carnival Snack Order" color="pink" onClick={() => setCurrentView("carnival-snack")} testId="admin-card-carnival-snack" />
              <AdminMenuCard icon={ClipboardList} label="Parent Survey" color="teal" onClick={() => setCurrentView("parent-survey")} testId="admin-card-parent-survey" />
              <AdminMenuCard icon={Trophy} label="Milestone Certificates" color="amber" onClick={() => setCurrentView("milestone-certificates")} testId="admin-card-milestone-certificates" />
              <AdminMenuCard icon={Users} label="Teams" color="indigo" onClick={() => setCurrentView("teams")} testId="admin-card-teams" />
              <AdminMenuCard icon={MapPin} label="Lunch Table & Changing Area" color="teal" onClick={() => setCurrentView("camper-locations")} testId="admin-card-camper-locations" />
              <AdminMenuCard icon={Trash2} label="Testing Tools" color="red" onClick={() => setCurrentView("testing-tools")} testId="admin-card-testing-tools" />
            </ReorderableGrid>
          </div>
        )}

        {/* ═══ CAMPERS VIEW ═══ */}
        {currentView === "campers" && camperSubView === "groups" && (
          <div className="space-y-4">
            <div>
              <h2 className="font-heading font-bold text-lg">All Groups</h2>
              <p className="text-sm text-muted-foreground">{campers.length} campers across {groups.length} groups</p>
            </div>
            <div className="relative" data-testid="admin-camper-global-search">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input 
                placeholder="Search any camper by name..." 
                value={camperSearch} 
                onChange={(e) => setCamperSearch(e.target.value)} 
                className="pl-10" 
                data-testid="admin-global-camper-search"
              />
            </div>
            {camperSearch.trim() ? (
              <div className="space-y-2">
                {campers
                  .filter(c => {
                    const q = camperSearch.toLowerCase();
                    const full = `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase();
                    return full.includes(q);
                  })
                  .sort((a, b) => `${a.last_name}`.localeCompare(`${b.last_name}`))
                  .slice(0, 30)
                  .map(c => (
                    <button
                      key={c.id}
                      onClick={() => { setSelectedCamper(c); }}
                      className="w-full flex items-center justify-between p-3 rounded-lg border bg-white hover:bg-primary-50 hover:border-primary-300 transition-all text-left"
                      data-testid={`admin-search-result-${c.id}`}
                    >
                      <div>
                        <span className="text-sm font-semibold text-primary-800">{c.first_name} {c.last_name}</span>
                      </div>
                      <Badge variant="secondary" className="text-xs bg-primary-50 text-primary-700 border-primary-200">{c.group_code}</Badge>
                    </button>
                  ))
                }
                {campers.filter(c => {
                  const q = camperSearch.toLowerCase();
                  return `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase().includes(q);
                }).length === 0 && (
                  <p className="text-center text-sm text-muted-foreground py-8">No campers match "{camperSearch}"</p>
                )}
              </div>
            ) : (
            <>
            {groups.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No groups synced yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {groups.sort((a, b) => (a.code || "").localeCompare(b.code || "", undefined, { numeric: true })).map(group => {
                  const groupCamperCount = campers.filter(c => c.group_code === group.code).length;
                  return (
                    <button
                      key={group.code}
                      onClick={() => { setGroupFilter(group.code); setCamperSubView("list"); }}
                      className="flex flex-col items-center gap-2 p-5 rounded-2xl border-2 bg-white border-primary-200 hover:border-primary-400 hover:shadow-lg transition-all active:scale-95"
                      data-testid={`admin-group-card-${group.code}`}
                    >
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center">
                        <Users className="w-6 h-6 text-primary-600" />
                      </div>
                      <span className="text-sm font-bold text-primary-800">{group.code}</span>
                      {group.name && <span className="text-xs text-muted-foreground">{group.name}</span>}
                      <Badge variant="secondary" className="text-xs bg-primary-50 text-primary-700 border-primary-200">
                        {groupCamperCount} camper{groupCamperCount !== 1 ? "s" : ""}
                      </Badge>
                    </button>
                  );
                })}
              </div>
            )}
            </>
            )}
          </div>
        )}

        {currentView === "campers" && camperSubView === "list" && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="sm" onClick={() => { setCamperSubView("groups"); setGroupFilter(""); setCamperSearch(""); }} className="px-2" data-testid="admin-back-to-groups">
                  <ArrowLeft className="w-4 h-4" />
                </Button>
                <div>
                  <h2 className="font-heading font-bold text-lg flex items-center gap-2">
                    <Badge variant="secondary" className="text-sm bg-primary-50 text-primary-700 border-primary-200 px-3 py-1">{groupFilter}</Badge>
                    {groups.find(g => g.code === groupFilter)?.name || ""}
                  </h2>
                  <p className="text-sm text-muted-foreground">{campers.filter(c => c.group_code === groupFilter).length} campers</p>
                </div>
              </div>
              <div className="relative w-full sm:w-56">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name..."
                  value={camperSearch}
                  onChange={handleCamperSearch}
                  className="pl-10"
                  data-testid="admin-camper-search"
                />
              </div>
            </div>

            {(() => {
              let filtered = campers.filter(c => c.group_code === groupFilter);
              if (camperSearch) {
                const q = camperSearch.toLowerCase();
                filtered = filtered.filter(c => {
                  const name = `${c.first_name} ${c.last_name}`.toLowerCase();
                  return name.includes(q);
                });
              }
              if (filtered.length === 0) {
                return (
                  <div className="text-center py-12 text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>{camperSearch ? "No campers match your search" : "No campers in this group"}</p>
                  </div>
                );
              }
              return (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {filtered.map(camper => (
                    <AdminCamperCard
                      key={camper.id}
                      camper={camper}
                      onClick={() => setSelectedCamper(camper)}
                    />
                  ))}
                </div>
              );
            })()}
          </div>
        )}

        {/* Camper Detail Modal */}
        {selectedCamper && (
          <Dialog open={!!selectedCamper} onOpenChange={() => setSelectedCamper(null)}>
            <DialogContent className="max-w-2xl max-h-[90vh] p-0 w-[calc(100%-2rem)] sm:w-full rounded-xl" aria-describedby="admin-camper-detail-desc">
              <span id="admin-camper-detail-desc" className="sr-only">Camper information details</span>
              <AdminCamperDetail camper={selectedCamper} onClose={() => setSelectedCamper(null)} onDownloadPDF={handleDownloadPDF} />
            </DialogContent>
          </Dialog>
        )}

        {/* ═══ DAILY ROSTER — GROUP CARDS ═══ */}
        {currentView === "daily-roster" && rosterSubView === "groups" && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="font-heading font-bold text-lg">Daily Roster</h2>
                <p className="text-sm text-muted-foreground">Select a group to view today's roster</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="border-rose-300 text-rose-700 hover:bg-rose-50"
                data-testid="absent-report-btn"
                onClick={() => {
                  const token = localStorage.getItem("token");
                  window.open(`${API}/reports/absent/pdf?token=${token}`, "_blank");
                }}
              >
                <FileText className="w-3.5 h-3.5 mr-2" />
                Today's Absent Report (PDF)
              </Button>
            </div>
            {groups.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No groups synced yet</p>
              </div>
            ) : (
              <>
                <div className="relative max-w-md">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search group code or name (e.g. B05, Lobsters)..."
                    value={rosterGroupSearch}
                    onChange={(e) => setRosterGroupSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400"
                    data-testid="admin-roster-group-search"
                  />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                  {groups
                    .filter(g => {
                      const q = rosterGroupSearch.trim().toLowerCase();
                      if (!q) return true;
                      return (g.code || "").toLowerCase().includes(q) || (g.name || "").toLowerCase().includes(q);
                    })
                    .sort((a, b) => (a.code || "").localeCompare(b.code || "", undefined, { numeric: true }))
                    .map(group => (
                      <button
                        key={group.code}
                        onClick={() => { setRosterGroupFilter(group.code); setRosterSubView("list"); setRosterCamperSearch(""); fetchRosterForGroup(group.code, rosterSelectedDate || undefined); }}
                        className="flex flex-col items-center gap-2 p-5 rounded-2xl border-2 bg-white border-cyan-200 hover:border-cyan-400 hover:shadow-lg transition-all active:scale-95"
                        data-testid={`roster-group-card-${group.code}`}
                      >
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-100 to-cyan-200 flex items-center justify-center">
                          <CalendarDays className="w-6 h-6 text-cyan-600" />
                        </div>
                        <span className="text-sm font-bold text-cyan-800">{group.code}</span>
                        {group.name && <span className="text-xs text-muted-foreground">{group.name}</span>}
                      </button>
                    ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* ═══ DAILY ROSTER — GROUP ROSTER LIST ═══ */}
        {currentView === "daily-roster" && rosterSubView === "list" && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="sm" onClick={() => { setRosterSubView("groups"); setRosterGroupFilter(""); setRosterCamperSearch(""); setRosterData(null); }} className="px-2" data-testid="roster-back-to-groups">
                  <ArrowLeft className="w-4 h-4" />
                </Button>
                <div>
                  <h2 className="font-heading font-bold text-lg flex items-center gap-2">
                    <Badge variant="secondary" className="text-sm bg-cyan-50 text-cyan-700 border-cyan-200 px-3 py-1">{rosterGroupFilter}</Badge>
                    {groups.find(g => g.code === rosterGroupFilter)?.name || ""}
                  </h2>
                  {rosterData && (() => {
                    const presentCount = (rosterData.campers || []).filter(c => (c.attendance || "").toLowerCase() !== "absent").length;
                    const absentCount = (rosterData.campers || []).filter(c => (c.attendance || "").toLowerCase() === "absent").length;
                    return (
                      <p className="text-sm text-muted-foreground">
                        Roster for {new Date(rosterData.date + "T12:00:00").toLocaleDateString("en-US", { timeZone: "America/New_York", weekday: "long", month: "long", day: "numeric", year: "numeric" })} — {presentCount} camper{presentCount !== 1 ? "s" : ""}
                        {absentCount > 0 ? ` (${absentCount} absent — hidden)` : ""}
                      </p>
                    );
                  })()}
                </div>
              </div>
              <div className="flex items-center gap-2 ml-10 sm:ml-0">
                <label className="text-xs font-medium text-cyan-700">View date:</label>
                <input
                  type="date"
                  min="2026-06-29"
                  max="2026-08-20"
                  value={rosterSelectedDate || rosterData?.date || ""}
                  onChange={(e) => { setRosterSelectedDate(e.target.value); fetchRosterForGroup(rosterGroupFilter, e.target.value); }}
                  className="px-2 py-1 text-sm border border-cyan-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-cyan-400"
                  data-testid="admin-roster-date-picker"
                />
              </div>
            </div>
            {rosterLoading ? (
              <div className="text-center py-16">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading roster...</p>
              </div>
            ) : !rosterData?.is_camp_day || (rosterData?.campers || []).length === 0 ? (
              <div className="text-center py-16">
                <CalendarDays className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                <h3 className="text-xl text-muted-foreground mb-2">No campers scheduled for this date.</h3>
                <p className="text-sm text-muted-foreground">
                  {!rosterData?.is_camp_day ? "This is not a camp operating day." : "No campers match the session filter for this date."}
                </p>
              </div>
            ) : (
              <>
                <div className="relative max-w-md">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search camper by name..."
                    value={rosterCamperSearch}
                    onChange={(e) => setRosterCamperSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400"
                    data-testid="admin-roster-camper-search"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                  {[...(rosterData?.campers || [])]
                    .filter(c => (c.attendance || "").toLowerCase() !== "absent")
                    .filter(c => {
                      const q = rosterCamperSearch.trim().toLowerCase();
                      if (!q) return true;
                      const full = `${c.first_name || ""} ${c.last_name || ""}`.toLowerCase();
                      const pref = `${c.preferred_name || ""} ${c.last_name || ""}`.toLowerCase();
                      return full.includes(q) || pref.includes(q) || (c.last_name || "").toLowerCase().includes(q);
                    })
                    .sort((a, b) =>
                      (a.last_name || "").localeCompare(b.last_name || "", undefined, { sensitivity: "base" }) ||
                      (a.first_name || "").localeCompare(b.first_name || "", undefined, { sensitivity: "base" })
                    )
                    .map(camper => (
                      <AdminCamperCard key={camper.id} camper={camper} onClick={() => setSelectedCamper(camper)} />
                    ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* ═══ SETTINGS VIEW (continued) ═══ */}
        {currentView === "settings" && (
          <div className="space-y-4">
            <Card className="border-emerald-200 bg-emerald-50/40">
              <CardContent className="flex items-center justify-between gap-3 flex-wrap p-4 sm:p-6">
                <div className="flex items-center gap-3">
                  <Utensils className="w-5 h-5 text-emerald-700" />
                  <div>
                    <p className="font-heading font-semibold text-sm">Overnight Trip — Allergy List</p>
                    <p className="text-xs text-muted-foreground">All Navigators & CITs on the overnight with their allergies — for dietary staff</p>
                  </div>
                </div>
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 text-xs"
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem("token");
                      const res = await axios.get(`${API}/admin/overnight-allergies.pdf`, {
                        headers: { Authorization: `Bearer ${token}` },
                        responseType: "blob",
                      });
                      const url = URL.createObjectURL(res.data);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = "overnight_allergy_list.pdf";
                      a.click();
                      URL.revokeObjectURL(url);
                    } catch {
                      toast.error("Failed to download allergy list");
                    }
                  }}
                  data-testid="download-overnight-allergies-btn"
                >
                  <Download className="w-3 h-3 mr-1" /> Download PDF
                </Button>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="flex items-center justify-between p-4 sm:p-6">
                <div className="flex items-center gap-3">
                  <MessageSquare className={`w-5 h-5 ${messagingEnabled ? 'text-green-600' : 'text-red-500'}`} />
                  <div>
                    <p className="font-heading font-semibold text-sm">System Messaging</p>
                    <p className="text-xs text-muted-foreground">
                      {messagingEnabled ? "Counselors can send messages to parents" : "All outbound messaging is disabled"}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={messagingEnabled}
                  onCheckedChange={async (checked) => {
                    try {
                      const token = localStorage.getItem("token");
                      await axios.post(`${API}/admin/messaging-toggle`, { enabled: checked }, { headers: { Authorization: `Bearer ${token}` } });
                      setMessagingEnabled(checked);
                      toast.success(checked ? "Messaging enabled" : "Messaging disabled");
                    } catch (err) {
                      toast.error("Failed to toggle messaging");
                    }
                  }}
                  data-testid="messaging-toggle"
                />
              </CardContent>
            </Card>

            {/* Feature Toggles */}
            {[
              { key: "mariner_hobbies_enabled", label: "Mariner Hobbies", desc: "Hobby selection for B08-B11, G07-G11", icon: Star },
              { key: "navigator_hobbies_enabled", label: "Navigator Hobbies", desc: "Hobby selection for B12-B15, G12-G15", icon: Sparkles },
              { key: "navigator_intramurals_enabled", label: "Navigator Intramurals", desc: "Team sorter, schedule, standings, playoffs", icon: Trophy },
              { key: "buddy_sheets_unlocked", label: "Buddy Sheets Unlocked", desc: "Counselors can edit/save buddy sheets. Auto-locks at 9:20 AM, unlocks at 5 PM.", icon: Waves },
            ].map(({ key, label, desc, icon: Icon }) => (
              <Card key={key}>
                <CardContent className="flex items-center justify-between p-4 sm:p-6">
                  <div className="flex items-center gap-3">
                    <Icon className={`w-5 h-5 ${featureToggles[key] ? 'text-green-600' : 'text-red-500'}`} />
                    <div>
                      <p className="font-heading font-semibold text-sm">{label}</p>
                      <p className="text-xs text-muted-foreground">
                        {featureToggles[key] ? `${label} is active` : `${label} is disabled for all users`}
                      </p>
                      <p className="text-[10px] text-muted-foreground">{desc}</p>
                    </div>
                  </div>
                  <Switch
                    checked={featureToggles[key]}
                    onCheckedChange={async (checked) => {
                      try {
                        const token = localStorage.getItem("token");
                        await axios.post(`${API}/admin/feature-toggle`, { key, enabled: checked }, { headers: { Authorization: `Bearer ${token}` } });
                        setFeatureToggles(prev => ({ ...prev, [key]: checked }));
                        toast.success(`${label} ${checked ? "enabled" : "disabled"}`);
                      } catch (err) {
                        toast.error("Failed to toggle feature");
                      }
                    }}
                    data-testid={`toggle-${key}`}
                  />
                </CardContent>
              </Card>
            ))}

            {/* Activity capacity manager — accordion per program */}
            <CapacityAccordion token={localStorage.getItem("token")} />

            {/* Hobby/Intensive Test Date Override */}
            <Card>
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <CalendarDays className={`w-5 h-5 ${hobbyTestDate ? "text-amber-600" : "text-muted-foreground"}`} />
                    <div>
                      <p className="font-heading font-semibold text-sm">Hobby/Intensive Test Date</p>
                      <p className="text-xs text-muted-foreground">
                        {hobbyTestDate ? `Simulating: ${new Date(hobbyTestDate).toLocaleString()}` : "Using real date — set a date to test selection windows"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="datetime-local"
                      className="w-52 h-8 text-xs"
                      value={hobbyTestDate}
                      onChange={e => setHobbyTestDate(e.target.value)}
                      data-testid="hobby-test-date-input"
                    />
                    <Button size="sm" className="h-8 text-xs bg-amber-600 hover:bg-amber-700" disabled={!hobbyTestDate}
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem("token");
                          await axios.post(`${API}/admin/hobby-test-date`, { date: hobbyTestDate ? hobbyTestDate + ":00" : null }, { headers: { Authorization: `Bearer ${token}` } });
                          toast.success(`Test date set to ${new Date(hobbyTestDate).toLocaleString()}`);
                        } catch { toast.error("Failed to set test date"); }
                      }}
                      data-testid="hobby-test-date-set-btn"
                    >
                      Set
                    </Button>
                    <Button size="sm" variant="outline" className="h-8 text-xs"
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem("token");
                          await axios.post(`${API}/admin/hobby-test-date`, { date: null }, { headers: { Authorization: `Bearer ${token}` } });
                          setHobbyTestDate("");
                          toast.success("Test date cleared — using real date");
                        } catch { toast.error("Failed to clear"); }
                      }}
                      data-testid="hobby-test-date-clear-btn"
                    >
                      Clear
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Hobby/Intensive Roster Exports */}
            <Card data-testid="hobby-export-card">
              <CardContent className="p-4 sm:p-6 space-y-4">
                <div className="flex items-center gap-3">
                  <FileSpreadsheet className="w-5 h-5 text-sky-700" />
                  <div>
                    <p className="font-heading font-semibold text-sm">Reports — Weekly Activity Rosters (.xlsx)</p>
                    <p className="text-xs text-muted-foreground">
                      Pick a module and a week. Branded multi-tab workbook: one tab per activity, portrait letter, print-ready. Hand to specialty counselors.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { key: "mariner_girls", label: "Mariner Girls Hobbies", accent: "border-pink-300 bg-pink-50", dayKey: "wednesday", weeksKey: "mariner" },
                    { key: "mariner_boys", label: "Mariner Boys Hobbies", accent: "border-blue-300 bg-blue-50", dayKey: "wednesday", weeksKey: "mariner" },
                    { key: "navigator", label: "Navigator Hobbies", accent: "border-violet-300 bg-violet-50", dayKey: "monday", weeksKey: "navigator" },
                    { key: "intensive", label: "Navigator Intensives", accent: "border-fuchsia-300 bg-fuchsia-50", dayKey: "wednesday", weeksKey: "intensive" },
                  ].map(opt => (
                    <WeeklyRosterExportRow
                      key={opt.key}
                      opt={opt}
                      api={API}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>

          <Tabs defaultValue="fields" className="space-y-4">
            <TabsList className="bg-muted/50">
              <TabsTrigger value="fields" className="text-xs"><FileSpreadsheet className="w-3.5 h-3.5 mr-1" />Fields</TabsTrigger>
              <TabsTrigger value="groups" className="text-xs"><Database className="w-3.5 h-3.5 mr-1" />Groups</TabsTrigger>
              <TabsTrigger value="contacts" className="text-xs"><Phone className="w-3.5 h-3.5 mr-1" />Contacts</TabsTrigger>
              <TabsTrigger value="allergy-reports" className="text-xs"><Utensils className="w-3.5 h-3.5 mr-1" />Allergy Reports</TabsTrigger>
              <TabsTrigger value="sync" className="text-xs"><RefreshCw className="w-3.5 h-3.5 mr-1" />Sync</TabsTrigger>
            </TabsList>

          {/* Custom Fields Tab */}
          <TabsContent value="fields">
            <Card>
              <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <CardTitle className="font-heading">Custom Fields</CardTitle>
                  <CardDescription>
                    Define medical fields to track for each camper
                  </CardDescription>
                </div>
                <Button 
                  onClick={() => setFieldDialog({ open: true, field: null })}
                  data-testid="add-field-btn"
                  className="w-full sm:w-auto"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Field
                </Button>
              </CardHeader>
              <CardContent>
                {customFields.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <FileSpreadsheet className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>No custom fields defined yet</p>
                    <p className="text-sm">Click "Add Field" to create your first field</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Medical</TableHead>
                        <TableHead>Order</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {customFields.map((field) => (
                        <TableRow key={field.id} data-testid={`field-row-${field.id}`}>
                          <TableCell className="font-medium">{field.name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{field.field_type}</Badge>
                          </TableCell>
                          <TableCell>
                            {field.is_medical ? (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : (
                              <XCircle className="w-4 h-4 text-muted-foreground" />
                            )}
                          </TableCell>
                          <TableCell>{field.display_order}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setFieldDialog({ open: true, field })}
                              data-testid={`edit-field-${field.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteField(field.id)}
                              className="text-red-600 hover:text-red-700"
                              data-testid={`delete-field-${field.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Groups Tab */}
          <TabsContent value="groups">
            <Card>
              <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <CardTitle className="font-heading">Groups / Bunks</CardTitle>
                  <CardDescription>
                    Manage counselor groups and their access credentials
                  </CardDescription>
                </div>
                <Button 
                  onClick={() => setGroupDialog({ open: true, group: null })}
                  data-testid="add-group-btn"
                  className="w-full sm:w-auto"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Group
                </Button>
              </CardHeader>
              <CardContent>
                {groups.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>No groups defined yet</p>
                    <p className="text-sm">Sync with CampMinder or add manually</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Code (Username)</TableHead>
                        <TableHead>Name (Password)</TableHead>
                        <TableHead>CampMinder ID</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {groups.map((group) => (
                        <TableRow key={group.id} data-testid={`group-row-${group.id}`}>
                          <TableCell className="font-mono font-medium">
                            {group.code}
                          </TableCell>
                          <TableCell>{group.name}</TableCell>
                          <TableCell className="text-muted-foreground">
                            {group.campminder_bunk_id || "—"}
                          </TableCell>
                          <TableCell>
                            <Badge variant={group.is_active !== false ? "default" : "secondary"}>
                              {group.is_active !== false ? "Active" : "Inactive"}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sync & Import Tab */}
          {/* Counselor Contacts Tab */}
          <TabsContent value="contacts">
            <div className="space-y-4">
              {/* Season Management — master switch */}
              <SeasonManagementPanel token={token} />

              {/* Per-day Quiet Hours editor */}
              <QuietHoursPanel token={token} />

            <Card>
              <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <CardTitle className="font-heading">Head Counselor Contacts</CardTitle>
                  <CardDescription>Phone numbers for SMS check-in reminders & early dismissal alerts</CardDescription>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button variant="default" size="sm" onClick={syncStaffFromCM} disabled={staffSyncing} className="gap-1.5" data-testid="sync-staff-contacts-btn">
                    <RefreshCw className={`w-3.5 h-3.5 ${staffSyncing ? "animate-spin" : ""}`} /> {staffSyncing ? "Syncing..." : "Sync from CampMinder"}
                  </Button>
                  <a href={`${API}/admin/counselor-contacts/template`} download className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium border rounded-lg hover:bg-muted/50 transition-colors" data-testid="download-contacts-template">
                    <Download className="w-3.5 h-3.5" /> Template
                  </a>
                  <label className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-secondary text-secondary-foreground rounded-lg cursor-pointer hover:bg-secondary/90 transition-colors ${contactUploading ? "opacity-50 pointer-events-none" : ""}`} data-testid="upload-contacts-btn">
                    <Upload className="w-3.5 h-3.5" /> {contactUploading ? "Uploading..." : "Upload CSV"}
                    <input type="file" accept=".csv" className="hidden" onChange={handleContactUpload} disabled={contactUploading} />
                  </label>
                </div>
              </CardHeader>
              <CardContent>
                {!counselorContacts.length && !contactsLoading ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Phone className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>No counselor contacts uploaded yet</p>
                    <p className="text-sm mt-1">Download the template, fill in phone numbers, and upload the CSV</p>
                  </div>
                ) : contactsLoading ? (
                  <div className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div></div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Group</TableHead>
                        <TableHead>Counselor Name</TableHead>
                        <TableHead>Position</TableHead>
                        <TableHead>Phone Number</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(() => {
                        // Main table = head counselors only (no reserves).
                        // Every group is ALWAYS selectable — assigning a group
                        // that's taken automatically moves its current holder
                        // to the Reserve list (backend handles the swap).
                        const headRows = counselorContacts.filter(c => !c.is_reserve && c.group_code !== "RESERVE");
                        return headRows.map(c => {
                          const isUnassigned = c.group_code === "UNASSIGNED";
                          const ownCode = (c.group_code || "").toUpperCase();
                          const availableGroups = (groups || []).filter(g => !!g.code);
                          return (
                        <TableRow key={c.person_id || c.group_code} className={isUnassigned ? "bg-amber-50" : ""} data-testid={`contact-row-${c.person_id || c.group_code}`}>
                          <TableCell>
                            <select
                              value={isUnassigned ? "" : ownCode}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v === ownCode) return;
                                if (v === "__NONE__") {
                                  if (!window.confirm(`Unassign ${c.counselor_name || "this counselor"} from ${ownCode}?\n\nThey move to the Reserve list below and ${ownCode} becomes (Vacant). They will NOT be auto-restored by the CampMinder sync.`)) {
                                    e.target.value = ownCode; return;
                                  }
                                  vacateGroup(ownCode);
                                  return;
                                }
                                if (!v) return;
                                if (!isUnassigned && !window.confirm(`Move ${c.counselor_name || "this counselor"} from ${ownCode} to ${v}?`)) {
                                  e.target.value = ownCode; return;
                                }
                                assignContactGroup(c.person_id, v);
                              }}
                              className="text-xs border rounded px-2 py-1 bg-white min-w-[140px]"
                              data-testid={`assign-group-${c.person_id || c.group_code}`}
                            >
                              {isUnassigned && <option value="">Assign Group…</option>}
                              {!isUnassigned && <option value="__NONE__">— None (Unassign) —</option>}
                              {availableGroups.map(g => (
                                <option key={g.code} value={g.code}>{g.code} {g.name}</option>
                              ))}
                            </select>
                          </TableCell>
                          <TableCell className="font-medium">
                            {c.counselor_name === "(Vacant)" ? (
                              <span className="text-amber-700 italic">(Vacant — sub coverage)</span>
                            ) : (
                              c.counselor_name || "—"
                            )}
                            {c.sub_until && (
                              <span
                                className="ml-1.5 inline-block text-[10px] font-bold px-1.5 py-0.5 rounded bg-orange-100 text-orange-800 align-middle"
                                title={`Auto-reverts back to ${c.previous_hc_name || "previous HC"} on ${c.sub_until}`}
                                data-testid={`hc-sub-until-${c.group_code}`}
                              >
                                ⏱ until {c.sub_until}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {c.position || "—"}
                            {c.manually_assigned && (
                              <span className="ml-1.5 inline-block text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700" title="Assignment set manually by admin — protected from CampMinder sync overrides">
                                MANUAL
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm">{c.phone_number || <span className="text-amber-700 italic">—</span>}</TableCell>
                          <TableCell className="text-right whitespace-nowrap">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-xs h-7 px-2"
                              onClick={async () => {
                                const newName = window.prompt(`Head counselor name for ${c.group_code} (type "None" to mark Vacant):`, c.counselor_name || "");
                                if (newName === null) return;
                                const trimmedName = newName.trim();
                                const isNone = !trimmedName || trimmedName.toLowerCase() === "none";
                                const finalName = isNone ? "(Vacant)" : trimmedName;
                                const newPhone = isNone
                                  ? ""
                                  : window.prompt(`Phone for ${finalName} (10 digits, leave blank if covering with a sub):`, c.phone_number || "");
                                if (newPhone === null) return;
                                try {
                                  await axios.put(
                                    `${API}/admin/counselor-contacts/${c.group_code}`,
                                    { counselor_name: finalName, phone_number: newPhone || "", person_id: isNone ? null : c.person_id },
                                    { headers: { Authorization: `Bearer ${token}` } }
                                  );
                                  toast.success(isNone ? `${c.group_code} → Vacant` : `${c.group_code} updated`);
                                  fetchCounselorContacts();
                                } catch (e) {
                                  toast.error(e.response?.data?.detail || "Failed");
                                }
                              }}
                              data-testid={`edit-contact-${c.group_code}`}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-xs h-7 px-2 text-amber-700 hover:bg-amber-50"
                              onClick={async () => {
                                if (!window.confirm(`Mark ${c.group_code} (${c.counselor_name}) as Vacant?\n\nThis clears the phone too — SMS won't fire for this group until you assign someone new.`)) return;
                                try {
                                  await axios.put(
                                    `${API}/admin/counselor-contacts/${c.group_code}`,
                                    { counselor_name: "(Vacant)", phone_number: "", person_id: null },
                                    { headers: { Authorization: `Bearer ${token}` } }
                                  );
                                  toast.success(`${c.group_code} marked Vacant`);
                                  fetchCounselorContacts();
                                } catch (e) {
                                  toast.error(e.response?.data?.detail || "Failed");
                                }
                              }}
                              data-testid={`vacant-contact-${c.group_code}`}
                            >
                              Vacant
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => deleteContact(c.person_id ? `PID-${c.person_id}` : c.group_code)} className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-contact-${c.person_id || c.group_code}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </TableCell>
                        </TableRow>
                          );
                        });
                      })()}
                    </TableBody>
                  </Table>
                )}
                <p className="text-xs text-muted-foreground mt-3">{counselorContacts.length} contact{counselorContacts.length !== 1 ? "s" : ""} configured • SMS sent Mon-Fri at 12 PM, 2 PM, 3 PM EST • Early dismissal texts sent automatically</p>

                {/* Reserve / Substitute Staff — separate section at the bottom */}
                {(() => {
                  const reserves = counselorContacts.filter(c => c.is_reserve || c.group_code === "RESERVE");
                  return (
                    <div className="mt-6 pt-4 border-t-2 border-amber-200" data-testid="reserve-staff-section">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <h4 className="font-heading font-bold text-sm text-amber-900 flex items-center gap-1.5">
                            <Bell className="w-3.5 h-3.5" /> Reserve / Substitute Staff
                          </h4>
                          <p className="text-[11px] text-muted-foreground">
                            Subs and floaters available to backfill. Phones can be edited individually — they don't appear in the group-routing tables, but they ARE searchable in the supervisor pool.
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            const name = window.prompt("Reserve staff name?");
                            if (!name) return;
                            const phone = window.prompt(`Phone for ${name} (10 digits, optional):`, "") || "";
                            const position = window.prompt(`Position for ${name}:`, "Substitute") || "Substitute";
                            try {
                              await axios.post(`${API}/admin/counselor-contacts/reserve`, { counselor_name: name, phone_number: phone, position }, { headers: { Authorization: `Bearer ${token}` } });
                              toast.success(`Added ${name}`);
                              fetchCounselorContacts();
                            } catch (e) {
                              toast.error(e.response?.data?.detail || "Failed");
                            }
                          }}
                          data-testid="reserve-add-btn"
                        >
                          + Add Reserve
                        </Button>
                      </div>
                      {reserves.length === 0 ? (
                        <p className="text-xs text-muted-foreground italic">No reserve staff configured.</p>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-40">Assign To</TableHead>
                              <TableHead className="w-44">Name</TableHead>
                              <TableHead>Role</TableHead>
                              <TableHead>Phone</TableHead>
                              <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {reserves.map(r => {
                              // Every group always selectable — assigning to a
                              // taken group swaps its holder into Reserve.
                              const availableGroupsForReserve = (groups || []).filter(g => !!g.code);
                              return (
                              <TableRow key={`reserve-${r.counselor_name}`} className={!r.phone_number ? "bg-amber-50/50" : ""} data-testid={`reserve-row-${(r.counselor_name||"").replace(/\s+/g, "-").toLowerCase()}`}>
                                <TableCell>
                                  <select
                                    value=""
                                    onChange={async (e) => {
                                      const newCode = e.target.value;
                                      if (!newCode) return;
                                      if (!window.confirm(`Assign ${r.counselor_name} to ${newCode} (sub coverage)?`)) {
                                        e.target.value = ""; return;
                                      }
                                      // Optional: ask admin for an auto-revert date so they don't
                                      // have to remember to manually swap the original HC back.
                                      const subUntilRaw = window.prompt(
                                        `(Optional) Auto-revert ${r.counselor_name} back to reserve on date (YYYY-MM-DD).\n\nLeave blank if open-ended — you can swap them back manually anytime.`,
                                        ""
                                      );
                                      const subUntil = (subUntilRaw || "").trim();
                                      if (subUntil && !/^\d{4}-\d{2}-\d{2}$/.test(subUntil)) {
                                        toast.error("Auto-revert date must be YYYY-MM-DD (e.g., 2026-07-25) or blank");
                                        e.target.value = ""; return;
                                      }
                                      try {
                                        const token = localStorage.getItem("token");
                                        // Single atomic assign: backend archives the current
                                        // head to Reserve and removes this person's reserve rows.
                                        const subBody = { counselor_name: `${r.counselor_name} (sub)`, phone_number: r.phone_number || "" };
                                        if (r.person_id) subBody.person_id = r.person_id;
                                        if (subUntil) subBody.sub_until = subUntil;
                                        await axios.put(`${API}/admin/counselor-contacts/${newCode}`, subBody, { headers: { Authorization: `Bearer ${token}` } });
                                        toast.success(`${r.counselor_name} now covering ${newCode}${subUntil ? ` (auto-reverts on ${subUntil})` : ""}`);
                                        fetchCounselorContacts();
                                      } catch (err) {
                                        toast.error(err.response?.data?.detail || "Failed");
                                      }
                                    }}
                                    className="text-xs border rounded px-2 py-1 bg-white min-w-[130px]"
                                    data-testid={`reserve-assign-${(r.counselor_name||"").replace(/\s+/g, "-").toLowerCase()}`}
                                  >
                                    <option value="">— Cover Group —</option>
                                    {availableGroupsForReserve.map(g => (
                                      <option key={g.code} value={g.code}>{g.code} {g.name}</option>
                                    ))}
                                  </select>
                                </TableCell>
                                <TableCell className="font-medium">{r.counselor_name}</TableCell>
                                <TableCell className="text-xs">{r.position || "Substitute"}</TableCell>
                                <TableCell className="text-sm">
                                  {r.phone_number || <span className="text-amber-700 text-xs font-semibold">⚠ no phone</span>}
                                </TableCell>
                                <TableCell className="text-right">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-xs h-7 px-2"
                                    onClick={async () => {
                                      const next = window.prompt(`Phone for ${r.counselor_name} (10 digits, leave blank to clear):`, r.phone_number || "");
                                      if (next === null) return;
                                      try {
                                        const enc = encodeURIComponent(r.counselor_name.replace(/\s+/g, "_"));
                                        await axios.put(`${API}/admin/counselor-contacts/reserve/${enc}`, { phone_number: next }, { headers: { Authorization: `Bearer ${token}` } });
                                        toast.success("Updated");
                                        fetchCounselorContacts();
                                      } catch (e) {
                                        toast.error(e.response?.data?.detail || "Failed");
                                      }
                                    }}
                                    data-testid={`reserve-edit-${(r.counselor_name||"").replace(/\s+/g, "-").toLowerCase()}`}
                                  >
                                    Edit Phone
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      if (!window.confirm(`Remove ${r.counselor_name} from reserves?`)) return;
                                      const enc = encodeURIComponent(`RESERVE-${r.counselor_name.replace(/\s+/g, "_")}`);
                                      axios.delete(`${API}/admin/counselor-contacts/${enc}`, { headers: { Authorization: `Bearer ${token}` } })
                                        .then(() => { toast.success("Removed"); fetchCounselorContacts(); })
                                        .catch(() => toast.error("Failed"));
                                    }}
                                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                    data-testid={`reserve-delete-${(r.counselor_name||"").replace(/\s+/g, "-").toLowerCase()}`}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );})}
                          </TableBody>
                        </Table>
                      )}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
            </div>
          </TabsContent>

          <TabsContent value="allergy-reports">
            <AllergyReportsTab
              token={token}
              dietaryReports={dietaryReports}
              setDietaryReports={setDietaryReports}
              dietaryDetail={dietaryDetail}
              setDietaryDetail={setDietaryDetail}
              dietaryLoading={dietaryLoading}
              setDietaryLoading={setDietaryLoading}
            />
          </TabsContent>

          <TabsContent value="sync">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <RefreshCw className="w-5 h-5" />
                    CampMinder Sync
                  </CardTitle>
                  <CardDescription>
                    Sync camper data from CampMinder API
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-muted/30 rounded-lg space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Last Sync:</span>
                      <span className="font-medium">
                        {syncStatus?.last_sync 
                          ? new Date(syncStatus.last_sync).toLocaleString("en-US", { timeZone: "America/New_York" })
                          : "Never"}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Status:</span>
                      <Badge variant={
                        syncStatus?.status === "completed" ? "default" :
                        syncStatus?.status === "running" ? "secondary" : "outline"
                      }>
                        {syncStatus?.status || "Never synced"}
                      </Badge>
                    </div>
                    {syncStatus?.status === "running" && syncStatus?.progress && (
                      <div className="text-sm text-primary-600 font-medium bg-primary-50 px-3 py-2 rounded-md animate-pulse" data-testid="sync-progress">
                        {syncStatus.progress}
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Campers:</span>
                      <span className="font-medium">{syncStatus?.camper_count || 0}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Groups:</span>
                      <span className="font-medium">{syncStatus?.group_count || 0}</span>
                    </div>
                    {!syncing && syncStatus?.status === "completed" && syncStatus?.progress && (
                      <div className="text-xs text-green-700 bg-green-50 px-3 py-2 rounded-md" data-testid="sync-summary">
                        {syncStatus.progress}
                      </div>
                    )}
                    {!syncing && syncStatus?.status?.startsWith("failed") && (
                      <div className="text-xs text-red-700 bg-red-50 px-3 py-2 rounded-md" data-testid="sync-error">
                        {syncStatus.status}
                      </div>
                    )}
                  </div>
                  
                  <Button 
                    onClick={handleSync} 
                    disabled={syncing}
                    className="w-full"
                    data-testid="sync-btn"
                  >
                    {syncing ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Sync Now
                      </>
                    )}
                  </Button>
                  {syncing && (
                    <Button 
                      variant="destructive"
                      size="sm"
                      className="w-full"
                      data-testid="force-reset-sync-btn"
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem("token");
                          await axios.post(`${API}/sync/force-reset`, {}, { headers: { Authorization: `Bearer ${token}` } });
                          toast.success("Sync reset! You can now trigger a new sync.");
                          setSyncing(false);
                          fetchData();
                        } catch (err) {
                          toast.error("Failed to reset sync");
                        }
                      }}
                    >
                      Force Reset Stuck Sync
                    </Button>
                  )}

                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    data-testid="backup-sheet-sync-btn"
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem("token");
                        await axios.post(`${API}/sync/backup-sheet`, {}, { headers: { Authorization: `Bearer ${token}` } });
                        toast.success("Backup sync started — sheet + auto-pull from CampMinder");
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Failed to start backup sync");
                      }
                    }}
                  >
                    <RefreshCw className="w-3.5 h-3.5 mr-2" />
                    Backup Sync (Google Sheet)
                  </Button>

                  <FlexDaysSyncControl />
                  <FlexPushControl />

                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full border-sky-300 text-sky-800 hover:bg-sky-50"
                    data-testid="flex-last-day-report-btn"
                    onClick={() => {
                      const token = localStorage.getItem("token");
                      window.open(`${API}/reports/flex-last-days/pdf?token=${token}`, "_blank");
                    }}
                  >
                    <FileText className="w-3.5 h-3.5 mr-2" />
                    Last Day Flex Report (PDF)
                  </Button>

                  <ParentEmailSyncControl />

                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="sms-consent-sync-notice">
                    <p className="text-xs font-semibold text-amber-900 mb-1">SMS Consent Notice</p>
                    <p className="text-[11px] text-amber-800 leading-relaxed">
                      By syncing parent phone numbers from CampMinder, you confirm that parents have provided consent to receive text messages from Rolling River Day Camp regarding camp updates and camper notifications. Message and data rates may apply. Message frequency varies. Parents can reply STOP to unsubscribe or HELP for help. <a href="/sms-terms" target="_blank" rel="noopener noreferrer" className="underline text-blue-600 font-medium">View full SMS Terms</a>
                    </p>
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    data-testid="resume-sync-btn"
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem("token");
                        const res = await axios.post(`${API}/sync/resume`, {}, { headers: { Authorization: `Bearer ${token}` } });
                        if (res.data.pending && res.data.pending > 0) {
                          toast.success(`Resuming sync — ${res.data.pending} campers remaining`);
                          setSyncing(true);
                        } else {
                          toast.info("Nothing to resume — last sync completed");
                        }
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Failed to resume");
                      }
                    }}
                  >
                    Resume Sync from Checkpoint
                  </Button>

                  <Button
                    variant="outline"
                    className="w-full text-rose-700 border-rose-300 hover:bg-rose-50"
                    data-testid="admin-cleanup-stale-campers"
                    onClick={async () => {
                      try {
                        const preview = await axios.get(`${API}/sync/stale-campers`, {
                          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
                        });
                        const n = preview.data.stale_count || 0;
                        if (n === 0) {
                          toast.success("No stale campers — your DB matches CampMinder.");
                          return;
                        }
                        const names = (preview.data.stale_campers || [])
                          .slice(0, 15)
                          .map(c => `• ${c.first_name} ${c.last_name} (${c.group_code || "?"})`)
                          .join("\n");
                        const more = n > 15 ? `\n…and ${n - 15} more` : "";
                        if (!window.confirm(
                          `Delete ${n} stale (withdrawn) camper${n === 1 ? "" : "s"} no longer in CampMinder?\n\n${names}${more}\n\nThis only deletes kids with a person_id — manually-added campers are kept.`
                        )) return;
                        const res = await axios.post(`${API}/sync/stale-campers/cleanup`, {}, {
                          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
                        });
                        toast.success(`Removed ${res.data.deleted} stale camper${res.data.deleted === 1 ? "" : "s"}`);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Cleanup failed");
                      }
                    }}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Clean Up Stale Campers
                  </Button>

                  <p className="text-xs text-muted-foreground text-center">
                    Auto-sync: 12:00 AM EST &amp; 9:10 AM EST daily
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Droplets className="w-5 h-5" />
                    Swim Color Sync
                  </CardTitle>
                  <CardDescription>
                    Pull swim colors from Swim Track API
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-muted/30 rounded-lg space-y-2">
                    <p className="text-sm text-muted-foreground">
                      Fetches swim colors from the Rolling River Swim Track system and updates all matching campers.
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Also runs automatically after every CampMinder sync and daily at 8:00 AM EST.
                    </p>
                  </div>
                  <Button
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem("token");
                        await axios.post(`${API}/sync/swim-colors`, {}, { headers: { Authorization: `Bearer ${token}` } });
                        toast.success("Swim color sync started!");
                      } catch (err) {
                        toast.error("Failed to start swim color sync");
                      }
                    }}
                    className="w-full"
                    data-testid="swim-sync-btn"
                  >
                    <Droplets className="w-4 h-4 mr-2" />
                    Sync Swim Colors Now
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Utensils className="w-5 h-5" />
                    Lunch Order Sync
                  </CardTitle>
                  <CardDescription>
                    Pull daily lunch orders from the Lunch app
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-muted/30 rounded-lg space-y-2">
                    <p className="text-sm text-muted-foreground">
                      Fetches today's lunch orders by group and updates each camper's card. Resets daily.
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Auto-syncs daily at 10:00 AM EST.
                    </p>
                  </div>
                  <Button
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem("token");
                        await axios.post(`${API}/sync/lunch`, {}, { headers: { Authorization: `Bearer ${token}` } });
                        toast.success("Lunch order sync started!");
                      } catch (err) {
                        toast.error("Failed to start lunch sync");
                      }
                    }}
                    className="w-full"
                    data-testid="lunch-sync-btn"
                  >
                    <Utensils className="w-4 h-4 mr-2" />
                    Sync Lunch Orders Now
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <ArrowRightLeft className="w-5 h-5" />
                    Group Assignment Sync
                  </CardTitle>
                  <CardDescription>
                    Update camper group placements from CampMinder
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-muted/30 rounded-lg space-y-2">
                    <p className="text-sm text-muted-foreground">
                      Lightweight sync that only updates which group each camper belongs to — much faster than a full sync.
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Auto-syncs at 7:30 AM, 11:00 AM, and 2:00 PM EST daily.
                    </p>
                  </div>
                  <Button
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem("token");
                        await axios.post(`${API}/sync/groups`, {}, { headers: { Authorization: `Bearer ${token}` } });
                        toast.success("Group sync started!");
                      } catch (err) {
                        toast.error("Failed to start group sync");
                      }
                    }}
                    className="w-full"
                    data-testid="group-sync-btn"
                  >
                    <ArrowRightLeft className="w-4 h-4 mr-2" />
                    Sync Groups Now
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Bus className="w-5 h-5" />
                    Bus Data Sync
                  </CardTitle>
                  <CardDescription>
                    Sync AM/PM bus assignments from Google Sheet
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-muted/30 rounded-lg space-y-2">
                    <p className="text-sm text-muted-foreground">
                      Pulls bus route assignments (AM &amp; PM) from the transportation Google Sheet and updates camper records.
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Auto-syncs at 8:10 AM EST daily and after every CampMinder sync.
                    </p>
                  </div>
                  <Button
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem("token");
                        toast.info("Bus sync started...");
                        setBusSyncResult(null);
                        await axios.post(`${API}/sync/bus`, {}, { headers: { Authorization: `Bearer ${token}` } });
                        // Poll for result
                        let attempts = 0;
                        const poll = setInterval(async () => {
                          attempts++;
                          try {
                            const res = await axios.get(`${API}/sync/bus-status`, { headers: { Authorization: `Bearer ${token}` } });
                            const d = res.data;
                            if (d.status === "success") {
                              clearInterval(poll);
                              toast.success(d.message);
                              setBusSyncResult(d);
                            } else if (d.status === "error") {
                              clearInterval(poll);
                              toast.error(`Bus sync failed: ${d.message}`);
                              setBusSyncResult(d);
                            } else if (attempts > 30) {
                              clearInterval(poll);
                              toast.error("Bus sync timed out");
                            }
                          } catch { if (attempts > 30) clearInterval(poll); }
                        }, 2000);
                      } catch (err) {
                        toast.error("Failed: " + (err.response?.data?.detail || err.message));
                      }
                    }}
                    className="w-full"
                    data-testid="bus-sync-btn"
                  >
                    <Bus className="w-4 h-4 mr-2" />
                    Sync Bus Data Now
                  </Button>

                  {busSyncResult && (
                    <div className="p-3 rounded-lg border bg-slate-50 space-y-2" data-testid="bus-sync-result">
                      <div className="flex items-center justify-between flex-wrap gap-2 text-xs">
                        <span className="font-semibold text-slate-800">
                          {busSyncResult.campers_matched ?? 0}/{busSyncResult.total_campers ?? 0} matched
                          {" "}· {busSyncResult.sheet_rows ?? 0} sheet rows
                        </span>
                        <span className="text-slate-500">
                          {busSyncResult.timestamp ? new Date(busSyncResult.timestamp).toLocaleString("en-US", { timeZone: "America/New_York" }) : ""}
                        </span>
                      </div>
                      {(busSyncResult.unmatched_campers || []).length > 0 && (
                        <details className="text-xs" data-testid="bus-sync-unmatched-campers">
                          <summary className="cursor-pointer font-semibold text-amber-700 hover:text-amber-900">
                            ⚠ {busSyncResult.unmatched_campers.length} camper(s) NOT in sheet — click to view
                          </summary>
                          <div className="mt-2 max-h-64 overflow-y-auto pr-1 space-y-1">
                            {busSyncResult.unmatched_campers.map((u, i) => (
                              <div key={i} className="flex items-center gap-2 p-1.5 rounded bg-white border border-amber-200">
                                <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 bg-amber-50 shrink-0">{u.group_code || "—"}</Badge>
                                <span className="font-medium text-slate-800">{u.name}</span>
                                {u.preferred_name && <span className="text-slate-500">(pref: {u.preferred_name})</span>}
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                      {(busSyncResult.unmatched_sheet_rows || []).length > 0 && (
                        <details className="text-xs" data-testid="bus-sync-unmatched-sheet">
                          <summary className="cursor-pointer font-semibold text-rose-700 hover:text-rose-900">
                            ⚠ {busSyncResult.unmatched_sheet_rows.length} sheet row(s) didn't match any camper — typos / former campers
                          </summary>
                          <div className="mt-2 max-h-64 overflow-y-auto pr-1 space-y-1">
                            {busSyncResult.unmatched_sheet_rows.map((u, i) => (
                              <div key={i} className="p-1.5 rounded bg-white border border-rose-200">
                                <span className="font-medium text-slate-800">{u.sheet_first} {u.sheet_last}</span>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Upload className="w-5 h-5" />
                    Import Data
                  </CardTitle>
                  <CardDescription>
                    Import medical data or Flex camper schedules from Excel/CSV
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="border-2 border-dashed rounded-lg p-6 sm:p-8 text-center hover:border-primary transition-colors">
                    {importing ? (
                      <div className="py-4">
                        <RefreshCw className="w-8 h-8 text-primary animate-spin mx-auto mb-3" />
                        <p className="text-sm font-medium">Processing file...</p>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-4" />
                        <p className="text-sm text-muted-foreground mb-4">
                          Upload Excel (.xlsx) or CSV file
                        </p>
                        <Label htmlFor="file-upload" className="cursor-pointer">
                          <Input
                            id="file-upload"
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            onChange={handleFileUpload}
                            className="hidden"
                            data-testid="file-upload-input"
                          />
                          <Button variant="outline" asChild>
                            <span>
                              <FileSpreadsheet className="w-4 h-4 mr-2" />
                              Select File
                            </span>
                          </Button>
                        </Label>
                      </>
                    )}
                  </div>
                  
                  {importResult && !importResult.error && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-sm" data-testid="import-result">
                      <p className="font-medium text-green-800 mb-1">{importResult.message}</p>
                      <p className="text-green-700">Updated: {importResult.updated}, Skipped: {importResult.skipped}</p>
                      {importResult.errors?.length > 0 && (
                        <div className="mt-2 text-xs text-red-600">
                          {importResult.errors.map((err, i) => <p key={i}>{err}</p>)}
                        </div>
                      )}
                    </div>
                  )}
                  {importResult?.error && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                      {importResult.error}
                    </div>
                  )}

                  <div className="p-4 bg-muted/30 rounded-lg text-sm space-y-3">
                    <div>
                      <p className="font-medium mb-1">Medical Data Import:</p>
                      <ul className="list-disc list-inside text-muted-foreground text-xs space-y-0.5">
                        <li>Include <code className="bg-muted px-1 rounded">person_id</code> or <code className="bg-muted px-1 rounded">first_name</code> + <code className="bg-muted px-1 rounded">last_name</code></li>
                        <li>Other columns become medical data fields</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-medium mb-1">Flex Schedule Import:</p>
                      <ul className="list-disc list-inside text-muted-foreground text-xs space-y-0.5">
                        <li>Include camper name or ID columns</li>
                        <li>Add columns with "date", "day", or "schedule" in the name</li>
                        <li>Dates in YYYY-MM-DD or M/D/YYYY format</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-medium mb-1">Previous Group Import:</p>
                      <ul className="list-disc list-inside text-muted-foreground text-xs space-y-0.5">
                        <li>Columns: <code className="bg-muted px-1 rounded">first_name</code>, <code className="bg-muted px-1 rounded">last_name</code>, <code className="bg-muted px-1 rounded">previous_group</code></li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="border rounded-lg p-4 bg-slate-50">
                    <p className="text-sm font-medium mb-2">Upload Previous Groups (CSV/Excel)</p>
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handlePrevGroupUpload}
                      className="text-sm"
                      data-testid="prev-group-upload"
                    />
                    {prevGroupResult?.message && (
                      <p className="mt-2 text-sm text-green-600">{prevGroupResult.message}</p>
                    )}
                    {prevGroupResult?.error && (
                      <p className="mt-2 text-sm text-red-600">{prevGroupResult.error}</p>
                    )}
                  </div>

                  <div className="border rounded-lg p-4 bg-purple-50" data-testid="notes-2025-import-section">
                    <p className="text-sm font-medium mb-1">Import 2025 Incident Notes (CSV/Excel)</p>
                    <p className="text-xs text-muted-foreground mb-3">Columns: <code className="bg-muted px-1 rounded">last_name</code>, <code className="bg-muted px-1 rounded">first_name</code>, <code className="bg-muted px-1 rounded">group</code>, <code className="bg-muted px-1 rounded">notes</code></p>
                    {notesImporting ? (
                      <div className="flex items-center gap-2 text-sm text-purple-700">
                        <RefreshCw className="w-4 h-4 animate-spin" />Processing...
                      </div>
                    ) : (
                      <input
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        onChange={handleNotesImport}
                        className="text-sm"
                        data-testid="notes-2025-import-input"
                      />
                    )}
                    {notesImportResult && !notesImportResult.error && (
                      <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
                        <p className="font-medium text-green-800">{notesImportResult.message}</p>
                        <p className="text-green-700 text-xs">Applied now: {notesImportResult.updated} | Stored: {notesImportResult.stored} | Pending (auto-apply on sync): {notesImportResult.pending} | Skipped: {notesImportResult.skipped}</p>
                        {notesImportResult.errors?.length > 0 && (
                          <div className="mt-1 text-xs text-red-600">
                            {notesImportResult.errors.map((err, i) => <p key={i}>{err}</p>)}
                          </div>
                        )}
                      </div>
                    )}
                    {notesImportResult?.error && (
                      <p className="mt-2 text-sm text-red-600">{notesImportResult.error}</p>
                    )}
                  </div>

                  <div className="border rounded-lg p-4 bg-fuchsia-50" data-testid="legacy-notes-import-section">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div>
                        <p className="text-sm font-medium">Import CampMinder Notes Report (Legacy Notes)</p>
                        <p className="text-xs text-muted-foreground">
                          Bulk-load notes from a CampMinder notes report. Every row becomes a Legacy Note entry on the matching camper's card.
                        </p>
                      </div>
                      {legacyNotesStats && (
                        <Badge variant="outline" className="text-[10px] border-fuchsia-300 text-fuchsia-700 bg-white shrink-0">
                          {legacyNotesStats.total} loaded · {legacyNotesStats.matched} matched
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mb-3 leading-relaxed">
                      <p className="mb-1"><span className="font-semibold">Required:</span> a <code className="bg-muted px-1 rounded">notes</code> column + either <code className="bg-muted px-1 rounded">person_id</code> OR both <code className="bg-muted px-1 rounded">first_name</code> &amp; <code className="bg-muted px-1 rounded">last_name</code>.</p>
                      <p><span className="font-semibold">Optional:</span> <code className="bg-muted px-1 rounded">group</code>, <code className="bg-muted px-1 rounded">year</code> (e.g. 2024), <code className="bg-muted px-1 rounded">date</code> (year is auto-extracted if present).</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      <label className="flex items-center gap-1.5 text-xs">
                        <input
                          type="checkbox"
                          checked={legacyNotesReplace}
                          onChange={(e) => setLegacyNotesReplace(e.target.checked)}
                          data-testid="legacy-notes-replace-toggle"
                        />
                        <span>Replace previous import (recommended)</span>
                      </label>
                      <label className="flex items-center gap-1.5 text-xs">
                        Default year (fallback):
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder="e.g. 2024"
                          value={legacyNotesYear}
                          onChange={(e) => setLegacyNotesYear(e.target.value)}
                          className="w-20 px-2 py-1 text-xs border rounded"
                          data-testid="legacy-notes-default-year"
                        />
                      </label>
                    </div>
                    {legacyNotesImporting ? (
                      <div className="flex items-center gap-2 text-sm text-fuchsia-700">
                        <RefreshCw className="w-4 h-4 animate-spin" />Processing...
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-center gap-3">
                        <input
                          type="file"
                          accept=".csv,.xlsx,.xls"
                          onChange={handleLegacyNotesImport}
                          className="text-sm"
                          data-testid="legacy-notes-import-input"
                        />
                        {legacyNotesStats && legacyNotesStats.total > 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 text-xs border-red-300 text-red-700 hover:bg-red-50"
                            onClick={handleClearLegacyNotes}
                            data-testid="legacy-notes-clear-btn"
                          >
                            <Trash2 className="w-3 h-3 mr-1" /> Wipe all legacy notes
                          </Button>
                        )}
                      </div>
                    )}
                    {legacyNotesImportResult && !legacyNotesImportResult.error && (
                      <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
                        <p className="font-medium text-green-800">{legacyNotesImportResult.message}</p>
                        <p className="text-green-700 text-xs">
                          Inserted: {legacyNotesImportResult.inserted} · Matched to camper: {legacyNotesImportResult.matched} · Unmatched: {legacyNotesImportResult.unmatched} · Skipped: {legacyNotesImportResult.skipped}
                          {legacyNotesImportResult.replaced ? " · Previous import replaced" : ""}
                        </p>
                      </div>
                    )}
                    {legacyNotesImportResult?.error && (
                      <p className="mt-2 text-sm text-red-600">{legacyNotesImportResult.error}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          </Tabs>
          </div>
        )}

        {/* ═══ EVALUATIONS VIEW ═══ */}
        {currentView === "evaluations" && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <CardTitle className="font-heading text-base sm:text-lg">End of Season Program Evaluations</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      {evalsData ? `${evalsData.total_submitted}/${evalsData.total_groups} groups submitted` : "Loading..."}
                    </p>
                  </div>
                  <Button 
                    onClick={handleGenerateSummary} 
                    disabled={summarizing || !evalsData?.total_submitted}
                    data-testid="generate-summary-btn"
                  >
                    {summarizing ? "Generating..." : "Generate AI Summary & Email"}
                  </Button>
                </div>
                {evalsData && (
                  <Progress value={evalsData.total_groups ? (evalsData.total_submitted / evalsData.total_groups * 100) : 0} className="h-2 mt-3" />
                )}
              </CardHeader>
              <CardContent>
                {evalSummary && (
                  <div className="mb-6 p-4 bg-indigo-50 border border-indigo-200 rounded-lg" data-testid="eval-summary">
                    <h3 className="font-heading font-semibold text-indigo-800 mb-2">AI Summary (Generated {new Date(evalSummary.generated_at).toLocaleString("en-US", { timeZone: "America/New_York" })})</h3>
                    <div className="text-sm text-indigo-900 whitespace-pre-wrap">{evalSummary.summary}</div>
                  </div>
                )}
                
                {evalsData?.evaluations?.length > 0 ? (
                  <div className="space-y-3">
                    {Object.entries(evalsData.evaluations.reduce((groups, ev) => {
                      const d = ev.submitted_at ? ev.submitted_at.split("T")[0] : "Unknown";
                      (groups[d] = groups[d] || []).push(ev);
                      return groups;
                    }, {})).sort(([a], [b]) => b.localeCompare(a)).map(([date, evals], dayIdx) => (
                      <details key={date} open={dayIdx === 0} className="border rounded-xl overflow-hidden bg-white shadow-sm" data-testid={`eval-day-${date}`}>
                        <summary className="px-4 py-3 bg-purple-50 cursor-pointer hover:bg-purple-100 transition-colors font-semibold text-sm flex items-center justify-between">
                          <span>{new Date(date + "T12:00:00").toLocaleDateString("en-US", { timeZone: "America/New_York", weekday: "long", month: "short", day: "numeric", year: "numeric" })}</span>
                          <Badge variant="secondary" className="text-xs">{evals.length} eval{evals.length !== 1 ? "s" : ""}</Badge>
                        </summary>
                        <div className="p-3 space-y-3 border-t">
                          {evals.map((ev) => (
                            <details key={ev.group_code} className="border rounded-lg overflow-hidden" data-testid={`eval-${ev.group_code}`}>
                              <summary className="px-4 py-2.5 bg-muted/20 cursor-pointer hover:bg-muted/40 flex items-center justify-between">
                                <span className="font-medium text-sm">{ev.group_name} ({ev.group_code}) — {ev.head_counselor}</span>
                                <div className="flex items-center gap-2">
                                  <Button variant="ghost" size="sm" className="h-7 px-2" onClick={(e) => { e.preventDefault(); handleDownloadEvalPDF(ev.group_code); }} data-testid={`eval-pdf-${ev.group_code}`}>
                                    <Download className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button variant="ghost" size="sm" className="h-7 px-2 text-red-500 hover:text-red-700" onClick={(e) => { e.preventDefault(); handleDeleteEvaluation(ev.group_code); }} data-testid={`eval-delete-${ev.group_code}`}>
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              </summary>
                              <div className="p-4 space-y-3 text-sm">
                                <div><p className="font-semibold text-xs text-muted-foreground uppercase">Activity Appropriateness</p><p className="mt-1">{ev.responses?.activity_appropriateness || "—"}</p></div>
                                <div><p className="font-semibold text-xs text-muted-foreground uppercase">Activity Frequency</p><p className="mt-1">{ev.responses?.activity_frequency || "—"}</p></div>
                                <div><p className="font-semibold text-xs text-muted-foreground uppercase">Programming Improvement</p><p className="mt-1">{ev.responses?.programming_improvement || "—"}</p></div>
                                <div><p className="font-semibold text-xs text-muted-foreground uppercase">Other Ideas/Issues</p><p className="mt-1">{ev.responses?.other_ideas || "—"}</p></div>
                              </div>
                            </details>
                          ))}
                        </div>
                      </details>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">No evaluations submitted yet.</p>
                )}
              </CardContent>
            </Card>

        )}

        {/* ═══ TRIP FORMS VIEW ═══ */}
        {currentView === "trip-forms" && (
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <CardTitle className="font-heading text-base sm:text-lg">Trip Accountability Forms</CardTitle>
                    <CardDescription>View submitted trip forms by group and date</CardDescription>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Input
                      placeholder="Filter by group..."
                      value={tripFilterGroup}
                      onChange={(e) => { setTripFilterGroup(e.target.value); fetchTripForms(e.target.value, tripFilterDate); }}
                      className="w-40"
                      data-testid="trip-filter-group"
                    />
                    <Input
                      type="date"
                      value={tripFilterDate}
                      onChange={(e) => { setTripFilterDate(e.target.value); fetchTripForms(tripFilterGroup, e.target.value); }}
                      className="w-40"
                      data-testid="trip-filter-date"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {tripForms.length > 0 ? (
                  <div className="space-y-3">
                    {Object.entries(tripForms.reduce((groups, form) => {
                      const d = form.date || "Unknown";
                      (groups[d] = groups[d] || []).push(form);
                      return groups;
                    }, {})).sort(([a], [b]) => b.localeCompare(a)).map(([date, forms]) => (
                      <details key={date} open={date === Object.entries(tripForms.reduce((g, f) => { (g[f.date || "Unknown"] = g[f.date || "Unknown"] || []).push(f); return g; }, {})).sort(([a], [b]) => b.localeCompare(a))[0]?.[0]} className="border rounded-xl overflow-hidden bg-white shadow-sm" data-testid={`trip-day-${date}`}>
                        <summary className="px-4 py-3 bg-green-50 cursor-pointer hover:bg-green-100 transition-colors font-semibold text-sm flex items-center justify-between">
                          <span>{new Date(date + "T12:00:00").toLocaleDateString("en-US", { timeZone: "America/New_York", weekday: "long", month: "short", day: "numeric", year: "numeric" })}</span>
                          <Badge variant="secondary" className="text-xs">{forms.length} form{forms.length !== 1 ? "s" : ""}</Badge>
                        </summary>
                        <div className="p-3 space-y-3 border-t">
                          {forms.map((form) => (
                            <details key={form.id} className="border rounded-lg overflow-hidden" data-testid={`trip-form-${form.id}`}>
                              <summary className="px-4 py-2.5 bg-muted/20 cursor-pointer hover:bg-muted/40">
                                <div className="flex items-center justify-between">
                                  <span className="font-medium text-sm">
                                    {form.trip_name} — {form.group_name} ({form.group_code})
                                  </span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-muted-foreground">{new Date(form.submitted_at).toLocaleTimeString("en-US", { timeZone: "America/New_York" })}</span>
                                    <Button variant="ghost" size="sm" className="h-7 px-2" onClick={(e) => { e.preventDefault(); handleDownloadTripPDF(form.id, form.trip_name, form.group_code, form.date); }} data-testid={`trip-pdf-${form.id}`}>
                                      <Download className="w-3.5 h-3.5" />
                                    </Button>
                                    <Button variant="ghost" size="sm" className="h-7 px-2 text-red-500 hover:text-red-700" onClick={(e) => { e.preventDefault(); handleDeleteTripForm(form.id); }} data-testid={`trip-delete-${form.id}`}>
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                  </div>
                                </div>
                              </summary>
                              <div className="p-4 space-y-3">
                                {(form.counselor_sections || []).map((sec, idx) => (
                                  <div key={idx} className="border rounded-lg p-3 bg-slate-50">
                                    <p className="text-sm font-semibold text-slate-800 mb-2">Counselor: {sec.counselor}</p>
                                    <div className="flex flex-wrap gap-1.5">
                                      {(sec.campers || []).map((c, ci) => <Badge key={ci} variant="secondary" className="text-xs">{c}</Badge>)}
                                      {(!sec.campers || sec.campers.length === 0) && <span className="text-xs text-muted-foreground">No campers listed</span>}
                                    </div>
                                  </div>
                                ))}
                                <p className="text-xs text-muted-foreground">Submitted by: {form.submitted_by} at {new Date(form.submitted_at).toLocaleString("en-US", { timeZone: "America/New_York" })}</p>
                              </div>
                            </details>
                          ))}
                        </div>
                      </details>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Eye className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>No trip forms submitted yet</p>
                    <p className="text-sm mt-1">Counselors can submit trip forms from their dashboard</p>
                  </div>
                )}
              </CardContent>
            </Card>
        )}

        {/* ═══ BUDDY SHEETS VIEW ═══ */}
        {currentView === "buddy-sheets" && (
          <>
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <CardTitle className="font-heading text-base sm:text-lg">Daily Buddy Check Sheets</CardTitle>
                    <CardDescription>View and download saved buddy check sheets by group, date, and AM/PM</CardDescription>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Input
                      placeholder="Filter by group..."
                      value={buddyFilterGroup}
                      onChange={(e) => { setBuddyFilterGroup(e.target.value); fetchBuddySheets(e.target.value, buddyFilterDate, buddyFilterPeriod); }}
                      className="w-36"
                      data-testid="buddy-filter-group"
                    />
                    <Input
                      type="date"
                      value={buddyFilterDate}
                      onChange={(e) => { setBuddyFilterDate(e.target.value); fetchBuddySheets(buddyFilterGroup, e.target.value, buddyFilterPeriod); }}
                      className="w-40"
                      data-testid="buddy-filter-date"
                    />
                    <Select value={buddyFilterPeriod} onValueChange={(v) => { setBuddyFilterPeriod(v === "ALL" ? "" : v); fetchBuddySheets(buddyFilterGroup, buddyFilterDate, v === "ALL" ? "" : v); }}>
                      <SelectTrigger className="w-24" data-testid="buddy-filter-period">
                        <SelectValue placeholder="AM/PM" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ALL">All</SelectItem>
                        <SelectItem value="AM">AM</SelectItem>
                        <SelectItem value="PM">PM</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {buddySheets.length > 0 ? (
                  <ScrollArea className="h-[500px]">
                    <div className="space-y-3">
                      {Object.entries(buddySheets.reduce((groups, sheet) => {
                        const d = sheet.date || "Unknown";
                        (groups[d] = groups[d] || []).push(sheet);
                        return groups;
                      }, {})).sort(([a], [b]) => b.localeCompare(a)).map(([date, sheets], dayIdx) => (
                        <details key={date} open={dayIdx === 0} className="border rounded-xl overflow-hidden bg-white shadow-sm" data-testid={`buddy-day-${date}`}>
                          <summary className="px-4 py-3 bg-amber-50 cursor-pointer hover:bg-amber-100 transition-colors font-semibold text-sm flex items-center justify-between">
                            <span>{new Date(date + "T12:00:00").toLocaleDateString("en-US", { timeZone: "America/New_York", weekday: "long", month: "short", day: "numeric", year: "numeric" })}</span>
                            <Badge variant="secondary" className="text-xs">{sheets.length} sheet{sheets.length !== 1 ? "s" : ""}</Badge>
                          </summary>
                          <div className="p-3 space-y-2 border-t">
                            {sheets.map((sheet) => (
                              <div
                                key={sheet.id}
                                role="button"
                                tabIndex={0}
                                onClick={() => setViewingSheet(sheet)}
                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setViewingSheet(sheet); } }}
                                className="w-full border rounded-lg p-3 bg-amber-50/50 hover:bg-amber-100/70 transition-colors flex items-center justify-between gap-3 cursor-pointer"
                                data-testid={`buddy-sheet-${sheet.id}`}
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium text-sm">{sheet.group_name || sheet.group_code} ({sheet.group_code})</span>
                                    <Badge variant={sheet.period === "AM" ? "default" : "secondary"} className={`text-[10px] ${sheet.period === "AM" ? "bg-amber-600" : "bg-indigo-600 text-white"}`}>
                                      {sheet.period}
                                    </Badge>
                                  </div>
                                  <p className="text-xs text-muted-foreground mt-0.5">
                                    {(sheet.rows || []).filter(r => r.buddy_a || r.buddy_b).length} rows filled · Saved {new Date(sheet.saved_at).toLocaleString("en-US", { timeZone: "America/New_York" })} · click to view
                                  </p>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <span className="inline-flex items-center text-xs gap-1 px-2 py-1 rounded-md border bg-white text-amber-800">
                                    <Eye className="w-3.5 h-3.5" />View
                                  </span>
                                  <Button variant="ghost" size="sm" className="h-7 px-2" onClick={(e) => { e.stopPropagation(); handleDownloadBuddyPDF(sheet.id, sheet.group_code, sheet.date, sheet.period); }} data-testid={`buddy-pdf-${sheet.id}`}>
                                    <Download className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button variant="ghost" size="sm" className="h-7 px-2 text-red-500 hover:text-red-700" onClick={(e) => { e.stopPropagation(); handleDeleteBuddySheet(sheet.id); }} data-testid={`buddy-delete-${sheet.id}`}>
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </details>
                      ))}
                    </div>
                  </ScrollArea>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Droplets className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>No buddy sheets found</p>
                    <p className="text-sm mt-1">Counselors can fill out buddy check sheets from their dashboard</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* View Buddy Sheet Modal */}
            {viewingSheet && (
              <Dialog open={!!viewingSheet} onOpenChange={() => setViewingSheet(null)}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Droplets className="w-5 h-5 text-amber-600" />
                      {viewingSheet.group_name || viewingSheet.group_code} — {viewingSheet.period}
                    </DialogTitle>
                    <DialogDescription>
                      {new Date(viewingSheet.date + "T12:00:00").toLocaleDateString('en-US', { timeZone: 'America/New_York', weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })} | Group #{viewingSheet.group_number}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-3 mt-2">
                    {(() => {
                      const rows = viewingSheet.rows || [];
                      const sections = [
                        { key: "red", label: "Red — Deep End (Rows 1–10)", bg: "bg-red-50", header: "bg-red-500 text-white", border: "border-red-300" },
                        { key: "yellow", label: "Yellow — Middle (Rows 11–20)", bg: "bg-yellow-50", header: "bg-yellow-400 text-yellow-900", border: "border-yellow-300" },
                        { key: "green", label: "Green — Shallow (Rows 21–30)", bg: "bg-green-50", header: "bg-green-500 text-white", border: "border-green-300" },
                      ];
                      const grouped = { red: [], yellow: [], green: [] };
                      for (const r of rows) {
                        const sec = (r.section || "green").toLowerCase();
                        (grouped[sec] || grouped.green).push(r);
                      }
                      return sections.map(sec => (
                        grouped[sec.key].length > 0 && (
                          <div key={sec.key} className={`rounded-lg overflow-hidden border-2 ${sec.border}`} data-testid={`admin-bs-section-${sec.key}`}>
                            <div className={`${sec.header} px-3 py-2 text-xs font-bold uppercase tracking-wide`}>{sec.label}</div>
                            <div className={`divide-y ${sec.bg}`}>
                              <div className="grid grid-cols-[40px_1fr_1fr_1fr] gap-2 px-3 py-1.5 text-[10px] font-bold uppercase text-slate-700">
                                <div>#</div><div>Buddy A</div><div>Buddy B</div><div>Buddy C</div>
                              </div>
                              {grouped[sec.key].map((row, idx) => {
                                const empty = !row.buddy_a && !row.buddy_b && !row.buddy_c;
                                return (
                                  <div key={idx} className={`grid grid-cols-[40px_1fr_1fr_1fr] gap-2 px-3 py-1.5 text-sm bg-white ${empty ? "opacity-40" : ""}`}>
                                    <div className="font-bold text-slate-600">{row.row_num}</div>
                                    <div>{row.buddy_a || "—"}</div>
                                    <div>{row.buddy_b || "—"}</div>
                                    <div>{row.buddy_c || "—"}</div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )
                      ));
                    })()}
                    {viewingSheet.comments && (
                      <div className="bg-muted/30 rounded-lg p-3 border">
                        <p className="text-xs font-medium text-muted-foreground mb-1">Comments</p>
                        <p className="text-sm">{viewingSheet.comments}</p>
                      </div>
                    )}
                    <div className="flex justify-end gap-2 pt-2">
                      <Button variant="outline" size="sm" onClick={() => setViewingSheet(null)}>Close</Button>
                      <Button size="sm" onClick={() => { handleDownloadBuddyPDF(viewingSheet.id, viewingSheet.group_code, viewingSheet.date, viewingSheet.period); }} data-testid="buddy-view-download">
                        <Download className="w-4 h-4 mr-1" />Download PDF
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </>
        )}

        {/* ═══ UNASSIGNED VIEW ═══ */}
        {currentView === "unassigned" && (
            <Card>
              <CardHeader className="pb-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <UserX className="w-5 h-5 text-red-500" />
                      Unassigned Campers
                    </CardTitle>
                    <CardDescription>
                      {unassignedCampers.length} camper{unassignedCampers.length !== 1 ? "s" : ""} enrolled but not assigned to a bunk
                    </CardDescription>
                  </div>
                  <div className="relative w-full sm:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search unassigned..."
                      value={unassignedSearch}
                      onChange={(e) => setUnassignedSearch(e.target.value)}
                      className="pl-10"
                      data-testid="unassigned-search"
                    />
                  </div>
                </div>
                {/* Assign to Group Bar */}
                {unassignedCampers.length > 0 && (
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mt-4 p-3 bg-muted/30 rounded-lg border" data-testid="assign-group-bar">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground flex-shrink-0">
                      <ArrowRightLeft className="w-4 h-4" />
                      <span>{selectedUnassigned.size} selected</span>
                    </div>
                    <Select value={assigningGroup} onValueChange={setAssigningGroup}>
                      <SelectTrigger className="w-full sm:w-48" data-testid="assign-group-select">
                        <SelectValue placeholder="Select group..." />
                      </SelectTrigger>
                      <SelectContent>
                        {groups.map(g => (
                          <SelectItem key={g.code} value={g.code}>
                            {g.code} — {g.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={handleAssignGroup}
                      disabled={!assigningGroup || selectedUnassigned.size === 0 || assigning}
                      className="w-full sm:w-auto"
                      data-testid="assign-group-btn"
                    >
                      {assigning ? "Assigning..." : "Assign to Group"}
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent className="px-0 sm:px-6">
                {(() => {
                  const filtered = unassignedCampers.filter(c => {
                    if (!unassignedSearch) return true;
                    const q = unassignedSearch.toLowerCase();
                    return (c.first_name || "").toLowerCase().includes(q)
                      || (c.last_name || "").toLowerCase().includes(q)
                      || (c.preferred_name || "").toLowerCase().includes(q);
                  });
                  return (
                    <ScrollArea className="h-[500px]">
                      {filtered.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground px-6">
                          <UserX className="w-12 h-12 mx-auto mb-4 opacity-30" />
                          <p>{unassignedSearch ? "No unassigned campers match your search" : "All campers are assigned to groups!"}</p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-10">
                                  <input
                                    type="checkbox"
                                    checked={selectedUnassigned.size === filtered.length && filtered.length > 0}
                                    onChange={() => toggleAllUnassigned(filtered)}
                                    className="rounded"
                                    data-testid="unassigned-select-all"
                                  />
                                </TableHead>
                                <TableHead className="min-w-[160px]">Name</TableHead>
                                <TableHead className="hidden sm:table-cell">Age</TableHead>
                                <TableHead className="hidden md:table-cell">Gender</TableHead>
                                <TableHead>Sessions</TableHead>
                                <TableHead className="min-w-[140px]">Parent</TableHead>
                                <TableHead className="hidden lg:table-cell">Parent Phone</TableHead>
                                <TableHead className="text-right">PDF</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {filtered.map((camper) => (
                                <TableRow key={camper.id} data-testid={`unassigned-row-${camper.id}`} className={selectedUnassigned.has(camper.id) ? "bg-primary-50" : ""}>
                                  <TableCell>
                                    <input
                                      type="checkbox"
                                      checked={selectedUnassigned.has(camper.id)}
                                      onChange={() => toggleUnassignedSelection(camper.id)}
                                      className="rounded"
                                      data-testid={`unassigned-check-${camper.id}`}
                                    />
                                  </TableCell>
                                  <TableCell className="font-medium">
                                    <div>
                                      {camper.first_name} {camper.last_name}
                                    </div>
                                    <div className="sm:hidden text-xs text-muted-foreground mt-0.5">
                                      {camper.age ? `Age ${Math.floor(camper.age)}` : ""}{camper.gender ? ` | ${camper.gender}` : ""}
                                    </div>
                                  </TableCell>
                                  <TableCell className="hidden sm:table-cell">{camper.age ? Math.floor(camper.age) : "—"}</TableCell>
                                  <TableCell className="hidden md:table-cell">{camper.gender || "—"}</TableCell>
                                  <TableCell>
                                    <div className="flex flex-wrap gap-1">
                                      {(camper.sessions || []).map((s, i) => (
                                        <Badge key={i} variant="outline" className="text-[10px] px-1.5 py-0">
                                          {s.tag || s.name?.substring(0, 8) || "?"}
                                        </Badge>
                                      ))}
                                      {(!camper.sessions || camper.sessions.length === 0) && (
                                        <span className="text-xs text-muted-foreground">—</span>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-sm">
                                    <div>{camper.parent_info?.name || "—"}</div>
                                    <div className="lg:hidden text-xs text-muted-foreground">
                                      {formatPhone(camper.parent_info?.phone) || ""}
                                    </div>
                                  </TableCell>
                                  <TableCell className="hidden lg:table-cell text-sm">
                                    {formatPhone(camper.parent_info?.phone) || "—"}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDownloadPDF(camper)}
                                      data-testid={`unassigned-pdf-${camper.id}`}
                                      className="h-8 px-2 sm:px-3"
                                    >
                                      <Download className="w-4 h-4 sm:mr-1" />
                                      <span className="hidden sm:inline">PDF</span>
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </ScrollArea>
                  );
                })()}
              </CardContent>
            </Card>
        )}

        {/* ═══ ALUMNI VIEW ═══ */}
        {currentView === "alumni" && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <h2 className="font-heading font-bold text-lg flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-slate-600" />Alumni
                </h2>
                <p className="text-sm text-muted-foreground">{alumni.length} former camper{alumni.length !== 1 ? "s" : ""}</p>
              </div>
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search alumni..."
                  onChange={(e) => {
                    const q = e.target.value.toLowerCase();
                    if (!q) { fetchAlumni(selectedYear); return; }
                    setAlumni(prev => prev.filter(a =>
                      `${a.first_name} ${a.last_name}`.toLowerCase().includes(q)
                    ));
                  }}
                  className="pl-10"
                  data-testid="alumni-search"
                />
              </div>
            </div>

            {selectedAlumni ? (
              <Card data-testid="alumni-detail">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{selectedAlumni.first_name} {selectedAlumni.last_name}</CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedAlumni(null)} data-testid="alumni-detail-close">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  <CardDescription>Last Group: {selectedAlumni.last_group} | Last Year: {selectedAlumni.last_year}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Notes */}
                  {selectedAlumni.notes && (
                    <div>
                      <h4 className="text-sm font-semibold text-purple-700 mb-1">Camper Notes</h4>
                      <div className="p-3 rounded-lg border bg-purple-50/40 text-sm max-h-[200px] overflow-y-auto">
                        {selectedAlumni.notes.split("\n").filter(l => l.trim()).map((line, i) => (
                          <p key={i} className="py-0.5">{line}</p>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Year History */}
                  <div>
                    <h4 className="text-sm font-semibold mb-2">Year-by-Year History</h4>
                    {(selectedAlumni.year_history || []).sort((a, b) => b.year - a.year).map((yh, idx) => (
                      <details key={idx} open={idx === 0} className="border rounded-lg overflow-hidden mb-2" data-testid={`alumni-year-${yh.year}`}>
                        <summary className="px-4 py-2 bg-slate-50 cursor-pointer hover:bg-slate-100 font-semibold text-sm">
                          {yh.year} — {yh.group_code} ({yh.group_name || ""})
                        </summary>
                        <div className="p-3 space-y-2 text-sm">
                          {yh.notes && (
                            <div>
                              <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">Notes</p>
                              <div className="p-2 bg-purple-50/40 rounded border text-sm max-h-[120px] overflow-y-auto">
                                {yh.notes.split("\n").filter(l => l.trim()).map((line, i) => (
                                  <p key={i} className="py-0.5">{line}</p>
                                ))}
                              </div>
                            </div>
                          )}
                          {yh.tags?.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {yh.tags.map((t, ti) => <Badge key={ti} variant="secondary" className="text-xs">{t}</Badge>)}
                            </div>
                          )}
                        </div>
                      </details>
                    ))}
                    {(!selectedAlumni.year_history || selectedAlumni.year_history.length === 0) && (
                      <p className="text-sm text-muted-foreground italic">No historical data yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {alumni.length === 0 ? (
                  <div className="col-span-full text-center py-12 text-muted-foreground">
                    <GraduationCap className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>No alumni yet</p>
                    <p className="text-sm mt-1">Campers move to alumni when they don't re-enroll for the next season</p>
                  </div>
                ) : alumni.map(alum => (
                  <button
                    key={alum.id}
                    onClick={() => setSelectedAlumni(alum)}
                    className="p-4 border rounded-xl bg-white hover:shadow-md transition-all text-left"
                    data-testid={`alumni-card-${alum.id}`}
                  >
                    <p className="font-semibold text-sm">{alum.first_name} {alum.last_name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="secondary" className="text-[10px]">{alum.last_group}</Badge>
                      <span className="text-xs text-muted-foreground">Last: {alum.last_year}</span>
                    </div>
                    {alum.year_history && <p className="text-[10px] text-muted-foreground mt-1">{alum.year_history.length} year{alum.year_history.length !== 1 ? "s" : ""} of history</p>}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ═══ INCIDENT REPORTS VIEW ═══ */}
        {currentView === "incidents" && (
          <div className="space-y-4">
            <div>
              <h2 className="font-heading font-bold text-lg">Incident Reports</h2>
              <p className="text-sm text-muted-foreground">{filteredIncidents.length}{incidentFilterActive ? ` of ${incidentReports.length}` : ""} report{filteredIncidents.length !== 1 ? 's' : ''}{incidentFilterActive ? " matching" : " submitted"}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2" data-testid="incident-search-bar">
              <div className="relative flex-1 min-w-[180px]">
                <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search by camper name or group…"
                  value={incidentSearch}
                  onChange={(e) => setIncidentSearch(e.target.value)}
                  className="pl-8 h-9 bg-white"
                  data-testid="incident-search-input"
                />
              </div>
              <input
                type="date"
                value={incidentDateFilter}
                onChange={(e) => setIncidentDateFilter(e.target.value)}
                className="h-9 border rounded-md px-2 text-sm bg-white"
                data-testid="incident-date-filter"
              />
              {incidentFilterActive && (
                <Button variant="ghost" size="sm" className="h-9 text-xs" onClick={() => { setIncidentSearch(""); setIncidentDateFilter(""); }} data-testid="incident-clear-filters">
                  Clear
                </Button>
              )}
            </div>
            {filteredIncidents.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <AlertTriangle className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>{incidentFilterActive ? "No reports match your search" : "No incident reports submitted yet"}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {Object.entries(filteredIncidents.reduce((groups, report) => {
                  const d = report.date || "Unknown";
                  (groups[d] = groups[d] || []).push(report);
                  return groups;
                }, {})).sort(([a], [b]) => b.localeCompare(a)).map(([date, reports], dayIdx) => (
                  <details key={date} open={incidentFilterActive || dayIdx === 0} className="border rounded-xl overflow-hidden bg-white shadow-sm" data-testid={`incident-day-${date}`}>
                    <summary className="px-4 py-3 bg-orange-50 cursor-pointer hover:bg-orange-100 transition-colors font-semibold text-sm flex items-center justify-between">
                      <span>{new Date(date + "T12:00:00").toLocaleDateString("en-US", { timeZone: "America/New_York", weekday: "long", month: "short", day: "numeric", year: "numeric" })}</span>
                      <Badge variant="secondary" className="text-xs">{reports.length} report{reports.length !== 1 ? "s" : ""}</Badge>
                    </summary>
                    <div className="p-3 space-y-3 border-t">
                      {reports.map(report => {
                        const isOpen = expandedIncidentId === report.id;
                        return (
                        <Card key={report.id} className="hover:shadow-md transition-shadow" data-testid={`incident-${report.id}`}>
                          <CardContent className="p-4">
                            <button
                              type="button"
                              onClick={() => setExpandedIncidentId(isOpen ? null : report.id)}
                              className="w-full text-left"
                              data-testid={`incident-toggle-${report.id}`}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <Badge variant="secondary" className="text-xs">{report.group_code}</Badge>
                                    {report.great_day ? (
                                      <Badge className="text-xs bg-green-100 text-green-800 border-green-200"><CheckCircle className="w-3 h-3 mr-1" />Great Day</Badge>
                                    ) : (
                                      <Badge className={`text-xs ${report.incident_type === 'Medical' ? 'bg-red-100 text-red-800 border-red-200' : 'bg-orange-100 text-orange-800 border-orange-200'}`}>{report.incident_type}</Badge>
                                    )}
                                    {report.submitter_type && report.submitter_type !== "counselor" && (
                                      <Badge variant="outline" className="text-[10px] capitalize">{report.submitter_type}{report.specialty ? ` — ${report.specialty}` : ''}</Badge>
                                    )}
                                    {Array.isArray(report.supervisor_notes) && report.supervisor_notes.length > 0 && (
                                      <Badge className="text-[10px] bg-rose-100 text-rose-800 border-rose-200">
                                        <MessageSquare className="w-3 h-3 mr-1" />{report.supervisor_notes.length} note{report.supervisor_notes.length !== 1 ? 's' : ''}
                                      </Badge>
                                    )}
                                  </div>
                                  {report.camper_names && report.camper_names.length > 0 && (
                                    <p className="text-sm mt-1"><span className="text-muted-foreground">Camper(s):</span> <span className="font-medium">{report.camper_names.join(', ')}</span></p>
                                  )}
                                  {!isOpen && report.description && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{report.description}</p>}
                                  {!isOpen && report.great_day_notes && <p className="text-sm text-green-700 mt-1 line-clamp-2">{report.great_day_notes}</p>}
                                  {!isOpen && report.ai_summary && (
                                    <div className="mt-2 p-2 bg-blue-50 border border-blue-100 rounded-lg">
                                      <p className="text-xs font-semibold text-blue-700 mb-0.5">AI Summary</p>
                                      <p className="text-xs text-blue-900">{report.ai_summary}</p>
                                    </div>
                                  )}
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <Button variant="outline" size="sm" onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingReport(report);
                                  }} data-testid={`incident-edit-${report.id}`}>
                                    <Edit className="w-4 h-4 mr-1" />Edit
                                  </Button>
                                  <Button variant="outline" size="sm" onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      const tk = localStorage.getItem("token");
                                      const res = await axios.get(`${API}/incident-reports/${report.id}/pdf`, { headers: { Authorization: `Bearer ${tk}` }, responseType: 'blob' });
                                      window.open(window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })), '_blank');
                                    } catch { toast.error("Failed to load PDF"); }
                                  }} data-testid={`incident-pdf-${report.id}`}>
                                    <Download className="w-4 h-4 mr-1" />PDF
                                  </Button>
                                  {isOpen ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                                </div>
                              </div>
                            </button>

                            {isOpen && (
                              <div className="mt-3 pt-3 border-t space-y-3" data-testid={`incident-details-${report.id}`}>
                                {report.great_day ? (
                                  report.great_day_notes && (
                                    <div>
                                      <p className="text-xs font-bold text-muted-foreground mb-1">Notes:</p>
                                      <p className="text-sm bg-green-50 border border-green-100 rounded-lg p-3 whitespace-pre-wrap">{report.great_day_notes}</p>
                                    </div>
                                  )
                                ) : (
                                  <>
                                    {report.description && (
                                      <div>
                                        <p className="text-xs font-bold text-muted-foreground mb-1">Description</p>
                                        <p className="text-sm bg-white border rounded-lg p-3 whitespace-pre-wrap">{report.description}</p>
                                      </div>
                                    )}
                                    {report.actions_taken?.length > 0 && (
                                      <div>
                                        <p className="text-xs font-bold text-muted-foreground mb-1">Actions Taken</p>
                                        <div className="flex flex-wrap gap-1">{report.actions_taken.map((a, i) => <Badge key={i} variant="outline" className="text-[10px]">{a}</Badge>)}</div>
                                      </div>
                                    )}
                                    {report.parent_contact_detail && (
                                      <div>
                                        <p className="text-xs font-bold text-muted-foreground mb-1">Parent Contact</p>
                                        <p className="text-sm bg-white border rounded-lg p-3">{report.parent_contact_detail}</p>
                                      </div>
                                    )}
                                    {report.action_details && (
                                      <div>
                                        <p className="text-xs font-bold text-muted-foreground mb-1">Action Details</p>
                                        <p className="text-sm bg-white border rounded-lg p-3 whitespace-pre-wrap">{report.action_details}</p>
                                      </div>
                                    )}
                                    {report.followup?.length > 0 && (
                                      <div>
                                        <p className="text-xs font-bold text-muted-foreground mb-1">Follow-Up</p>
                                        <div className="flex flex-wrap gap-1">{report.followup.map((f, i) => <Badge key={i} variant="outline" className="text-[10px]">{f}</Badge>)}</div>
                                      </div>
                                    )}
                                    {report.followup_details && (
                                      <div>
                                        <p className="text-xs font-bold text-muted-foreground mb-1">Follow-Up Details</p>
                                        <p className="text-sm bg-white border rounded-lg p-3 whitespace-pre-wrap">{report.followup_details}</p>
                                      </div>
                                    )}
                                    {report.ai_summary && (
                                      <div>
                                        <p className="text-xs font-bold text-blue-700 mb-1">AI Summary</p>
                                        <p className="text-sm bg-blue-50 border border-blue-100 rounded-lg p-3 italic whitespace-pre-wrap">{report.ai_summary}</p>
                                      </div>
                                    )}
                                  </>
                                )}
                                <div className="flex items-center gap-2 text-[11px] text-muted-foreground pt-1">
                                  <Clock className="w-3 h-3" />
                                  Submitted {report.submitted_at ? new Date(report.submitted_at).toLocaleString("en-US", { timeZone: "America/New_York" }) : "—"}
                                </div>
                                <SupervisorNotesEditor
                                  report={report}
                                  token={token}
                                  onUpdate={(updated) => setIncidentReports(prev => prev.map(r => r.id === updated.id ? updated : r))}
                                />
                              </div>
                            )}
                          </CardContent>
                        </Card>
                        );
                      })}
                    </div>
                  </details>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ═══ MESSAGES VIEW ═══ */}
        {currentView === "messages" && !adminMsgSelectedGroup && (
          <div className="space-y-4" data-testid="admin-messages-groups">
            <SafeModePanel token={token} />
            <div className="flex items-center justify-between gap-3">
              <h2 className="font-heading font-bold text-lg text-blue-800 flex items-center gap-2">
                <MessageSquare className="w-5 h-5" /> Parent Messages — All Groups
              </h2>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  try {
                    const res = await axios.get(`${API}/admin/messages/list-stuck`, { headers: { Authorization: `Bearer ${token}` } });
                    setStuckMessages(res.data?.messages || []);
                    setShowStuckModal(true);
                  } catch (err) {
                    toast.error("Failed to load stuck messages");
                  }
                }}
                className="text-xs"
                data-testid="admin-msg-view-stuck"
              >
                <AlertTriangle className="w-3.5 h-3.5 mr-1" />Stuck
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  if (!window.confirm("Recover any inbound messages currently stuck under 'unknown' (phone/email match failed) and re-clean older email bodies? This is safe to re-run.")) return;
                  try {
                    const res = await axios.post(`${API}/admin/messages/backfill-inbound`, {}, { headers: { Authorization: `Bearer ${token}` } });
                    const s = res.data?.sms || {}; const e = res.data?.email || {};
                    if ((s.fixed || 0) + (e.fixed || 0) > 0) {
                      toast.success(`Recovered: ${s.fixed || 0} SMS, ${e.fixed || 0} email · re-cleaned ${e.re_cleaned || 0} email bodies`);
                    }
                    const totalUnmatched = (s.no_match || 0) + (e.no_match || 0);
                    if (totalUnmatched > 0) {
                      const lines = [];
                      (s.unmatched || []).forEach(u => lines.push(`SMS  ${u.from_phone || "(no phone)"}  →  "${u.preview}"`));
                      (e.unmatched || []).forEach(u => lines.push(`Email  ${u.from_email || "(no email)"}  →  "${u.preview}"`));
                      window.alert(
                        `${totalUnmatched} message(s) couldn't be matched to any camper.\n\n` +
                        lines.join("\n") +
                        "\n\nClick 'Stuck' to view and manually assign these messages to the right camper."
                      );
                    } else if ((s.fixed || 0) + (e.fixed || 0) === 0) {
                      toast.info("No stuck messages to recover.");
                    }
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Backfill failed");
                  }
                }}
                className="text-xs"
                data-testid="admin-msg-recover-backlog"
              >
                <RefreshCw className="w-3.5 h-3.5 mr-1" />Recover Backlog
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  if (!window.confirm(
                    "Re-scan all inbound messages from the last 30 days for sibling routing errors?\n\n" +
                    "• Messages with a sibling name in the body will be auto-moved to the correct group.\n" +
                    "• Messages that are AMBIGUOUS (could be either sibling) will be moved to the Stuck queue AND the parent will be texted asking which child.\n" +
                    "• When the parent replies, the message will route to the right counselor automatically."
                  )) return;
                  try {
                    const res = await axios.post(
                      `${API}/admin/messages/rescan-sibling-routing?hours=720&dry_run=false`,
                      {},
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    const d = res.data || {};
                    const moved = d.reassigned_count || 0;
                    const amb = d.flagged_ambiguous_count || 0;
                    const texted = (d.flagged_ambiguous || []).filter(x => x.parent_text_sent).length;
                    toast.success(
                      `Scanned ${d.scanned}. Auto-fixed ${moved}. Moved ${amb} to Stuck queue. Texted ${texted} parent(s) to disambiguate.`
                    );
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Sibling rescan failed");
                  }
                }}
                className="text-xs"
                data-testid="admin-msg-rescan-siblings"
              >
                <Users className="w-3.5 h-3.5 mr-1" />Rescan Siblings
              </Button>
              </div>
            </div>
            {adminMsgLoading ? (
              <div className="text-center py-10 text-muted-foreground">Loading...</div>
            ) : (
              <>
                <p className="text-xs text-muted-foreground">{adminMsgGroups.length > 0 ? `${adminMsgGroups.length} groups with messages` : "No messages sent yet. Select a group to compose."}</p>
                <div className="grid grid-cols-3 sm:grid-cols-6 md:grid-cols-8 gap-2">
                  {(adminMsgGroups.length > 0 ? adminMsgGroups : groups).map(g => {
                    const gc = g.group_code || g.code;
                    const count = g.message_count || 0;
                    return (
                      <button
                        key={gc}
                        onClick={() => {
                          setAdminMsgSelectedGroup(gc);
                          setAdminMsgLoading(true);
                          setAdminMsgCamperFilter(""); setAdminMsgDateFrom(""); setAdminMsgDateTo("");
                          const h = { Authorization: `Bearer ${token}` };
                          axios.get(`${API}/supervisor/messages/${gc}`, { headers: h, params: { year: selectedYear } }).then(res => setAdminMsgMessages(res.data)).catch(() => toast.error("Failed")).finally(() => setAdminMsgLoading(false));
                        }}
                        className="flex flex-col items-center gap-1 p-3 rounded-xl border-2 bg-blue-50 border-blue-200 hover:border-blue-400 hover:shadow-md transition-all active:scale-95"
                        data-testid={`admin-msg-group-${gc}`}
                      >
                        <span className="text-xs font-bold text-blue-800">{gc}</span>
                        {count > 0 && <span className="text-[10px] text-muted-foreground">{count} msgs</span>}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {currentView === "messages" && adminMsgSelectedGroup && (
          <div className="space-y-4" data-testid="admin-messages-detail">
            <ThreadedMessagesInbox
              token={token}
              groupCode={adminMsgSelectedGroup}
              groupName={adminMsgSelectedGroup}
              campers={campers.filter(c => (c.group_code || "").toUpperCase() === adminMsgSelectedGroup.toUpperCase())}
              onBack={() => { setAdminMsgSelectedGroup(null); setAdminMsgMessages([]); setAdminMsgText(""); setAdminMsgCamperFilter(""); setAdminMsgDateFrom(""); setAdminMsgDateTo(""); setAdminMsgSelectedCamperIds(new Set()); }}
            />
          </div>
        )}

        {/* ═══ INTRAMURALS VIEW ═══ */}
        {currentView === "intramurals" && (
          <IntramuralsLanding token={token} onBack={() => setCurrentView("menu")} />
        )}

        {/* ═══ NAVIGATOR INTRAMURALS VIEW ═══ */}
        {currentView === "nav-intramurals" && (
          <NavigatorIntramuralsLanding headers={{ Authorization: `Bearer ${token}` }} onBack={() => setCurrentView("menu")} isEditable={true} />
        )}

        {/* ═══ COLOR WAR VIEW ═══ */}
        {currentView === "color-war" && (
          <ColorWarView token={token} isAdmin={true} />
        )}

        {/* ═══ TESTING TOOLS VIEW ═══ */}
        {currentView === "testing-tools" && (
          <TestingToolsView token={token} />
        )}
        {currentView === "attendance-report" && (
          <AttendanceReportView token={token} onBack={() => setCurrentView("menu")} />
        )}
        {currentView === "admin-conflicts" && (
          <AdminConflictsView token={token} onBack={() => setCurrentView("menu")} />
        )}
        {currentView === "incident-digest" && (
          <IncidentDigestView token={token} onBack={() => setCurrentView("menu")} />
        )}

        {currentView === "sms-settings" && (
          <SmsSettingsPanel token={token} onBack={() => setCurrentView("menu")} />
        )}
        {currentView === "schedules" && (
          <SchedulesPicker title="All Schedules" adminMode={true} onBack={() => setCurrentView("menu")} />
        )}

        {currentView === "logins" && (
          <LoginsView onBack={() => setCurrentView("menu")} />
        )}

        {currentView === "group-logins" && (
          <AdminGroupLoginsView token={token} onBack={() => setCurrentView("menu")} />
        )}

        {currentView === "intramural-uploads" && (
          <AdminIntramuralUploadsView token={token} onBack={() => setCurrentView("menu")} />
        )}

        {currentView === "activity-caps" && (
          <ActivityCapsView token={token} onBack={() => setCurrentView("menu")} />
        )}
        {currentView === "camper-locations" && (
          <CamperLocationsView token={token} onBack={() => setCurrentView("menu")} />
        )}

        {currentView === "service-providers" && (
          <ServiceProvidersView token={token} onBack={() => setCurrentView("menu")} />
        )}

        {currentView === "carnival-snack" && (
          <CarnivalSnackAdmin token={token} onBack={() => setCurrentView("menu")} />
        )}

        {currentView === "parent-survey" && (
          <ParentSurveyAdminView token={token} onBack={() => setCurrentView("menu")} />
        )}

        {currentView === "milestone-certificates" && (
          <MilestoneCertificatesView token={token} onBack={() => setCurrentView("menu")} />
        )}

        {currentView === "teams" && (
          <TeamsAdminView token={token} onBack={() => setCurrentView("menu")} />
        )}

        {currentView === "transport-audit" && (
          <TransportAuditView onBack={() => setCurrentView("menu")} />
        )}

        {currentView === "division-notifications" && (
          <DivisionNotificationsView onBack={() => setCurrentView("menu")} />
        )}

        {currentView === "dismissal-reports" && (
          <DismissalReportsView onBack={() => setCurrentView("menu")} />
        )}

        {currentView === "trip-rosters" && (
          <TripRostersView
            data={tripRostersData}
            loading={tripRostersLoading}
            onBack={() => setCurrentView("menu")}
            headerSubtitle="All groups — CIT & Navigators"
            syncing={tripRostersSyncing}
            printingExceptions={tripExceptionsPrinting}
            onPrintExceptions={async (location) => {
              setTripExceptionsPrinting(location);
              try {
                const res = await axios.get(`${API}/admin/dismissal/trip-exceptions.pdf`, {
                  headers: { Authorization: `Bearer ${token}` }, responseType: "blob",
                  params: { location },
                });
                window.open(window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" })), "_blank");
              } catch (e) {
                if (e.response?.status === 404) toast.info(`No kids marked ${location} today — add them via PM exceptions first`);
                else toast.error("Failed to generate exceptions PDF");
              } finally { setTripExceptionsPrinting(false); }
            }}
            onSync={async () => {
              setTripRostersSyncing(true);
              try {
                const res = await axios.post(`${API}/sync/cit-trips`, {}, { headers: { Authorization: `Bearer ${token}` } });
                const r = await axios.get(`${API}/cit/overnight`, { headers: { Authorization: `Bearer ${token}` } });
                setTripRostersData(r.data);
                if (res.data.ok) {
                  toast.success(`Synced — sheet has ${res.data.sheet_overnight} overnight, ${res.data.sheet_broadway} broadway`);
                  if ((res.data.unmatched_person_ids || []).length > 0) {
                    toast.warning(`${res.data.unmatched_person_ids.length} sheet row(s) have no matching camper (PersonIDs: ${res.data.unmatched_person_ids.join(", ")})`);
                  }
                } else {
                  toast.error(`Sync failed: ${res.data.error || "unknown error"}`);
                }
              } catch {
                toast.error("Sheet sync failed");
              }
              setTripRostersSyncing(false);
            }}
          />
        )}

        {currentView === "camper-photos" && (
          <CamperPhotosView onBack={() => setCurrentView("menu")} />
        )}

        {editingReport && (
          <IncidentReportEditor
            report={editingReport}
            token={token}
            onClose={() => setEditingReport(null)}
            onSaved={(updated) => setIncidentReports(prev => prev.map(r => r.id === updated.id ? { ...r, ...updated } : r))}
          />
        )}
      </main>

      {/* Stuck inbound messages modal */}
      <Dialog open={showStuckModal} onOpenChange={setShowStuckModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="stuck-messages-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Stuck Inbound Messages ({stuckMessages.length})
            </DialogTitle>
          </DialogHeader>
          {stuckMessages.length > 0 && (
            <div className="flex items-center justify-end -mt-2 mb-2">
              <Button
                size="sm"
                variant="outline"
                className="text-xs text-rose-700 border-rose-300 hover:bg-rose-50"
                data-testid="stuck-clear-all"
                onClick={async () => {
                  if (!window.confirm(
                    `Permanently DELETE all ${stuckMessages.length} stuck inbound message(s)?\n\n` +
                    "Use this after you've reassigned the ones that matter — the rest will be wiped from the queue.\n\n" +
                    "This cannot be undone."
                  )) return;
                  try {
                    const res = await axios.delete(`${API}/admin/messages/clear-stuck`, { headers: { Authorization: `Bearer ${token}` } });
                    toast.success(`Cleared ${res.data?.deleted || 0} stuck message(s)`);
                    setStuckMessages([]);
                    setShowStuckModal(false);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Failed to clear stuck messages");
                  }
                }}
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" />Clear All
              </Button>
            </div>
          )}
          {stuckMessages.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No stuck messages.</p>
          ) : (
            <div className="space-y-2">
              {stuckMessages.map(m => (
                <div key={m.id} className="border rounded-lg p-3 bg-amber-50/50">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-[10px]">{m.channel?.toUpperCase()}</Badge>
                        <span className="text-xs font-mono text-gray-600">{m.from_phone || m.from_email || "—"}</span>
                        <span className="text-[10px] text-muted-foreground">
                          {m.timestamp ? new Date(m.timestamp).toLocaleString() : ""}
                        </span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap text-gray-700 break-words">{m.message}</p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => { setReassignTargetMsg(m); setReassignSearch(""); }}
                      className="bg-blue-600 hover:bg-blue-700 text-xs shrink-0"
                      data-testid={`reassign-msg-${m.id}`}
                    >
                      Reassign
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reassign-to-camper picker */}
      <Dialog open={!!reassignTargetMsg} onOpenChange={() => setReassignTargetMsg(null)}>
        <DialogContent className="max-w-md max-h-[70vh] overflow-y-auto" data-testid="reassign-modal">
          <DialogHeader>
            <DialogTitle>Move message to which camper?</DialogTitle>
          </DialogHeader>
          {reassignTargetMsg && (
            <p className="text-xs text-muted-foreground mb-2 p-2 bg-gray-50 rounded">
              From <strong>{reassignTargetMsg.from_phone || reassignTargetMsg.from_email}</strong>:
              <br />
              "{(reassignTargetMsg.message || "").slice(0, 120)}"
            </p>
          )}
          <Input
            placeholder="Search by camper or parent name..."
            value={reassignSearch}
            onChange={e => setReassignSearch(e.target.value)}
            className="mb-2"
            data-testid="reassign-search"
            autoFocus
          />
          <div className="space-y-1 max-h-[40vh] overflow-y-auto">
            {campers
              .filter(c => {
                if (!reassignSearch) return false;
                const q = reassignSearch.toLowerCase();
                const name = `${c.first_name || ""} ${c.last_name || ""} ${c.preferred_name || ""}`.toLowerCase();
                const parent = `${c.parent_info?.name || ""} ${c.parent_info?.email || ""} ${c.parent_info?.phone || ""} ${c.parent_info?.secondary_email || ""} ${c.parent_info?.secondary_phone || ""}`.toLowerCase();
                return name.includes(q) || parent.includes(q);
              })
              .slice(0, 25)
              .map(c => (
                <button
                  key={c.id}
                  onClick={async () => {
                    try {
                      await axios.post(
                        `${API}/admin/messages/${reassignTargetMsg.id}/reassign`,
                        { camper_id: c.id },
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      toast.success(`Moved to ${c.first_name} ${c.last_name} (${c.group_code})`);
                      setStuckMessages(prev => prev.filter(m => m.id !== reassignTargetMsg.id));
                      setReassignTargetMsg(null);
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Reassign failed");
                    }
                  }}
                  className="w-full text-left p-2 rounded hover:bg-blue-50 border border-transparent hover:border-blue-200"
                  data-testid={`reassign-pick-${c.id}`}
                >
                  <div className="text-sm font-medium">{c.first_name} {c.last_name}</div>
                  <div className="text-xs text-muted-foreground">
                    {c.group_code} · {c.parent_info?.name} · {c.parent_info?.phone}
                  </div>
                </button>
              ))}
            {reassignSearch && campers.filter(c => {
              const q = reassignSearch.toLowerCase();
              const name = `${c.first_name || ""} ${c.last_name || ""} ${c.preferred_name || ""}`.toLowerCase();
              const parent = `${c.parent_info?.name || ""} ${c.parent_info?.email || ""} ${c.parent_info?.phone || ""}`.toLowerCase();
              return name.includes(q) || parent.includes(q);
            }).length === 0 && (
              <p className="text-xs text-muted-foreground py-4 text-center">No campers match.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <FieldDialog
        open={fieldDialog.open}
        field={fieldDialog.field}
        onClose={() => setFieldDialog({ open: false, field: null })}
        onSave={handleSaveField}
      />

      {/* Group Dialog */}
      <GroupDialog
        open={groupDialog.open}
        group={groupDialog.group}
        onClose={() => setGroupDialog({ open: false, group: null })}
        onSave={handleSaveGroup}
      />

      {/* Fire Drill PDF — date picker modal */}
      <Dialog open={fireDrillOpen} onOpenChange={setFireDrillOpen}>
        <DialogContent className="sm:max-w-md" data-testid="fire-drill-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flame className="w-5 h-5 text-red-600" /> Fire Drill PDF
            </DialogTitle>
            <DialogDescription>
              One page per group, sorted by last name. Real daily roster as of the date you pick (HS1 / HS2 / Full Season). CIT groups excluded. B01-B15 + G01-G15 + N02-N16.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="fire-drill-date" className="text-xs uppercase tracking-wide text-muted-foreground">Drill Date</Label>
              <Input
                id="fire-drill-date"
                type="date"
                value={fireDrillDate}
                min="2026-06-29"
                max="2026-08-21"
                onChange={(e) => setFireDrillDate(e.target.value)}
                data-testid="fire-drill-date-input"
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">Pick today or any future camp day — useful for printing tomorrow&apos;s drill in advance.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFireDrillOpen(false)} data-testid="fire-drill-cancel-btn">Cancel</Button>
            <Button onClick={handleDownloadFireDrillPDF} disabled={fireDrillLoading || !fireDrillDate} className="bg-red-600 hover:bg-red-700" data-testid="fire-drill-download-btn">
              <Download className="w-4 h-4 mr-1" /> {fireDrillLoading ? "Generating..." : "Download PDF"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ title, value, icon: Icon, color }) {
  const colorClasses = {
    primary: "bg-primary-50 text-primary-600",
    secondary: "bg-secondary-50 text-secondary-600",
    accent: "bg-blue-50 text-blue-600",
    success: "bg-green-50 text-green-600",
    warning: "bg-yellow-50 text-yellow-600",
    muted: "bg-muted text-muted-foreground"
  };

  return (
    <Card>
      <CardContent className="pt-4 sm:pt-6 pb-4 px-3 sm:px-6">
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <p className="text-xs sm:text-sm text-muted-foreground truncate">{title}</p>
            <p className="text-xl sm:text-2xl font-heading font-bold truncate">{value}</p>
          </div>
          <div className={`p-2 sm:p-3 rounded-full flex-shrink-0 ${colorClasses[color]}`}>
            <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function FieldDialog({ open, field, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: "",
    field_type: "text",
    is_medical: true,
    display_order: 0
  });

  useEffect(() => {
    if (field) {
      setFormData({
        name: field.name || "",
        field_type: field.field_type || "text",
        is_medical: field.is_medical ?? true,
        display_order: field.display_order || 0
      });
    } else {
      setFormData({
        name: "",
        field_type: "text",
        is_medical: true,
        display_order: 0
      });
    }
  }, [field, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-heading">
            {field ? "Edit Field" : "Add Custom Field"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="field-name">Field Name</Label>
            <Input
              id="field-name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Allergies, Medications"
              required
              data-testid="field-name-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="field-type">Field Type</Label>
            <Select
              value={formData.field_type}
              onValueChange={(value) => setFormData(prev => ({ ...prev, field_type: value }))}
            >
              <SelectTrigger data-testid="field-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="text">Text</SelectItem>
                <SelectItem value="textarea">Long Text</SelectItem>
                <SelectItem value="number">Number</SelectItem>
                <SelectItem value="date">Date</SelectItem>
                <SelectItem value="boolean">Yes/No</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="display-order">Display Order</Label>
            <Input
              id="display-order"
              type="number"
              value={formData.display_order}
              onChange={(e) => setFormData(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
              data-testid="field-order-input"
            />
          </div>
          
          <div className="flex items-center justify-between">
            <Label htmlFor="is-medical">Medical Field</Label>
            <Switch
              id="is-medical"
              checked={formData.is_medical}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_medical: checked }))}
              data-testid="field-medical-switch"
            />
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" data-testid="save-field-btn">
              {field ? "Update" : "Create"} Field
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function GroupDialog({ open, group, onClose, onSave }) {
  const [formData, setFormData] = useState({
    code: "",
    name: "",
    campminder_bunk_id: ""
  });

  useEffect(() => {
    if (group) {
      setFormData({
        code: group.code || "",
        name: group.name || "",
        campminder_bunk_id: group.campminder_bunk_id || ""
      });
    } else {
      setFormData({ code: "", name: "", campminder_bunk_id: "" });
    }
  }, [group, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-heading">
            {group ? "Edit Group" : "Add Group"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="group-code">Group Code (Username)</Label>
            <Input
              id="group-code"
              value={formData.code}
              onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
              placeholder="e.g., B02"
              required
              data-testid="group-code-input"
            />
            <p className="text-xs text-muted-foreground">
              Counselors will use this as their username
            </p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="group-name">Group Name (Password)</Label>
            <Input
              id="group-name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Sharks"
              required
              data-testid="group-name-input"
            />
            <p className="text-xs text-muted-foreground">
              This will be the password (case insensitive)
            </p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="bunk-id">CampMinder Bunk ID (Optional)</Label>
            <Input
              id="bunk-id"
              value={formData.campminder_bunk_id}
              onChange={(e) => setFormData(prev => ({ ...prev, campminder_bunk_id: e.target.value }))}
              placeholder="Leave empty if not linked"
              data-testid="group-bunkid-input"
            />
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" data-testid="save-group-btn">
              {group ? "Update" : "Create"} Group
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


const colorMap = {
  blue: "bg-blue-100 text-blue-700", amber: "bg-amber-100 text-amber-700", purple: "bg-purple-100 text-purple-700",
  green: "bg-green-100 text-green-700", red: "bg-red-100 text-red-700", indigo: "bg-indigo-100 text-indigo-700",
  teal: "bg-teal-100 text-teal-700", orange: "bg-orange-100 text-orange-700", emerald: "bg-emerald-100 text-emerald-700",
  cyan: "bg-cyan-100 text-cyan-700", sky: "bg-sky-100 text-sky-700", rose: "bg-rose-100 text-rose-700",
  yellow: "bg-yellow-100 text-yellow-700", lime: "bg-lime-100 text-lime-700", fuchsia: "bg-fuchsia-100 text-fuchsia-700",
  pink: "bg-pink-100 text-pink-700",
};
const borderMap = {
  blue: "border-blue-200 hover:border-blue-400", amber: "border-amber-200 hover:border-amber-400", purple: "border-purple-200 hover:border-purple-400",
  green: "border-green-200 hover:border-green-400", red: "border-red-200 hover:border-red-400", indigo: "border-indigo-200 hover:border-indigo-400",
  teal: "border-teal-200 hover:border-teal-400", orange: "border-orange-200 hover:border-orange-400", emerald: "border-emerald-200 hover:border-emerald-400",
  cyan: "border-cyan-200 hover:border-cyan-400", sky: "border-sky-200 hover:border-sky-400", rose: "border-rose-200 hover:border-rose-400",
  yellow: "border-yellow-200 hover:border-yellow-400", lime: "border-lime-200 hover:border-lime-400", fuchsia: "border-fuchsia-200 hover:border-fuchsia-400",
  pink: "border-pink-200 hover:border-pink-400",
};

function AdminMenuCard({ icon: Icon, label, color = "blue", onClick, href, disabled, badge, testId }) {
  const inner = (
    <>
      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${colorMap[color] || colorMap.blue}`}>
        <Icon className="w-5 h-5" />
      </div>
      <span className="text-[11px] font-bold text-gray-700 leading-tight text-center">{label}</span>
      {disabled && <span className="text-[9px] text-gray-400 -mt-1">Coming Soon</span>}
      {badge && (
        <span className="absolute top-1 right-1 bg-red-500 text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-bold">{badge}</span>
      )}
      {href && <ExternalLink className="absolute top-1.5 right-1.5 w-3 h-3 text-gray-300" />}
    </>
  );

  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" data-menu-card-label={label} className={`relative flex flex-col items-center gap-1.5 p-3 bg-white rounded-xl border-2 ${borderMap[color] || borderMap.blue} hover:shadow-md transition-all`} data-testid={testId}>
        {inner}
      </a>
    );
  }

  return (
    <button onClick={disabled ? undefined : onClick} aria-disabled={disabled || undefined} data-menu-card-label={label} className={`relative flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all ${disabled ? "bg-gray-50 border-gray-200 opacity-60 cursor-not-allowed" : `bg-white ${borderMap[color] || borderMap.blue} hover:shadow-md`}`} data-testid={testId}>
      {inner}
    </button>
  );
}

// Hobby/Intensive program weeks — dates mirror the backend (HOBBY_WEEKS / NAV_HOBBY_WEEKS / NAV_INTENSIVE_WEEKS)
const WEEK_DATES = {
  mariner: [
    { week: 1, date: "2026-07-01" }, { week: 2, date: "2026-07-08" },
    { week: 3, date: "2026-07-15" }, { week: 4, date: "2026-07-22" },
    { week: 5, date: "2026-07-29" }, { week: 6, date: "2026-08-05" },
  ],
  navigator: [
    { week: 1, date: "2026-06-29" }, { week: 2, date: "2026-07-06" },
    { week: 3, date: "2026-07-13" }, { week: 4, date: "2026-07-20" },
    { week: 5, date: "2026-07-27" }, { week: 6, date: "2026-08-03" },
  ],
  intensive: [
    { week: 1, date: "2026-07-01" }, { week: 2, date: "2026-07-08" },
    { week: 3, date: "2026-07-15" }, { week: 4, date: "2026-07-22" },
    { week: 5, date: "2026-07-29" }, { week: 6, date: "2026-08-05" },
  ],
};

function prettyWeekDate(iso) {
  try {
    const [y, m, d] = iso.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  } catch { return iso; }
}

function WeeklyRosterExportRow({ opt, api }) {
  const [week, setWeek] = useState(1);
  const [busy, setBusy] = useState(false);
  const weeks = WEEK_DATES[opt.weeksKey] || [];

  const download = async () => {
    setBusy(true);
    try {
      const tk = localStorage.getItem("token");
      const res = await axios.get(`${api}/hobbies/export-weekly/${opt.key}?week=${week}`, {
        headers: { Authorization: `Bearer ${tk}` },
        responseType: "blob",
      });
      const cd = res.headers["content-disposition"] || "";
      const match = /filename="([^"]+)"/.exec(cd);
      const filename = match ? match[1] : `${opt.label.replace(/\s/g, "_")}_Week${week}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${opt.label} Week ${week} downloaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Export failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`rounded-xl border-2 p-3 ${opt.accent}`} data-testid={`weekly-roster-row-${opt.key}`}>
      <p className="text-sm font-bold text-gray-800 mb-2">{opt.label}</p>
      <div className="flex items-center gap-2">
        <select
          value={week}
          onChange={e => setWeek(Number(e.target.value))}
          className="flex-1 h-9 text-xs border rounded-lg px-2 bg-white"
          data-testid={`weekly-roster-week-${opt.key}`}
        >
          {weeks.map(w => (
            <option key={w.week} value={w.week}>Week {w.week} — {prettyWeekDate(w.date)}</option>
          ))}
        </select>
        <Button
          size="sm"
          onClick={download}
          disabled={busy}
          className="h-9 text-xs bg-sky-900 hover:bg-sky-800 text-white flex-shrink-0"
          data-testid={`weekly-roster-export-${opt.key}`}
        >
          <Download className="w-3.5 h-3.5 mr-1" />
          {busy ? "..." : "Export XLSX"}
        </Button>
      </div>
    </div>
  );
}




const ADMIN_SESSION_SHORT_LABELS = {
  "Full Season- 5 Days": "FS5",
  "Half Season 1- 5 Days": "HS1",
  "Half Season 2- 5 Days": "HS2",
  "6 Week Season": "6W",
  "Flex 20 Days": "F20",
  "Flex 25 Days": "F25",
  "Flex 30 Days": "F30",
};

function adminHasMedicalAlerts(camper) {
  const md = camper.medical_data || {};
  if (md.Medical) {
    return Object.entries(md.Medical).some(([k, v]) => {
      if (!v) return false;
      const vl = v.toString().toLowerCase();
      return (k.toLowerCase().includes('allerg') || k.toLowerCase().includes('reaction'))
        && vl !== 'no' && vl !== 'none' && vl !== 'n/a' && vl !== '';
    });
  }
  return Object.entries(md).some(([k, v]) => {
    if (!v || typeof v === 'object') return false;
    const vl = v.toString().toLowerCase();
    return k.toLowerCase().includes('allerg')
      && vl !== 'no' && vl !== 'none' && vl !== 'n/a' && vl !== '';
  });
}

function AdminCITCardInline({ camper }) {
  const [open, setOpen] = useState(false);
  const gc = (camper.group_code || "").toUpperCase();
  if (!gc.startsWith("CIT")) return null;

  const ranks = [1, 2, 3];
  const choicePairs = ranks
    .map(r => ({
      rank: r,
      program: camper[`cit_program_${r}`] || "",
      choice: camper[`cit_choice_${r}`] || "",
    }))
    .filter(p => p.program || p.choice);

  const trips = camper.cit_trips || {};
  const enrolledTrips = Object.entries(trips).filter(([, v]) => v && String(v).trim().length > 0);
  const paperwork = camper.cit_parent_signature || "";
  const hasAny = choicePairs.length > 0 || enrolledTrips.length > 0 || paperwork;
  if (!hasAny) return null;

  return (
    <div className="mt-2" data-testid={`admin-cit-card-inline-${camper.id}`}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        className="w-full flex items-center justify-between gap-1.5 px-2.5 py-1.5 rounded-md border border-fuchsia-200 bg-fuchsia-50 hover:bg-fuchsia-100 transition-colors"
        data-testid={`admin-cit-card-inline-toggle-${camper.id}`}
      >
        <span className="flex items-center gap-1.5 text-xs font-semibold text-fuchsia-800">
          <Star className="w-3 h-3" />
          CIT Info
          {paperwork ? (
            <Badge variant="outline" className="h-4 px-1 text-[9px] border-emerald-300 text-emerald-700 bg-white">&#10003; Paper</Badge>
          ) : (
            <Badge variant="outline" className="h-4 px-1 text-[9px] border-amber-300 text-amber-700 bg-white">No Paper</Badge>
          )}
          {enrolledTrips.length > 0 && (
            <Badge variant="outline" className="h-4 px-1 text-[9px] border-fuchsia-300 text-fuchsia-700 bg-white">{enrolledTrips.length} trips</Badge>
          )}
        </span>
        {open ? <ChevronUp className="w-3 h-3 text-fuchsia-700" /> : <ChevronDown className="w-3 h-3 text-fuchsia-700" />}
      </button>
      {open && (
        <div
          className="mt-1.5 p-2 rounded-md border border-fuchsia-100 bg-white space-y-1.5"
          onClick={(e) => e.stopPropagation()}
        >
          {choicePairs.length > 0 && (
            <div className="space-y-0.5">
              <p className="text-[9px] font-semibold uppercase tracking-wider text-fuchsia-700">Choices</p>
              {choicePairs.map(p => (
                <div key={p.rank} className="flex items-start gap-1.5 text-[11px] leading-tight">
                  <span className="font-bold text-fuchsia-700">#{p.rank}</span>
                  <span className="text-slate-700">
                    {p.program && <span className="font-medium">{p.program}</span>}
                    {p.program && p.choice && <span className="text-slate-400"> · </span>}
                    {p.choice}
                  </span>
                </div>
              ))}
            </div>
          )}
          {enrolledTrips.length > 0 && (
            <div className="space-y-0.5">
              <p className="text-[9px] font-semibold uppercase tracking-wider text-fuchsia-700">Trips</p>
              <div className="flex flex-wrap gap-1">
                {enrolledTrips.map(([name]) => {
                  const d = formatAdminCitTripDate(name);
                  return (
                    <Badge key={name} variant="outline" className="h-4 px-1 text-[9px] border-fuchsia-300 text-fuchsia-700 bg-fuchsia-50">
                      {name}{d ? ` · ${d}` : ""}
                    </Badge>
                  );
                })}
              </div>
            </div>
          )}
          {paperwork && (
            <p className="text-[10px] text-slate-600"><span className="font-semibold">Paperwork:</span> {paperwork}</p>
          )}
        </div>
      )}
    </div>
  );
}

function AdminCamperCard({ camper, onClick }) {
  const initials = `${camper.first_name?.[0] || ''}${camper.last_name?.[0] || ''}`.toUpperCase();
  const displayName = camper.first_name;
  const sessionTags = (camper.sessions || []).map(s => ADMIN_SESSION_SHORT_LABELS[s.name] || s.name).filter(Boolean);
  const hasAlerts = adminHasMedicalAlerts(camper);
  const isNursery = (camper.group_code || "").toUpperCase().startsWith("N");
  const birthday = camper.date_of_birth ? new Date(camper.date_of_birth + 'T00:00:00').toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric' }) : null;

  return (
    <div
      onClick={onClick}
      className="group relative overflow-hidden rounded-xl border bg-white shadow-sm hover:shadow-lg transition-all duration-200 hover:-translate-y-1 cursor-pointer active:scale-[0.98]"
      data-testid={`admin-camper-card-${camper.id}`}
    >
      {hasAlerts && (
        <div className="absolute top-0 right-0 w-0 h-0 border-t-[36px] border-t-red-500 border-l-[36px] border-l-transparent">
          <AlertTriangle className="absolute -top-8 right-0.5 w-3.5 h-3.5 text-white" />
        </div>
      )}
      <div className="p-4 sm:p-5">
        <div className="flex items-start gap-3 sm:gap-4">
          <CamperPhoto
            camperId={camper.id}
            firstName={camper.first_name}
            lastName={camper.last_name}
            size={56}
            className="flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-semibold text-foreground text-sm sm:text-base truncate">
              {displayName} {camper.last_name}
            </h3>
            <div className="flex flex-wrap items-center gap-1.5 mt-1.5 sm:mt-2">
              {camper.group_code && (
                <Badge variant="secondary" className="text-xs bg-primary-50 text-primary-700 border-primary-200">
                  {camper.cit_subgroup || camper.group_code}
                </Badge>
              )}
              {camper.age && <span className="text-xs text-muted-foreground">Age {Math.floor(camper.age)}</span>}
              {isNursery ? (
                <Badge variant="outline" className="text-xs border-0 px-1.5">
                  <span className="w-2.5 h-2.5 rounded-full inline-block mr-1 bg-green-500" />
                  Kiddie
                </Badge>
              ) : camper.swim_color ? (
                <Badge variant="outline" className="text-xs border-0 px-1.5">
                  <span className={`w-2.5 h-2.5 rounded-full inline-block mr-1 ${
                    (() => {
                      const c = (camper.swim_color || "").toLowerCase();
                      if (c.includes("red") && c.includes("blue")) return "bg-gradient-to-r from-red-500 to-blue-500";
                      if (c.includes("yellow") && c.includes("blue")) return "bg-gradient-to-r from-yellow-400 to-blue-500";
                      if (c.includes("green") && c.includes("blue")) return "bg-gradient-to-r from-green-500 to-blue-500";
                      if (c === "red") return "bg-red-500";
                      if (c === "yellow") return "bg-yellow-400";
                      if (c === "blue") return "bg-blue-500";
                      if (c === "green") return "bg-green-500";
                      return "bg-gray-300";
                    })()
                  }`} />
                  {camper.swim_color}
                </Badge>
              ) : null}
              {camper.years_at_camp != null && (
                <Badge variant="outline" className="text-xs border-indigo-300 text-indigo-600 bg-indigo-50">Yr {camper.years_at_camp}</Badge>
              )}
              {camper.previous_group && (
                <Badge variant="outline" className="text-xs border-slate-300 text-slate-600 bg-slate-50">Prev: {camper.previous_group}</Badge>
              )}
              {sessionTags.map(tag => (
                <Badge key={tag} variant="outline" className={`text-xs ${
                  tag.startsWith('F') && tag !== 'FS5' ? 'border-orange-300 text-orange-600 bg-orange-50' : 'border-emerald-300 text-emerald-700 bg-emerald-50'
                }`}>{tag}</Badge>
              ))}
            </div>
            {birthday && <p className="text-xs text-muted-foreground mt-1">Born {birthday}</p>}
            {camper.lunch_order && camper.lunch_date === new Date().toISOString().split('T')[0] && (
              <div className="flex items-center gap-1.5 mt-1.5 px-2.5 py-1.5 bg-amber-50 border border-amber-200 rounded-lg" data-testid="admin-camper-lunch">
                <Utensils className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                <span className="text-xs font-medium text-amber-800 truncate">{camper.lunch_order}</span>
              </div>
            )}
            {(camper.am_bus || camper.pm_bus || camper.am_method || camper.pm_method || camper.pm_weekly_today) && (
              <div className="flex flex-col gap-1 mt-1.5 px-2.5 py-1.5 bg-green-50 border border-green-200 rounded-lg" data-testid="admin-camper-transport">
                <div className="flex items-center gap-2">
                  <Bus className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                  <span className="text-xs font-medium text-green-800">
                    {camper.am_bus && <>AM: {camper.am_bus}</>}
                    {camper.am_bus && (camper.pm_bus || camper.pm_weekly_today) && <span className="mx-1 text-green-400">|</span>}
                    {camper.pm_weekly_today ? (
                      <>
                        PM: {camper.pm_weekly_location}{camper.pm_weekly_remove ? " (not on pickup list)" : ""}
                        <span className="ml-1 text-[9px] font-bold uppercase px-1 py-px rounded bg-emerald-200 text-emerald-800 border border-emerald-300 align-middle" data-testid={`admin-pm-weekly-${camper.id}`}>Weekly</span>
                      </>
                    ) : camper.pm_bus ? <>PM: {camper.pm_bus}</> : null}
                  </span>
                </div>
                {(camper.am_method || camper.pm_method) && (
                  <span className="text-[10px] text-green-600 ml-5">
                    {camper.am_method && <>Drop-off: {camper.am_method}</>}
                    {camper.am_method && camper.pm_method && <> · </>}
                    {camper.pm_method && <>Pickup: {camper.pm_method}</>}
                  </span>
                )}
              </div>
            )}
            <AdminCITCardInline camper={camper} />
          </div>
        </div>
        {hasAlerts && (
          <div className="mt-2.5 sm:mt-3 pt-2.5 sm:pt-3 border-t">
            <Badge className="text-xs bg-red-50 text-red-700 border-red-200">
              <AlertTriangle className="w-3 h-3 mr-1" />Medical Alert
            </Badge>
          </div>
        )}
      </div>
      <div className="px-4 sm:px-5 py-2.5 sm:py-3 bg-muted/30 border-t text-xs text-muted-foreground flex items-center justify-between">
        <span>View Details</span>
        <span className="opacity-0 group-hover:opacity-100 transition-opacity">&rarr;</span>
      </div>
    </div>
  );
}

function AdminDismissalExceptionEditor({ camperId, camperName }) {
  const [todayPm, setTodayPm] = useState(null);
  const [todayAm, setTodayAm] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [period, setPeriod] = useState("pm"); // "am" | "pm"
  const [mode, setMode] = useState("today"); // "today" | "range"
  const [location, setLocation] = useState("");
  const [note, setNote] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const today = useMemo(() => new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" }), []);

  const loadFresh = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/admin/dismissal-exceptions/${camperId}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { date: today },
      });
      const pm = res.data?.today || {};
      const am = res.data?.today_am || {};
      setTodayPm(pm.location ? pm : null);
      setTodayAm(am.location ? am : null);
      setUpcoming(res.data?.upcoming || []);
    } catch { setTodayPm(null); setTodayAm(null); setUpcoming([]); }
  }, [camperId, today]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      await loadFresh();
      if (alive) {
        setStartDate(today); setEndDate(today); setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [loadFresh, today]);

  // Pre-fill location/note when user switches between AM/PM tabs
  useEffect(() => {
    const src = period === "am" ? todayAm : todayPm;
    if (src && src.location) { setLocation(src.location); setNote(src.note || ""); }
    else { setLocation(""); setNote(""); }
  }, [period, todayAm, todayPm]);

  const save = async (overrides = {}) => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const payload = overrides.clearOnly
        ? { location: null, date: today, period }
        : mode === "range"
          ? { location: location || null, note, start_date: startDate, end_date: endDate, period }
          : { location: location || null, note, date: today, period };
      const res = await axios.put(
        `${API}/admin/dismissal-exceptions/${camperId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadFresh();
      if (res.data?.status === "cleared") toast.success(`${period.toUpperCase()} exception cleared`);
      else if ((res.data?.dates || []).length > 1) toast.success(`${location} (${period.toUpperCase()}) set for ${res.data.dates.length} days`);
      else toast.success(`${period.toUpperCase()} dismissal set to ${location}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  // Delete a single upcoming exception row (by date + period)
  const deleteUpcoming = async (rowDate, rowPeriod) => {
    if (!window.confirm(`Delete ${rowPeriod.toUpperCase()} exception on ${rowDate}?`)) return;
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/admin/dismissal-exceptions/${camperId}`,
        { location: null, date: rowDate, period: rowPeriod },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadFresh();
      toast.success(`${rowPeriod.toUpperCase()} exception on ${rowDate} deleted`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  // Delete ALL upcoming exceptions in one shot (uses the date range of upcoming rows)
  const deleteAllUpcoming = async () => {
    if (upcoming.length === 0) return;
    if (!window.confirm(`Delete ALL ${upcoming.length} upcoming exception${upcoming.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    try {
      const token = localStorage.getItem("token");
      const sorted = [...upcoming].sort((a, b) => a.date.localeCompare(b.date));
      const first = sorted[0].date;
      const last = sorted[sorted.length - 1].date;
      // Backend clears per period; loop both am/pm if either has rows in the window
      const periodsPresent = Array.from(new Set(upcoming.map(u => (u.period || "pm"))));
      let totalDeleted = 0;
      for (const p of periodsPresent) {
        const res = await axios.put(
          `${API}/admin/dismissal-exceptions/${camperId}`,
          { location: null, start_date: first, end_date: last, period: p },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        totalDeleted += res.data?.deleted || 0;
      }
      await loadFresh();
      toast.success(`Deleted ${totalDeleted} upcoming exception${totalDeleted === 1 ? "" : "s"}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk delete failed"); }
  };

  if (loading) return null;

  const rangeDayCount = (() => {
    if (mode !== "range" || !startDate || !endDate) return 0;
    try {
      const d0 = new Date(startDate + "T12:00:00");
      const d1 = new Date(endDate + "T12:00:00");
      return Math.max(0, Math.round((d1 - d0) / 86400000) + 1);
    } catch { return 0; }
  })();

  const amOptions = ["Car Drop Off"];
  const pmOptions = ["Main Tent", "Hockey Rink", "Auditorium", "After Care", "Front Door", "Classroom", "Overnight", "Broadway"];
  const opts = period === "am" ? amOptions : pmOptions;
  const activeToday = period === "am" ? todayAm : todayPm;

  return (
    <section data-testid="admin-dismissal-exception-editor" className="border-2 border-dashed border-rose-300 rounded-xl p-3 sm:p-4 bg-rose-50/60">
      <h3 className="font-heading font-semibold text-base flex items-center gap-2 mb-1 text-rose-800">
        <Bus className="w-4 h-4" />
        Dismissal Exception
        <Badge variant="outline" className="ml-2 text-[10px] border-rose-300 text-rose-700 uppercase">Admin only</Badge>
      </h3>
      <p className="text-xs text-muted-foreground mb-2">
        Override <strong>{camperName.split(" ")[0]}</strong>'s arrival / dismissal for a single day or a date range.
        AM "Car Drop Off" removes them from the bus roster. PM After Care / Front Door / Classroom / Overnight / Broadway removes them from all 3 dismissal reports.
      </p>

      {/* AM / PM tab */}
      <div className="flex gap-1.5 mb-3" role="tablist">
        <button type="button" onClick={() => setPeriod("am")}
          className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition ${period === "am" ? "bg-rose-600 text-white border-rose-700" : "bg-white text-rose-800 border-rose-200 hover:bg-rose-100"}`}
          data-testid="admin-dismissal-exc-period-am">
          AM (Morning Arrival)
        </button>
        <button type="button" onClick={() => setPeriod("pm")}
          className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition ${period === "pm" ? "bg-rose-600 text-white border-rose-700" : "bg-white text-rose-800 border-rose-200 hover:bg-rose-100"}`}
          data-testid="admin-dismissal-exc-period-pm">
          PM (End of Day)
        </button>
      </div>

      {/* Today / Range toggle */}
      <div className="flex gap-1.5 mb-3" role="tablist">
        <button type="button" onClick={() => setMode("today")}
          className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition ${mode === "today" ? "bg-rose-600 text-white border-rose-700" : "bg-white text-rose-800 border-rose-200 hover:bg-rose-100"}`}
          data-testid="admin-dismissal-exc-mode-today">
          Today only
        </button>
        <button type="button" onClick={() => setMode("range")}
          className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition ${mode === "range" ? "bg-rose-600 text-white border-rose-700" : "bg-white text-rose-800 border-rose-200 hover:bg-rose-100"}`}
          data-testid="admin-dismissal-exc-mode-range">
          Date range
        </button>
      </div>

      {mode === "range" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
          <label className="block">
            <span className="text-[10px] uppercase tracking-wide text-rose-700 font-semibold">Start date</span>
            <input type="date" value={startDate} min={today} onChange={(e) => setStartDate(e.target.value)}
              className="w-full mt-0.5 px-2 py-1.5 text-sm border rounded-lg bg-white" data-testid="admin-dismissal-exc-start" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wide text-rose-700 font-semibold">End date</span>
            <input type="date" value={endDate} min={startDate || today} onChange={(e) => setEndDate(e.target.value)}
              className="w-full mt-0.5 px-2 py-1.5 text-sm border rounded-lg bg-white" data-testid="admin-dismissal-exc-end" />
          </label>
          {rangeDayCount > 0 && (
            <p className="sm:col-span-2 text-[11px] text-rose-700 -mt-1">
              {rangeDayCount} calendar day{rangeDayCount === 1 ? "" : "s"} — weekends and non-camp days are skipped automatically.
            </p>
          )}
        </div>
      )}

      <div className={`grid gap-1.5 mb-2 ${period === "am" ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-2 sm:grid-cols-5"}`}>
        {opts.map(opt => (
          <button
            key={opt}
            type="button"
            onClick={() => setLocation(opt === location ? "" : opt)}
            className={`px-2 py-1.5 text-xs font-semibold rounded-lg border transition ${location === opt ? "bg-rose-600 text-white border-rose-700" : "bg-white text-rose-800 border-rose-200 hover:bg-rose-100"}`}
            data-testid={`admin-dismissal-exc-${opt.replace(/\s+/g, '-').toLowerCase()}`}
          >
            {opt}
          </button>
        ))}
      </div>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Note (optional) — e.g., mom dropping off all week, picking up at 2:45"
        className="w-full min-h-[44px] p-2 text-sm border rounded-lg bg-white"
        data-testid="admin-dismissal-exc-note"
      />
      <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
        <div className="text-xs text-muted-foreground">
          {activeToday && activeToday.location
            ? <span><strong>Today ({period.toUpperCase()}):</strong> {activeToday.location}</span>
            : <span>No {period.toUpperCase()} exception today.</span>}
        </div>
        <div className="flex gap-2">
          {activeToday && (
            <Button size="sm" variant="outline" className="h-8 text-xs"
              onClick={() => save({ clearOnly: true })}
              disabled={saving} data-testid="admin-dismissal-exc-clear">
              Clear today
            </Button>
          )}
          <Button size="sm" className="bg-rose-600 hover:bg-rose-700 text-white h-8 text-xs"
            onClick={() => save()}
            disabled={saving || !location || (mode === "range" && (!startDate || !endDate))}
            data-testid="admin-dismissal-exc-save">
            <Save className="w-3 h-3 mr-1" />
            {saving ? "Saving…" : mode === "range" ? `Save ${rangeDayCount || ""} day${rangeDayCount === 1 ? "" : "s"}` : "Save Exception"}
          </Button>
        </div>
      </div>

      {/* Upcoming scheduled exceptions */}
      {upcoming.length > 0 && (
        <div className="mt-3 pt-3 border-t border-rose-200" data-testid="admin-dismissal-exc-upcoming">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-[10px] uppercase tracking-wide text-rose-700 font-semibold">Upcoming scheduled ({upcoming.length})</p>
            {upcoming.length > 1 && (
              <button
                type="button"
                onClick={deleteAllUpcoming}
                className="text-[10px] uppercase tracking-wide font-semibold text-rose-700 hover:text-rose-900 underline"
                data-testid="admin-dismissal-exc-delete-all"
              >
                Delete all
              </button>
            )}
          </div>
          <div className="space-y-1">
            {upcoming.map(u => (
              <div key={`${u.date}-${u.period || 'pm'}`} className="flex items-center justify-between gap-2 px-2 py-1 rounded-md bg-white border border-rose-200">
                <span className="text-[11px] font-semibold text-slate-700">{u.date}</span>
                <Badge variant="outline" className="text-[9px] border-slate-300">{(u.period || "pm").toUpperCase()}</Badge>
                <span className="text-[11px] text-rose-700">{u.location}</span>
                {u.note && <span className="text-[11px] text-muted-foreground italic truncate flex-1 text-right">{u.note}</span>}
                <button
                  type="button"
                  onClick={() => deleteUpcoming(u.date, u.period || "pm")}
                  className="shrink-0 p-1 rounded hover:bg-rose-100 text-rose-500 hover:text-rose-700 transition"
                  title={`Delete ${(u.period || "pm").toUpperCase()} exception on ${u.date}`}
                  data-testid={`admin-dismissal-exc-delete-${u.date}-${u.period || 'pm'}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}



function SecondaryEmailRow({ camperId, initialValue, primaryEmail }) {
  const token = localStorage.getItem("token");
  const [value, setValue] = useState(initialValue || "");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(initialValue || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const trimmed = (draft || "").trim();
    if (trimmed && !trimmed.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (trimmed && trimmed.toLowerCase() === (primaryEmail || "").toLowerCase()) {
      toast.error("Secondary email cannot match the primary email");
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/campers/${camperId}/secondary-email`,
        { secondary_email: trimmed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setValue(trimmed);
      setEditing(false);
      toast.success(trimmed ? "Secondary email saved — both parents will be CC'd" : "Secondary email cleared");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save secondary email");
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-2 p-2.5 sm:p-3 bg-purple-50 rounded-lg border border-purple-200" data-testid="secondary-email-edit-row">
        <Mail className="w-4 h-4 text-purple-600 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs text-muted-foreground mb-1">Parent 2 Email (also receives outbound messages)</p>
          <Input
            type="email"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="parent2@example.com"
            className="h-8 text-sm bg-white"
            data-testid="secondary-email-input"
            autoFocus
          />
        </div>
        <Button size="sm" onClick={save} disabled={saving} className="bg-purple-600 hover:bg-purple-700 h-8" data-testid="secondary-email-save-btn">
          <Save className="w-3 h-3 mr-1" />{saving ? "..." : "Save"}
        </Button>
        <Button size="sm" variant="outline" onClick={() => { setEditing(false); setDraft(value); }} className="h-8" data-testid="secondary-email-cancel-btn">
          <X className="w-3 h-3" />
        </Button>
      </div>
    );
  }

  if (value) {
    return (
      <div className="flex items-center gap-3 p-2.5 sm:p-3 bg-purple-50 rounded-lg border border-purple-200" data-testid="secondary-email-row">
        <Mail className="w-4 h-4 text-purple-600 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-xs text-purple-700 font-medium">Parent 2 Email <span className="text-muted-foreground font-normal">(also receives messages)</span></p>
          <p className="text-sm font-medium break-all">{value}</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => { setDraft(value); setEditing(true); }} className="h-8" data-testid="secondary-email-edit-btn">
          <Edit className="w-3 h-3" />
        </Button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="flex items-center gap-2 p-2.5 sm:p-3 bg-slate-50 hover:bg-purple-50 rounded-lg border border-dashed border-slate-300 hover:border-purple-300 transition w-full text-left"
      data-testid="secondary-email-add-btn"
    >
      <Plus className="w-4 h-4 text-slate-500 flex-shrink-0" />
      <span className="text-sm text-slate-600">Add Parent 2 Email (for divorced/co-parents)</span>
    </button>
  );
}


const DISMISSAL_LOCK_LOCATIONS = ["Main Tent", "Hockey Rink", "Auditorium"];

function PrimaryEmailRow({ camperId, initialValue, isOverride }) {
  const [value, setValue] = useState(initialValue || "");
  const [override, setOverride] = useState(!!isOverride);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(initialValue || "");
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem("token");

  const save = async () => {
    const trimmed = draft.trim();
    if (!trimmed || !trimmed.includes("@")) {
      toast.error("Enter a valid email address");
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/campers/${camperId}/primary-email`,
        { email: trimmed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setValue(trimmed);
      setOverride(true);
      setEditing(false);
      toast.success("Primary email saved — syncs will never overwrite it");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save email");
    } finally {
      setSaving(false);
    }
  };

  const clearOverride = async () => {
    if (!window.confirm("Remove the manual override? The next CampMinder sync will restore whatever email CampMinder has.")) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/campers/${camperId}/primary-email`,
        { email: "" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOverride(false);
      toast.success("Override removed — next sync will restore CampMinder's email");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to clear override");
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-2 p-2.5 sm:p-3 bg-blue-50 rounded-lg border border-blue-200" data-testid="primary-email-edit-row">
        <Mail className="w-4 h-4 text-blue-600 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs text-muted-foreground mb-1">Primary Parent Email</p>
          <Input
            type="email"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="parent@example.com"
            className="h-8 text-sm bg-white"
            data-testid="primary-email-input"
            autoFocus
          />
        </div>
        <Button size="sm" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 h-8" data-testid="primary-email-save-btn">
          <Save className="w-3 h-3 mr-1" />{saving ? "..." : "Save"}
        </Button>
        <Button size="sm" variant="outline" onClick={() => { setEditing(false); setDraft(value); }} className="h-8" data-testid="primary-email-cancel-btn">
          <X className="w-3 h-3" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 p-2.5 sm:p-3 bg-blue-50 rounded-lg border border-blue-100" data-testid="primary-email-row">
      <Mail className="w-4 h-4 text-blue-600 flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-xs text-muted-foreground">
          Email
          {override && (
            <span className="ml-1.5 text-[10px] font-bold text-blue-700 bg-blue-100 border border-blue-300 rounded px-1 py-0.5" data-testid="primary-email-override-badge">
              MANUAL — sync won't overwrite
            </span>
          )}
        </p>
        <p className="text-sm font-medium break-all">{value || <span className="text-slate-400 italic">none on file</span>}</p>
      </div>
      {override && (
        <Button size="sm" variant="ghost" onClick={clearOverride} disabled={saving} className="h-8 text-slate-500 hover:text-red-600 px-2" title="Remove manual override (next sync restores CampMinder value)" data-testid="primary-email-clear-override-btn">
          <X className="w-3 h-3" />
        </Button>
      )}
      <Button size="sm" variant="outline" onClick={() => { setDraft(value); setEditing(true); }} className="h-8" data-testid="primary-email-edit-btn">
        <Edit className="w-3 h-3" />
      </Button>
    </div>
  );
}

function DismissalLockRow({ camperId, initialValue, camperName }) {
  const [value, setValue] = useState(initialValue || "");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(initialValue || "");
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem("token");

  const save = async (newLoc) => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/campers/${camperId}/dismissal-lock`,
        { location: newLoc || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setValue(newLoc || "");
      setEditing(false);
      toast.success(newLoc
        ? `${camperName || "Camper"} pinned to ${newLoc} — sibling routing ignored`
        : "Dismissal lock cleared — camper follows sibling routing again");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save dismissal lock");
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="p-2.5 sm:p-3 bg-rose-50 rounded-lg border border-rose-200" data-testid="dismissal-lock-edit-row">
        <p className="text-xs text-rose-700 font-medium mb-2 flex items-center gap-1">
          <MapPin className="w-3.5 h-3.5" />
          Permanent Dismissal Lock
        </p>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {DISMISSAL_LOCK_LOCATIONS.map(loc => (
            <button
              key={loc}
              type="button"
              onClick={() => setDraft(loc)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                draft === loc
                  ? "bg-rose-600 text-white border-rose-600"
                  : "bg-white text-rose-700 border-rose-300 hover:bg-rose-100"
              }`}
              data-testid={`dismissal-lock-option-${loc.replace(/\s+/g, "-")}`}
            >
              {loc}
            </button>
          ))}
        </div>
        <p className="text-[11px] text-rose-700/80 mb-2">
          Camper stays here permanently regardless of sibling location.
        </p>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => save(draft)} disabled={saving || !draft} className="bg-rose-600 hover:bg-rose-700 h-8" data-testid="dismissal-lock-save-btn">
            <Save className="w-3 h-3 mr-1" />{saving ? "..." : "Save"}
          </Button>
          {value && (
            <Button size="sm" variant="outline" onClick={() => save("")} disabled={saving} className="h-8 text-rose-700 border-rose-300" data-testid="dismissal-lock-clear-btn">
              Clear lock
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => { setEditing(false); setDraft(value); }} className="h-8" data-testid="dismissal-lock-cancel-btn">
            <X className="w-3 h-3" />
          </Button>
        </div>
      </div>
    );
  }

  if (value) {
    return (
      <div className="flex items-center gap-3 p-2.5 sm:p-3 bg-rose-50 rounded-lg border border-rose-200" data-testid="dismissal-lock-row">
        <MapPin className="w-4 h-4 text-rose-600 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-xs text-rose-700 font-medium">Dismissal Lock <span className="text-muted-foreground font-normal">(overrides sibling routing)</span></p>
          <p className="text-sm font-semibold text-rose-900">{value}</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => { setDraft(value); setEditing(true); }} className="h-8" data-testid="dismissal-lock-edit-btn">
          <Edit className="w-3 h-3" />
        </Button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => { setDraft(""); setEditing(true); }}
      className="flex items-center gap-2 p-2.5 sm:p-3 bg-slate-50 hover:bg-rose-50 rounded-lg border border-dashed border-slate-300 hover:border-rose-300 transition w-full text-left"
      data-testid="dismissal-lock-add-btn"
    >
      <Plus className="w-4 h-4 text-slate-500 flex-shrink-0" />
      <span className="text-sm text-slate-600">Pin dismissal location (opt out of sibling routing)</span>
    </button>
  );
}


function AdminCamperDetail({ camper, onClose, onDownloadPDF }) {
  const initials = `${camper.first_name?.[0] || ''}${camper.last_name?.[0] || ''}`.toUpperCase();
  const medicalData = camper.medical_data || {};
  const parentInfo = camper.parent_info || {};

  return (
    <>
      <DialogHeader className="p-4 sm:p-6 pb-0">
        <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
          <div className="flex items-start gap-3 sm:gap-4 w-full sm:w-auto">
            <CamperPhoto
              camperId={camper.id}
              firstName={camper.first_name}
              lastName={camper.last_name}
              size={80}
              className="flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <DialogTitle className="font-heading text-xl sm:text-2xl text-primary-700">
                {camper.first_name} {camper.last_name}
              </DialogTitle>
              <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mt-1.5 sm:mt-2">
                {camper.group_code && (
                  <Badge variant="secondary" className="bg-primary-50 text-primary-700 text-xs">
                    <Users className="w-3 h-3 mr-1" />{camper.cit_subgroup || camper.group_name || camper.group_code}
                  </Badge>
                )}
                {camper.age && (
                  <Badge variant="outline" className="text-xs"><Calendar className="w-3 h-3 mr-1" />Age {Math.floor(camper.age)}</Badge>
                )}
                {camper.gender && (
                  <Badge variant="outline" className="text-xs"><User className="w-3 h-3 mr-1" />{camper.gender}</Badge>
                )}
                {camper.date_of_birth && (
                  <Badge variant="outline" className="text-xs"><Calendar className="w-3 h-3 mr-1" />{new Date(camper.date_of_birth + 'T00:00:00').toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric' })}</Badge>
                )}
                {camper.years_at_camp != null && (
                  <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200">{camper.years_at_camp} {camper.years_at_camp === 1 ? 'Year' : 'Years'} at Camp</Badge>
                )}
                {camper.previous_group && (
                  <Badge variant="outline" className="text-xs bg-slate-50 text-slate-700 border-slate-200">Prev Group: {camper.previous_group}</Badge>
                )}
              </div>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => onDownloadPDF(camper)} className="flex-shrink-0 w-full sm:w-auto" data-testid="admin-download-pdf-btn">
            <Download className="w-4 h-4 mr-2" />Download PDF
          </Button>
        </div>
      </DialogHeader>
      <ScrollArea className="max-h-[55vh] sm:max-h-[60vh]">
        <div className="p-4 sm:p-6 space-y-5 sm:space-y-6">
          {(parentInfo.name || parentInfo.phone || parentInfo.email) && (
            <section>
              <h3 className="font-heading font-semibold text-base sm:text-lg flex items-center gap-2 mb-3 sm:mb-4 text-primary-700">
                <User className="w-4 h-4 sm:w-5 sm:h-5" />Parent 1
              </h3>
              <div className="grid gap-2 sm:gap-3">
                {parentInfo.name && (
                  <div className="flex items-center gap-3 p-2.5 sm:p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <User className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <div className="min-w-0"><p className="text-xs text-muted-foreground">Name</p><p className="text-sm font-medium truncate">{parentInfo.name}</p></div>
                  </div>
                )}
                {parentInfo.phone && (
                  <div className="flex items-center gap-3 p-2.5 sm:p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <Phone className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <div className="min-w-0"><p className="text-xs text-muted-foreground">Phone</p><p className="text-sm font-medium">{formatPhone(parentInfo.phone)}</p></div>
                  </div>
                )}
                <PrimaryEmailRow camperId={camper.id} initialValue={parentInfo.email || ""} isOverride={!!parentInfo.email_override} />
                {parentInfo.address && (
                  <div className="flex items-center gap-3 p-2.5 sm:p-3 bg-blue-50 rounded-lg border border-blue-100" data-testid="parent1-address-row">
                    <MapPin className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <div className="min-w-0"><p className="text-xs text-muted-foreground">Address</p><p className="text-sm font-medium">{parentInfo.address}</p></div>
                  </div>
                )}
                <DismissalLockRow camperId={camper.id} initialValue={camper.dismissal_location_lock || ""} camperName={`${camper.first_name || ""} ${camper.last_name || ""}`.trim()} />
              </div>
            </section>
          )}

          {(parentInfo.secondary_name || parentInfo.secondary_phone || parentInfo.secondary_email || parentInfo.secondary_address) && (
            <section data-testid="parent2-section">
              <h3 className="font-heading font-semibold text-base sm:text-lg flex items-center gap-2 mb-3 sm:mb-4 text-primary-700">
                <User className="w-4 h-4 sm:w-5 sm:h-5" />Parent 2
              </h3>
              <div className="grid gap-2 sm:gap-3">
                {parentInfo.secondary_name && (
                  <div className="flex items-center gap-3 p-2.5 sm:p-3 bg-violet-50 rounded-lg border border-violet-100">
                    <User className="w-4 h-4 text-violet-600 flex-shrink-0" />
                    <div className="min-w-0"><p className="text-xs text-muted-foreground">Name</p><p className="text-sm font-medium truncate">{parentInfo.secondary_name}</p></div>
                  </div>
                )}
                {parentInfo.secondary_phone && (
                  <div className="flex items-center gap-3 p-2.5 sm:p-3 bg-violet-50 rounded-lg border border-violet-100">
                    <Phone className="w-4 h-4 text-violet-600 flex-shrink-0" />
                    <div className="min-w-0"><p className="text-xs text-muted-foreground">Phone</p><p className="text-sm font-medium">{formatPhone(parentInfo.secondary_phone)}</p></div>
                  </div>
                )}
                <SecondaryEmailRow camperId={camper.id} initialValue={parentInfo.secondary_email || ""} primaryEmail={parentInfo.email || ""} />
                {parentInfo.secondary_address && (
                  <div className="flex items-center gap-3 p-2.5 sm:p-3 bg-violet-50 rounded-lg border border-violet-100" data-testid="parent2-address-row">
                    <MapPin className="w-4 h-4 text-violet-600 flex-shrink-0" />
                    <div className="min-w-0"><p className="text-xs text-muted-foreground">Address</p><p className="text-sm font-medium">{parentInfo.secondary_address}</p></div>
                  </div>
                )}
              </div>
            </section>
          )}
          {!(parentInfo.secondary_name || parentInfo.secondary_phone || parentInfo.secondary_email || parentInfo.secondary_address) && (parentInfo.name || parentInfo.email) && (
            <section>
              <div className="grid gap-2 sm:gap-3">
                <SecondaryEmailRow camperId={camper.id} initialValue={parentInfo.secondary_email || ""} primaryEmail={parentInfo.email || ""} />
              </div>
            </section>
          )}

          {camper.sessions && camper.sessions.length > 0 && (
            <section>
              <h3 className="font-heading font-semibold text-base sm:text-lg flex items-center gap-2 mb-3 text-primary-700">
                <CalendarDays className="w-4 h-4 sm:w-5 sm:h-5" />Program
              </h3>
              <div className="grid gap-2">
                {camper.sessions.map((s, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                    <span className="text-sm font-medium text-emerald-800">{s.name}</span>
                    <span className="text-xs text-emerald-600">
                      {new Date(s.start + 'T00:00:00').toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric' })} – {new Date(s.end + 'T00:00:00').toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Authorized Pickup + Travel/Additional Info (CampMinder custom fields) */}
          {(camper.authorized_pickup || camper.travel_additional_info) && (
            <section data-testid="admin-pickup-info">
              <h3 className="font-heading font-semibold text-base sm:text-lg flex items-center gap-2 mb-3 text-primary-700">
                <User className="w-4 h-4 sm:w-5 sm:h-5" />Pickup & Travel
              </h3>
              <div className="grid gap-2">
                {camper.authorized_pickup && (
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-0.5">Authorized Pickup</p>
                    <p className="text-sm text-amber-900 whitespace-pre-wrap">{camper.authorized_pickup}</p>
                  </div>
                )}
                {camper.travel_additional_info && (
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-0.5">Travel / Additional Info</p>
                    <p className="text-sm text-slate-900 whitespace-pre-wrap">{camper.travel_additional_info}</p>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Admin-only: Today's Dismissal Exception */}
          <AdminDismissalExceptionEditor camperId={camper.id} camperName={`${camper.first_name} ${camper.last_name}`} />

          {camper.is_flex && camper.flex_days && camper.flex_days.length > 0 && (
            <section>
              <h3 className="font-heading font-semibold text-base sm:text-lg flex items-center gap-2 mb-3 text-primary-700">
                <CalendarDays className="w-4 h-4 sm:w-5 sm:h-5" />Flex Schedule ({camper.flex_days.length} days)
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {camper.flex_days.map(day => {
                  const d = new Date(day + "T12:00:00");
                  const label = d.toLocaleDateString('en-US', { timeZone: 'America/New_York', weekday: 'short', month: 'short', day: 'numeric' });
                  return <Badge key={day} variant="secondary" className="text-xs">{label}</Badge>;
                })}
              </div>
            </section>
          )}

          {(camper.am_bus || camper.pm_bus || camper.am_method || camper.pm_method || camper.pm_weekly_today) && (
            <section data-testid="admin-camper-transport-section">
              <h3 className="font-heading font-semibold text-base sm:text-lg flex items-center gap-2 mb-3 text-teal-700">
                <Bus className="w-4 h-4 sm:w-5 sm:h-5" />Transportation
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {(camper.am_bus || camper.am_method) && (
                  <div className="p-3 bg-sky-50 rounded-lg border border-sky-200" data-testid="admin-am-transport">
                    <p className="text-xs font-semibold uppercase tracking-wider text-sky-700 mb-1">AM Drop-off</p>
                    {camper.am_bus && (<p className="text-base font-bold text-sky-900">{camper.am_bus}</p>)}
                    {camper.am_method && camper.am_method !== camper.am_bus && (
                      <p className={`${camper.am_bus ? "text-xs text-sky-700 mt-0.5" : "text-base font-bold text-sky-900"}`}>{camper.am_method}</p>
                    )}
                  </div>
                )}
                {(camper.pm_bus || camper.pm_method || camper.pm_weekly_today) && (
                  <div className="p-3 bg-orange-50 rounded-lg border border-orange-200" data-testid="admin-pm-transport">
                    <p className="text-xs font-semibold uppercase tracking-wider text-orange-700 mb-1">PM Pickup</p>
                    {camper.pm_weekly_today ? (
                      <>
                        <p className="text-base font-bold text-orange-900">{camper.pm_weekly_location}</p>
                        {camper.pm_weekly_remove && (<p className="text-xs text-orange-700 mt-0.5">Not on a pickup list today</p>)}
                        <span className="inline-block mt-1 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-200 text-emerald-800 border border-emerald-300">Weekly schedule</span>
                      </>
                    ) : (
                      <>
                        {camper.pm_bus && (<p className="text-base font-bold text-orange-900">{camper.pm_bus}</p>)}
                        {camper.pm_method && camper.pm_method !== camper.pm_bus && (
                          <p className={`${camper.pm_bus ? "text-xs text-orange-700 mt-0.5" : "text-base font-bold text-orange-900"}`}>{camper.pm_method}</p>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </section>
          )}

          {(() => {
            const sectionOrder = ["Medical", "Family", "Behavioral", "Additional Info", "Skippers Only", "Swim"];
            const sectionIcons = { "Medical": Stethoscope, "Family": Heart, "Behavioral": Brain, "Additional Info": Star, "Skippers Only": FileText, "Swim": Waves };
            const sectionColors = { "Medical": "text-red-700", "Family": "text-blue-700", "Behavioral": "text-purple-700", "Additional Info": "text-green-700", "Skippers Only": "text-orange-700", "Swim": "text-cyan-700" };
            const hasSections = typeof Object.values(medicalData)[0] === 'object';

            if (hasSections) {
              return sectionOrder.map(section => {
                let data = medicalData[section];
                // Read swim color DIRECTLY from camper.swim_color (the same
                // field the daily roster uses) so this card can never get out
                // of sync. Backfill the nested entry into the rendered map.
                if (section === "Swim") {
                  data = { ...(data || {}) };
                  if (camper.swim_color) data["Swim Color"] = camper.swim_color;
                  if (camper.swim_level) data["Swim Level"] = camper.swim_level;
                }
                if (!data || Object.keys(data).length === 0) return null;
                if (section === "Skippers Only") {
                  const code = (camper.group_code || "").toUpperCase();
                  const num = parseInt(code.replace(/\D/g, ''), 10);
                  if (!code.startsWith('N') || num < 2 || num > 16) return null;
                }
                const Icon = sectionIcons[section] || FileText;
                const color = sectionColors[section] || "text-primary-700";
                return (
                  <section key={section}>
                    <h3 className={`font-heading font-semibold text-base sm:text-lg flex items-center gap-2 mb-3 ${color}`}>
                      <Icon className="w-4 h-4 sm:w-5 sm:h-5" />{section}
                    </h3>
                    <div className="grid gap-2 sm:gap-3">
                      {Object.entries(data)
                        .sort((a, b) => {
                          if (section === "Transportation") {
                            const order = ["Bus# AM", "Trans-AM Drop Off Method", "Bus# PM", "Trans-PM Dismissal Method"];
                            return (order.indexOf(a[0]) === -1 ? 99 : order.indexOf(a[0])) - (order.indexOf(b[0]) === -1 ? 99 : order.indexOf(b[0]));
                          }
                          return 0;
                        })
                        .map(([key, value]) => {
                          const isAlert = key.toLowerCase().includes('allerg') || key.toLowerCase().includes('meds') || key.toLowerCase().includes('reaction');
                          return (
                            <div key={key} className={`p-3 sm:p-4 rounded-lg border ${isAlert && section === "Medical" ? 'bg-red-50 border-red-200' : 'bg-muted/30'}`}>
                              <p className={`text-xs font-medium uppercase tracking-wider mb-1 ${isAlert && section === "Medical" ? 'text-red-600' : 'text-muted-foreground'}`}>
                                {isAlert && section === "Medical" && <AlertTriangle className="w-3 h-3 inline mr-1" />}{key}
                              </p>
                              <p className={`text-sm ${isAlert && section === "Medical" ? 'text-red-800 font-medium' : ''}`}>{value || "—"}</p>
                            </div>
                          );
                        })}
                    </div>
                  </section>
                );
              });
            } else if (Object.keys(medicalData).length > 0) {
              return (
                <section>
                  <h3 className="font-heading font-semibold text-base sm:text-lg flex items-center gap-2 mb-3 text-primary-700">
                    <Stethoscope className="w-4 h-4 sm:w-5 sm:h-5" />Medical Information
                  </h3>
                  <div className="grid gap-2 sm:gap-3">
                    {Object.entries(medicalData).map(([key, value]) => (
                      <div key={key} className="p-3 rounded-lg border bg-muted/30">
                        <p className="text-xs font-medium uppercase tracking-wider mb-1 text-muted-foreground">{key}</p>
                        <p className="text-sm">{value || "—"}</p>
                      </div>
                    ))}
                  </div>
                </section>
              );
            } else {
              return (
                <section>
                  <h3 className="font-heading font-semibold text-base sm:text-lg flex items-center gap-2 mb-3 text-primary-700">
                    <Stethoscope className="w-4 h-4 sm:w-5 sm:h-5" />Medical Information
                  </h3>
                  <div className="text-center py-6 bg-muted/30 rounded-lg">
                    <Stethoscope className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No medical data on file</p>
                  </div>
                </section>
              );
            }
          })()}

          <AdminCITInfoBlock camper={camper} />

          <CamperNotesEditor camperId={camper.id} />

          <CamperIncidentHistory camperId={camper.id} />

          <section className="text-xs text-muted-foreground border-t pt-4">
            <p>Person ID: {camper.person_id}</p>
            {camper.date_of_birth && <p>DOB: {camper.date_of_birth}</p>}
            {camper.last_synced && <p>Last Synced: {new Date(camper.last_synced).toLocaleString("en-US", { timeZone: "America/New_York" })}</p>}
          </section>
        </div>
      </ScrollArea>
    </>
  );
}

const ADMIN_CIT_TRIP_DATES_BY_NUM = {
  1: "2026-07-01",
  2: "2026-07-07",
  3: "2026-07-13",
  4: "2026-07-23",
  5: "2026-07-27",
  6: "2026-08-04",
  7: "2026-08-11",
  8: "2026-08-17",
};

function formatAdminCitTripDate(tripName) {
  const m = /^Trip\s+(\d+)/i.exec(tripName || "");
  if (!m) return "";
  const iso = ADMIN_CIT_TRIP_DATES_BY_NUM[parseInt(m[1], 10)];
  if (!iso) return "";
  try {
    return new Date(iso + "T12:00:00").toLocaleDateString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

function AdminCITInfoBlock({ camper }) {  const [open, setOpen] = useState(false);
  const gc = (camper.group_code || "").toUpperCase();
  if (!gc.startsWith("CIT")) return null;

  const ranks = [1, 2, 3];
  const choicePairs = ranks
    .map(r => ({
      rank: r,
      program: camper[`cit_program_${r}`] || "",
      choice: camper[`cit_choice_${r}`] || "",
    }))
    .filter(p => p.program || p.choice);

  const trips = camper.cit_trips || {};
  const tripDates = Array.isArray(camper.cit_trip_dates) ? camper.cit_trip_dates : [];
  const enrolledTrips = Object.entries(trips).filter(([, v]) => v && String(v).trim().length > 0);

  const paperwork = camper.cit_parent_signature || "";
  const lastYear = camper.cit_last_year_position || "";
  const gossip = camper.cit_gossip_signed;

  const hasAny = choicePairs.length > 0 || enrolledTrips.length > 0 || tripDates.length > 0 || paperwork || lastYear || gossip != null;
  if (!hasAny) return null;

  return (
    <section data-testid="admin-cit-info-section">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-fuchsia-200 bg-fuchsia-50 hover:bg-fuchsia-100 transition-colors"
        data-testid="admin-cit-info-toggle"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-fuchsia-800">
          <Star className="w-4 h-4" />
          CIT Info
          <Badge variant="outline" className="text-[10px] border-fuchsia-300 text-fuchsia-700 bg-white">
            {camper.cit_subgroup || gc}
          </Badge>
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-fuchsia-700" /> : <ChevronDown className="w-4 h-4 text-fuchsia-700" />}
      </button>
      {open && (
        <div className="mt-2 grid gap-3 p-3 rounded-lg border border-fuchsia-100 bg-white">
          {choicePairs.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-fuchsia-700 mb-1.5">Program / Choice Preferences</p>
              <div className="grid gap-1.5">
                {choicePairs.map(p => (
                  <div key={p.rank} className="flex items-center gap-2 text-sm">
                    <Badge variant="secondary" className="text-[10px] bg-fuchsia-100 text-fuchsia-800">#{p.rank}</Badge>
                    <span className="font-medium text-slate-800">{p.program || "—"}</span>
                    {p.choice && <span className="text-slate-500">·</span>}
                    {p.choice && <span className="text-slate-700">{p.choice}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {enrolledTrips.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-fuchsia-700 mb-1.5">CIT Trips ({enrolledTrips.length})</p>
              <div className="flex flex-wrap gap-1.5">
                {enrolledTrips.map(([name, dateVal]) => {
                  const d = formatAdminCitTripDate(name) || (typeof dateVal === "string" ? dateVal : "");
                  return (
                    <Badge key={name} variant="outline" className="text-xs border-fuchsia-300 text-fuchsia-700 bg-fuchsia-50">
                      {name}{d ? ` · ${d}` : ""}
                    </Badge>
                  );
                })}
              </div>
            </div>
          )}
          {tripDates.length > 0 && enrolledTrips.length === 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-fuchsia-700 mb-1.5">CIT Trip Dates</p>
              <div className="flex flex-wrap gap-1.5">
                {tripDates.map(d => (
                  <Badge key={d} variant="outline" className="text-xs border-fuchsia-300 text-fuchsia-700 bg-fuchsia-50">{d}</Badge>
                ))}
              </div>
            </div>
          )}
          <div className="grid gap-1.5 text-xs">
            {paperwork ? (
              <div className="flex items-start gap-2">
                <CheckCircle className="w-3.5 h-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />
                <span><span className="font-semibold text-slate-700">Paperwork:</span> <span className="text-slate-700">{paperwork}</span></span>
              </div>
            ) : (
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
                <span className="text-amber-800 font-medium">Parent paperwork not yet submitted</span>
              </div>
            )}
            {lastYear && (
              <div className="text-slate-600"><span className="font-semibold">Last year:</span> {lastYear}</div>
            )}
            {gossip != null && (
              <div className="text-slate-600">
                <span className="font-semibold">Gossip Agreement:</span>{" "}
                {gossip ? <span className="text-emerald-700 font-medium">Signed</span> : <span className="text-amber-700 font-medium">Not signed</span>}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

const QH_DAYS = [
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" },
];

function QuietHoursPanel({ token }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/quiet-hours`, { headers });
      setCfg(res.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (!cfg) {
    return (
      <div className="rounded-xl border bg-slate-50 p-3 text-xs text-muted-foreground">
        Loading Quiet Hours…
      </div>
    );
  }

  const persist = async (next) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/quiet-hours`, next, { headers });
      setCfg(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save Quiet Hours");
    }
    setSaving(false);
  };

  const toggleEnabled = () => {
    const next = { ...cfg, enabled: !cfg.enabled };
    persist(next);
  };

  const updateDay = (dayKey, patch) => {
    const newDays = { ...cfg.days, [dayKey]: { ...cfg.days[dayKey], ...patch } };
    persist({ ...cfg, days: newDays });
  };

  return (
    <div
      className={`rounded-xl border p-4 ${
        cfg.active_now ? "bg-amber-50 border-amber-300" : cfg.enabled ? "bg-slate-50 border-slate-300" : "bg-white border-slate-200"
      }`}
      data-testid="quiet-hours-panel"
    >
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="flex items-center gap-2 mr-auto">
          {cfg.active_now ? (
            <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-800 bg-amber-200 rounded-full px-2 py-0.5" data-testid="quiet-hours-active-badge">
              <BellOff className="w-3 h-3" /> ACTIVE — staff alerts muted right now
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700">
              <Bell className="w-3.5 h-3.5" /> Quiet Hours — per day schedule
            </span>
          )}
          <span className="text-[11px] text-muted-foreground">
            Suppresses outbound SMS/email AND inbound→counselor forwards during the window. In-app inbox still receives.
          </span>
        </div>
        <Button
          size="sm"
          variant={cfg.enabled ? "default" : "outline"}
          onClick={toggleEnabled}
          disabled={saving}
          className={cfg.enabled ? "bg-amber-600 hover:bg-amber-700 text-white" : ""}
          data-testid="quiet-hours-toggle"
        >
          {cfg.enabled ? "ON" : "OFF"}
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7 gap-2">
        {QH_DAYS.map(({ key, label }) => {
          const d = cfg.days?.[key] || { enabled: true, all_day: false, start: "21:00", end: "07:00" };
          return (
            <div
              key={key}
              className={`p-2 rounded-lg border text-xs ${
                d.enabled
                  ? d.all_day
                    ? "bg-rose-50 border-rose-300"
                    : "bg-white border-slate-300"
                  : "bg-slate-100 border-slate-200 opacity-60"
              }`}
              data-testid={`quiet-hours-day-${key}`}
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className="font-bold text-slate-800">{label}</span>
                <label className="inline-flex items-center gap-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!d.enabled}
                    onChange={(e) => updateDay(key, { enabled: e.target.checked })}
                    className="w-3.5 h-3.5"
                    data-testid={`quiet-hours-${key}-enabled`}
                  />
                  <span className="text-[10px] text-muted-foreground">on</span>
                </label>
              </div>
              <label className="flex items-center gap-1.5 mb-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!d.all_day}
                  onChange={(e) => updateDay(key, { all_day: e.target.checked })}
                  disabled={!d.enabled}
                  className="w-3.5 h-3.5"
                  data-testid={`quiet-hours-${key}-all-day`}
                />
                <span className="text-[11px]">All day quiet</span>
              </label>
              <div className={`grid grid-cols-2 gap-1 ${(!d.enabled || d.all_day) ? "opacity-40 pointer-events-none" : ""}`}>
                <label className="flex flex-col gap-0.5">
                  <span className="text-[10px] text-muted-foreground">Start</span>
                  <input
                    type="time"
                    value={d.start}
                    onChange={(e) => setCfg({ ...cfg, days: { ...cfg.days, [key]: { ...d, start: e.target.value } } })}
                    onBlur={() => persist(cfg)}
                    className="h-7 px-1.5 border rounded text-[11px]"
                    data-testid={`quiet-hours-${key}-start`}
                  />
                </label>
                <label className="flex flex-col gap-0.5">
                  <span className="text-[10px] text-muted-foreground">End</span>
                  <input
                    type="time"
                    value={d.end}
                    onChange={(e) => setCfg({ ...cfg, days: { ...cfg.days, [key]: { ...d, end: e.target.value } } })}
                    onBlur={() => persist(cfg)}
                    className="h-7 px-1.5 border rounded text-[11px]"
                    data-testid={`quiet-hours-${key}-end`}
                  />
                </label>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function SeasonManagementPanel({ token }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/season/state`, { headers });
      setState(res.data);
    } catch (e) {
      console.error(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);

  const activate = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/season/activate`, {}, { headers });
      setState(res.data);
      toast.success(`Season ${res.data.current_year} activated — all features live`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to activate season");
    }
    setBusy(false);
  };

  const switchSeason = async () => {
    if (confirmText !== "SWITCH") {
      toast.error('Please type "SWITCH" exactly to confirm');
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/season/switch`, { confirm: "SWITCH" }, { headers });
      toast.success(`Season ended. Archived ${res.data.from_year}, ready for ${res.data.to_year}.`);
      setShowConfirm(false);
      setConfirmText("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Switch failed");
    }
    setBusy(false);
  };

  if (!state) {
    return <div className="rounded-xl border bg-slate-50 p-3 text-xs text-muted-foreground">Loading Season State…</div>;
  }

  const active = !!state.active;

  return (
    <div
      className={`rounded-xl border p-4 ${active ? "bg-emerald-50 border-emerald-300" : "bg-slate-100 border-slate-400"}`}
      data-testid="season-management-panel"
    >
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="flex items-center gap-2 mr-auto">
          {active ? (
            <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-800 bg-emerald-200 rounded-full px-2.5 py-0.5" data-testid="season-active-badge">
              <CheckCircle2 className="w-3 h-3" /> Season {state.current_year} — ACTIVE
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs font-bold text-slate-800 bg-slate-300 rounded-full px-2.5 py-0.5" data-testid="season-inactive-badge">
              <Moon className="w-3 h-3" /> OFF-SEASON — preparing for {state.current_year}
            </span>
          )}
          <span className="text-[11px] text-muted-foreground">
            Master switch. When OFF: outbound messaging, CampMinder sync, and counselor-SMS forwards are paused. Archived reports (incident reports, buddy sheets, trip forms, evaluations) remain fully accessible.
          </span>
        </div>
        {active ? (
          showConfirm ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                placeholder='Type "SWITCH"'
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className="h-9 px-2 border-2 border-rose-400 rounded-md text-xs font-mono"
                data-testid="season-switch-confirm-input"
              />
              <Button size="sm" variant="destructive" onClick={switchSeason} disabled={busy || confirmText !== "SWITCH"} data-testid="season-switch-confirm-btn">
                {busy ? "Archiving..." : "End Season Now"}
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setShowConfirm(false); setConfirmText(""); }} disabled={busy} data-testid="season-switch-cancel-btn">Cancel</Button>
            </div>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setShowConfirm(true)} className="border-rose-400 text-rose-700 hover:bg-rose-50" data-testid="season-switch-btn">
              <Moon className="w-3.5 h-3.5 mr-1" /> Switch to New Season
            </Button>
          )
        ) : (
          <Button size="sm" onClick={activate} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="season-activate-btn">
            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> {busy ? "Activating..." : `Activate ${state.current_year} Season`}
          </Button>
        )}
      </div>
      {!active && (
        <div className="text-[11px] text-slate-700 bg-white/70 rounded-md p-2 border border-slate-200">
          <strong>Off-season checklist before activating:</strong> Sync new campers from CampMinder, re-upload counselor contacts CSV, set new Head Counselor passwords, re-assign Division Notification supervisors/assistants.
        </div>
      )}
      {state.last_archive_summary && active === false && (
        <details className="mt-2 text-[11px] text-slate-600">
          <summary className="cursor-pointer font-semibold">Last archive summary</summary>
          <pre className="bg-white/60 p-2 rounded border border-slate-200 mt-1 overflow-auto max-h-40">{JSON.stringify(state.last_archive_summary, null, 2)}</pre>
        </details>
      )}
    </div>
  );
}


function CamperIncidentHistory({ camperId }) {
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/campers/${camperId}/incident-reports`,
          { headers }
        );
        if (!cancelled) setReports(res.data?.reports || []);
      } catch (e) {
        if (!cancelled) setReports([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [camperId]);

  // Group by year for clearer year-to-year recall after season switches
  const byYear = reports.reduce((acc, r) => {
    const y = r.camp_year || (r.date ? new Date(r.date).getFullYear() : "Undated");
    (acc[y] = acc[y] || []).push(r);
    return acc;
  }, {});
  const years = Object.keys(byYear).sort((a, b) => Number(b) - Number(a));
  const apiUrl = process.env.REACT_APP_BACKEND_URL;

  return (
    <section className="border-t pt-4" data-testid="camper-incident-history-section">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-2 text-sm font-semibold text-rose-900 hover:text-rose-700"
        data-testid="camper-incident-history-toggle"
      >
        <span className="flex items-center gap-1.5">
          <AlertTriangle className="w-4 h-4 text-rose-600" />
          Incident History
          <Badge variant="secondary" className={`ml-1 h-4 px-1.5 text-[10px] ${reports.length ? "bg-rose-100 text-rose-800" : "bg-slate-200 text-slate-700"}`}>
            {loading ? "…" : reports.length}
          </Badge>
        </span>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {open && (
        <div className="mt-2 space-y-3 max-h-[360px] overflow-y-auto pr-1" data-testid="camper-incident-history-list">
          {loading ? (
            <p className="text-xs text-muted-foreground">Loading…</p>
          ) : reports.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">No incident reports filed for this camper.</p>
          ) : (
            years.map((y) => (
              <div key={y}>
                <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1">{y} ({byYear[y].length})</p>
                <div className="space-y-1.5">
                  {byYear[y].map((r) => {
                    const isGreatDay = !!r.great_day;
                    return (
                      <div
                        key={r.id}
                        className={`p-2 rounded-md border ${isGreatDay ? "border-emerald-200 bg-emerald-50/60" : "border-rose-200 bg-rose-50/60"}`}
                        data-testid={`camper-incident-row-${r.id}`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                              <Badge variant="outline" className={`text-[10px] ${isGreatDay ? "border-emerald-400 text-emerald-700" : "border-rose-400 text-rose-700"}`}>
                                {r.date || "—"}
                              </Badge>
                              <span className="text-[11px] font-semibold text-slate-800">
                                {isGreatDay ? "🌟 Great Day" : (r.incident_type || "Incident")}
                              </span>
                              {r.followup === "yes" && (
                                <Badge className="text-[10px] bg-amber-200 text-amber-900">Follow-up</Badge>
                              )}
                            </div>
                            <p className="text-[11px] text-slate-600 truncate">
                              {(r.description || r.great_day_notes || "").slice(0, 120) || <em className="text-slate-400">No description</em>}
                            </p>
                            <p className="text-[10px] text-slate-500 mt-0.5">
                              Reporter: {r.counselor || r.specialty || r.submitter_type || "—"}
                            </p>
                          </div>
                          <a
                            href={`${apiUrl}/api/incident-reports/${r.id}/pdf`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="shrink-0 inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold bg-white border border-slate-300 rounded hover:bg-slate-50"
                            data-testid={`camper-incident-pdf-${r.id}`}
                          >
                            <FileText className="w-3 h-3" /> PDF
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </section>
  );
}




function CamperNotesEditor({ camperId }) {
  // QuietHoursPanel defined just above
  const [notes, setNotes] = useState("");
  const [draft, setDraft] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);
  const [legacyNotes, setLegacyNotes] = useState([]);
  const [legacyOpen, setLegacyOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/campers/${camperId}/notes`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!alive) return;
        setNotes(res.data?.notes || "");
        setDraft(res.data?.notes || "");
        setUpdatedAt(res.data?.updated_at || null);
        setUpdatedBy(res.data?.updated_by || null);
        setLegacyNotes(Array.isArray(res.data?.legacy_notes) ? res.data.legacy_notes : []);
      } catch {
        if (alive) toast.error("Failed to load notes");
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [camperId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/campers/${camperId}/notes`, { notes: draft }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotes(res.data?.notes || "");
      setUpdatedAt(res.data?.updated_at || null);
      setUpdatedBy(res.data?.updated_by || null);
      toast.success("Notes saved");
    } catch {
      toast.error("Failed to save notes");
    } finally {
      setSaving(false);
    }
  };

  const dirty = draft !== notes;
  const updatedDisplay = updatedAt
    ? new Date(updatedAt).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })
    : null;

  return (
    <section data-testid="camper-notes-section">
      <h3 className="font-heading font-semibold text-base flex items-center gap-2 mb-2 text-purple-700">
        <FileText className="w-4 h-4" />Notes
        <Badge variant="outline" className="text-[10px] border-purple-300 text-purple-700 bg-purple-50 ml-1">Admin & Supervisors Only</Badge>
      </h3>
      {loading ? (
        <p className="text-xs text-muted-foreground italic">Loading notes…</p>
      ) : (
        <div className="space-y-2">
          <textarea
            className="w-full min-h-[110px] p-2.5 text-sm border rounded-lg bg-white focus:ring-2 focus:ring-purple-300 focus:border-purple-400 outline-none resize-y"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Private notes about this camper. Visible to Admin and all 3 division supervisors only."
            data-testid="camper-notes-textarea"
          />
          <div className="flex items-center justify-between gap-2">
            <p className="text-[10px] text-muted-foreground">
              {updatedDisplay
                ? <>Last edited{updatedBy ? <> by <span className="font-semibold">{updatedBy}</span></> : ""} · {updatedDisplay}</>
                : "Not edited yet."}
            </p>
            <Button size="sm" onClick={handleSave} disabled={saving || !dirty} className="bg-purple-600 hover:bg-purple-700 text-xs h-8" data-testid="camper-notes-save">
              <Save className="w-3 h-3 mr-1" />{saving ? "Saving…" : "Save Notes"}
            </Button>
          </div>
          {legacyNotes.length > 0 && (
            <div className="mt-3 border-t pt-3" data-testid="legacy-notes-block">
              <button
                type="button"
                onClick={() => setLegacyOpen(v => !v)}
                className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-slate-900"
                data-testid="legacy-notes-toggle"
              >
                <FileText className="w-3.5 h-3.5" />
                Legacy Notes (CampMinder &amp; prior seasons)
                <Badge variant="secondary" className="ml-1 h-4 px-1.5 text-[10px] bg-slate-200 text-slate-700">{legacyNotes.length}</Badge>
                {legacyOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </button>
              {legacyOpen && (
                <div className="mt-2 space-y-2 max-h-[260px] overflow-y-auto pr-1">
                  {legacyNotes.map((ln, i) => (
                    <div key={i} className="p-2 rounded-md border border-slate-200 bg-slate-50/60" data-testid={`legacy-note-${i}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-700 bg-white">
                          {ln.year ? ln.year : "Undated"}
                        </Badge>
                        <span className="text-[10px] text-slate-500">{ln.source}</span>
                      </div>
                      <p className="text-xs text-slate-700 whitespace-pre-wrap break-words">{ln.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function IncidentNotes2025Section({ camperId, initialNotes }) {
  const [allNotes, setAllNotes] = useState(initialNotes || "");
  const [newNote, setNewNote] = useState("");
  const [saving, setSaving] = useState(false);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    const dateStr = new Date().toLocaleDateString("en-US", { timeZone: "America/New_York", month: "2-digit", day: "2-digit", year: "numeric" });
    const entry = `[${dateStr}] ${newNote.trim()}`;
    const updated = allNotes ? `${allNotes}\n${entry}` : entry;
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/campers/${camperId}/incident-notes-2025`, { notes: updated }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllNotes(updated);
      setNewNote("");
      toast.success("Note added");
    } catch {
      toast.error("Failed to save note");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNote = async (lineIdx) => {
    const lines = allNotes.split("\n").filter((_, i) => i !== lineIdx);
    const updated = lines.join("\n");
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/campers/${camperId}/incident-notes-2025`, { notes: updated }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllNotes(updated);
      toast.success("Note deleted");
    } catch {
      toast.error("Failed to delete note");
    }
  };

  return (
    <section data-testid="incident-notes-2025-section">
      <h3 className="font-heading font-semibold text-base flex items-center gap-2 mb-2 text-purple-700">
        <FileText className="w-4 h-4" />Camper Notes
      </h3>
      {allNotes && (
        <div className="mb-2 p-2 rounded-lg border bg-purple-50/40 max-h-[160px] overflow-y-auto" data-testid="camper-notes-log">
          {allNotes.split("\n").filter(l => l.trim()).map((line, i) => (
            <div key={i} className="flex items-start gap-1 group py-0.5">
              <p className="text-sm flex-1">{line}</p>
              <button onClick={() => handleDeleteNote(i)} className="shrink-0 text-red-300 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity p-0.5" data-testid={`delete-note-${i}`} title="Delete note">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="space-y-2">
        <textarea
          className="w-full min-h-[44px] max-h-[100px] p-2.5 text-sm border rounded-lg bg-white focus:ring-2 focus:ring-purple-300 focus:border-purple-400 outline-none resize-y"
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          placeholder="Type a new note... (date added automatically)"
          data-testid="incident-notes-2025-textarea"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleAddNote(); } }}
        />
        <Button size="sm" onClick={handleAddNote} disabled={saving || !newNote.trim()} className="w-full bg-purple-600 hover:bg-purple-700 text-xs h-8" data-testid="incident-notes-2025-save">
          <Save className="w-3 h-3 mr-1" />{saving ? "Saving..." : "Add Note"}
        </Button>
      </div>
      <p className="text-[10px] text-muted-foreground mt-1">Press Enter to add. Date is stamped automatically.</p>
    </section>
  );
}

function AllergyReportsTab({ token, dietaryReports, setDietaryReports, dietaryDetail, setDietaryDetail, dietaryLoading, setDietaryLoading }) {
  const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    setDietaryLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/admin/dietary-reports`, { headers });
      setDietaryReports(res.data);
    } catch { /* ignore */ }
    finally { setDietaryLoading(false); }
  };

  const fetchDetail = async (date) => {
    setDietaryLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/admin/dietary-reports/${date}`, { headers });
      setDietaryDetail(res.data);
    } catch { toast.error("Failed to load report detail"); }
    finally { setDietaryLoading(false); }
  };

  const formatDate = (ds) => {
    const d = new Date(ds + "T12:00:00");
    return d.toLocaleDateString("en-US", { timeZone: "America/New_York", weekday: "short", month: "short", day: "numeric", year: "numeric" });
  };

  if (dietaryDetail) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-heading flex items-center gap-2">
                <Utensils className="w-5 h-5 text-amber-600" />
                Allergy Check Report — {formatDate(dietaryDetail.date)}
              </CardTitle>
            </div>
            <Button variant="outline" size="sm" onClick={() => setDietaryDetail(null)} data-testid="dietary-report-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {dietaryDetail.groups.map(g => (
              <div key={g.group_code} className={`rounded-lg border p-3 ${g.checked ? "border-green-200 bg-green-50/50" : "border-gray-200"}`} data-testid={`dietary-report-group-${g.group_code}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-sm">{g.group_code}</span>
                    {g.checked ? (
                      <Badge className="bg-green-100 text-green-700 text-[10px]">Verified</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] text-gray-500">Not Checked</Badge>
                    )}
                  </div>
                  {g.checked_at && (
                    <span className="text-[10px] text-muted-foreground">
                      {new Date(g.checked_at).toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit" })}
                    </span>
                  )}
                </div>
                {g.checked && g.campers.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1">
                    {g.campers.map((c, i) => (
                      <div key={i} className="flex items-center gap-1.5 text-xs py-0.5">
                        {c.checked ? <CheckCircle className="w-3.5 h-3.5 text-green-600 flex-shrink-0" /> : <XCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />}
                        <span className={c.checked ? "" : "text-muted-foreground"}>{c.camper_name}</span>
                        {c.allergies && c.allergies !== "None" && (
                          <span className="text-amber-600 text-[10px]">({c.allergies})</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <Utensils className="w-5 h-5 text-amber-600" />
          Allergy Daily Check Reports
        </CardTitle>
        <div className="flex items-center justify-between">
          <CardDescription>View daily allergy verification reports from dietary staff</CardDescription>
          <a href="http://www.rrdcdining.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 hover:text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5 hover:bg-amber-100 transition-colors" data-testid="admin-dining-link">
            <ExternalLink className="w-3.5 h-3.5" /> Dining
          </a>
        </div>
      </CardHeader>
      <CardContent>
        {dietaryLoading ? (
          <div className="text-center py-8 text-muted-foreground">Loading reports...</div>
        ) : dietaryReports.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">No allergy check reports yet</div>
        ) : (
          <div className="space-y-2">
            {dietaryReports.map(r => (
              <button
                key={r.date}
                onClick={() => fetchDetail(r.date)}
                className="w-full flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors text-left"
                data-testid={`dietary-report-date-${r.date}`}
              >
                <div>
                  <p className="font-semibold text-sm">{formatDate(r.date)}</p>
                  <p className="text-xs text-muted-foreground">{r.groups_checked}/{r.total_groups} groups checked</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${r.groups_checked === r.total_groups ? "bg-green-500" : "bg-amber-400"}`}
                      style={{ width: `${(r.groups_checked / r.total_groups) * 100}%` }}
                    />
                  </div>
                  <span className="text-xs font-medium">{Math.round((r.groups_checked / r.total_groups) * 100)}%</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}



function IncidentDigestView({ token, onBack }) {
  const [selectedDate, setSelectedDate] = useState(() =>
    new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" })
  );
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const handleSend = async () => {
    setRunning(true);
    setResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/incident-digest/run`,
        null,
        { headers, params: { date: selectedDate } },
      );
      setResult(res.data);
      const sent = (res.data?.results || []).filter(r => r.status === "sent").length;
      const skipped = (res.data?.results || []).filter(r => r.status === "skipped").length;
      const failed = (res.data?.results || []).filter(r => r.status === "failed").length;
      if (failed) {
        toast.error(`${failed} division${failed !== 1 ? "s" : ""} failed — see details below`);
      } else {
        toast.success(`Digest run: ${sent} sent, ${skipped} skipped`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to run digest");
    }
    setRunning(false);
  };

  const previewPdf = async (division) => {
    try {
      const res = await axios.get(
        `${API}/admin/incident-reports/eod-pdf/${division}`,
        { headers, params: { date: selectedDate }, responseType: "blob" },
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (e) {
      const status = e.response?.status;
      if (status === 404) toast.info(`No incidents for ${division} on ${selectedDate}`);
      else toast.error("Failed to load preview");
    }
  };

  return (
    <div className="p-4 max-w-3xl mx-auto space-y-4" data-testid="incident-digest-view">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="incident-digest-back">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Mail className="w-5 h-5 text-fuchsia-600" />
            Send Incident Digest
          </h2>
          <p className="text-xs text-muted-foreground">
            Manually fire the 4:45 PM ET incident digest to mariners@ / skippers@ / navigators@ rollingriver.com. One PDF per division, one incident per page.
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex items-end gap-4 flex-wrap">
            <div className="space-y-1">
              <Label className="text-xs">Digest date</Label>
              <Input
                type="date"
                value={selectedDate}
                onChange={e => setSelectedDate(e.target.value)}
                className="w-48"
                data-testid="incident-digest-date"
              />
            </div>
            <Button
              onClick={handleSend}
              disabled={running}
              className="gap-2 bg-fuchsia-600 hover:bg-fuchsia-700"
              data-testid="incident-digest-send-btn"
            >
              <Mail className="w-4 h-4" />
              {running ? "Sending…" : "Send Digest Now"}
            </Button>
          </div>

          <div className="flex flex-wrap gap-2 text-xs pt-2">
            <span className="text-muted-foreground">Preview PDF:</span>
            {["mariners", "skippers", "navigators"].map(d => (
              <Button
                key={d}
                variant="outline"
                size="sm"
                onClick={() => previewPdf(d)}
                className="h-7 px-2 text-[11px] capitalize"
                data-testid={`incident-digest-preview-${d}`}
              >
                {d}
              </Button>
            ))}
          </div>

          {result && (
            <div className="pt-3 border-t space-y-2" data-testid="incident-digest-result">
              <p className="text-xs font-semibold">Result for {result.date}:</p>
              {(result.results || []).map(r => (
                <div
                  key={r.division}
                  className={`flex items-center gap-2 text-xs p-2 rounded border ${
                    r.status === "sent" ? "bg-green-50 border-green-200"
                    : r.status === "failed" ? "bg-red-50 border-red-200"
                    : "bg-slate-50 border-slate-200"
                  }`}
                >
                  <Badge variant="outline" className="capitalize font-mono text-[10px]">{r.division}</Badge>
                  <span className="font-medium capitalize">{r.status}</span>
                  {r.status === "sent" && <span className="text-muted-foreground">→ {r.to} ({r.count} report{r.count !== 1 ? "s" : ""})</span>}
                  {r.status === "skipped" && <span className="text-muted-foreground">{r.reason?.replace(/_/g, " ")}</span>}
                  {r.status === "failed" && <span className="text-red-700 text-[11px]">{r.reason}</span>}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


function FlexDaysSyncControl() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/sync/flex-days/status`, { headers });
      setStatus(res.data);
      return res.data;
    } catch { return null; }
  };

  useEffect(() => {
    fetchStatus();
    const iv = setInterval(async () => {
      const s = await fetchStatus();
      if (s?.status !== "running") return;
    }, 4000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trigger = async (scope) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/sync/flex-days?scope=${scope}`, {}, { headers });
      if (res.data.status === "running") {
        toast.info(res.data.message);
      } else {
        toast.success(res.data.message);
      }
      await fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start flex-days sync");
    } finally {
      setBusy(false);
    }
  };

  const running = status?.status === "running";
  return (
    <div className="space-y-1.5" data-testid="flex-days-sync-control">
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          className="flex-1 border-sky-300 text-sky-700 hover:bg-sky-50"
          disabled={busy || running}
          onClick={() => trigger("flex")}
          data-testid="flex-days-sync-btn"
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-2 ${running ? "animate-spin" : ""}`} />
          {running ? `Flex sync ${status?.progress || "..."}` : "Sync Flex Days Only"}
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="border-sky-200 text-sky-600 hover:bg-sky-50"
          disabled={busy || running}
          onClick={() => trigger("all")}
          title="Slower — checks every camper, catches kids who just became flex"
          data-testid="flex-days-sync-all-btn"
        >
          All campers
        </Button>
      </div>
      {status?.status === "completed" && (
        <div className="text-[11px] text-sky-800 bg-sky-50 border border-sky-200 px-2.5 py-1.5 rounded-md" data-testid="flex-days-sync-summary">
          <span className="font-semibold">Flex days:</span> {status.progress}
          {(status.changed_campers || []).length > 0 && (
            <span className="block mt-0.5 text-sky-700">
              {status.changed_campers.slice(0, 5).map(c => c.name).join(", ")}
              {status.changed_campers.length > 5 ? ` +${status.changed_campers.length - 5} more` : ""}
            </span>
          )}
        </div>
      )}
      {status?.status === "failed" && (
        <div className="text-[11px] text-red-700 bg-red-50 px-2.5 py-1.5 rounded-md" data-testid="flex-days-sync-error">
          {status.progress}
        </div>
      )}
    </div>
  );
}


function FlexPushControl() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/sync/flex-push/status`, { headers });
      setStatus(res.data);
    } catch { /* silent */ }
  };

  useEffect(() => {
    fetchStatus();
    const iv = setInterval(fetchStatus, 30000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const push = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/sync/flex-push`, {}, { headers });
      const d = res.data || {};
      setStatus(d);
      if (d.status === "completed") {
        const un = (d.unmatched || []).length;
        toast[un > 0 ? "warning" : "success"](
          `Flex push: ${d.sent} sent · ${d.matched} matched${un > 0 ? ` · ${un} UNMATCHED` : ""}`
        );
      } else if (d.status === "skipped") {
        toast.warning("Push skipped — grouping API not configured");
      } else {
        toast.error(`Flex push failed: ${d.error || "unknown error"}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Flex push failed");
    } finally {
      setBusy(false);
    }
  };

  const triggerLabel = { manual: "manual", scheduled: "daily auto-push", after_flex_sync: "auto — flex sync", after_main_sync: "auto — main camper sync", after_excel_import: "auto — Excel import" };
  return (
    <div className="space-y-1.5" data-testid="flex-push-control">
      <Button
        variant="outline"
        size="sm"
        className="w-full border-violet-300 text-violet-700 hover:bg-violet-50"
        disabled={busy}
        onClick={push}
        data-testid="flex-push-btn"
      >
        <Send className={`w-3.5 h-3.5 mr-2 ${busy ? "animate-pulse" : ""}`} />
        {busy ? "Pushing flex days…" : "Push Flex Days to Grouping"}
      </Button>
      {status?.status === "completed" && (
        <div className="text-[11px] text-violet-800 bg-violet-50 border border-violet-200 px-2.5 py-1.5 rounded-md" data-testid="flex-push-summary">
          <span className="font-semibold">Last push:</span> {status.pushed_at_et} ({triggerLabel[status.trigger] || status.trigger}) —{" "}
          {status.sent} sent · {status.received} received · {status.matched} matched · {(status.unmatched || []).length} unmatched
          {(status.unknown_dates || []).length > 0 && (
            <span className="block mt-0.5 text-amber-700">Unknown dates: {status.unknown_dates.join(", ")}</span>
          )}
          {(status.unmatched || []).length > 0 && (
            <div className="mt-1 text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1" data-testid="flex-push-unmatched">
              <span className="font-semibold">Unmatched on grouping side:</span>
              {status.unmatched.map((u) => (
                <span key={u.person_id} className="block">
                  {u.name || "?"} {u.group_code ? `(${u.group_code})` : ""} — PersonID {u.person_id}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
      {status?.status === "failed" && (
        <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 px-2.5 py-1.5 rounded-md" data-testid="flex-push-error">
          <span className="font-semibold">Last push FAILED</span> ({status.pushed_at_et}, {triggerLabel[status.trigger] || status.trigger}): {status.error}
        </div>
      )}
      {status?.status === "skipped" && (
        <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1.5 rounded-md" data-testid="flex-push-skipped">
          Push skipped ({status.pushed_at_et}) — GROUPING_API_BASE_URL / GROUPING_API_KEY not configured.
        </div>
      )}
    </div>
  );
}


function ParentEmailSyncControl() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/sync/parent-emails/status`, { headers });
      setStatus(res.data);
    } catch { /* silent */ }
  };

  useEffect(() => {
    fetchStatus();
    const iv = setInterval(fetchStatus, 4000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trigger = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/sync/parent-emails`, {}, { headers });
      if (res.data.status === "running") toast.info(res.data.message);
      else toast.success(res.data.message);
      await fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start parent-email sync");
    } finally {
      setBusy(false);
    }
  };

  const running = status?.status === "running";
  const stalled = status?.status === "stalled";
  return (
    <div className="space-y-1.5" data-testid="parent-email-sync-control">
      <Button
        variant="outline"
        size="sm"
        className={`w-full ${stalled ? "border-amber-400 text-amber-700 hover:bg-amber-50" : "border-rose-300 text-rose-700 hover:bg-rose-50"}`}
        disabled={busy || running}
        onClick={trigger}
        data-testid="parent-email-sync-btn"
      >
        <Mail className={`w-3.5 h-3.5 mr-2 ${running ? "animate-pulse" : ""}`} />
        {running ? `Email sync ${status?.progress || "..."}` : stalled ? "Restart stalled email sync" : "Sync Parent Emails"}
      </Button>
      {stalled && (
        <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 px-2.5 py-1.5 rounded-md" data-testid="parent-email-sync-stalled">
          {status.progress}
        </div>
      )}
      {status?.status === "completed" && (
        <div className="text-[11px] text-rose-800 bg-rose-50 border border-rose-200 px-2.5 py-1.5 rounded-md" data-testid="parent-email-sync-summary">
          <span className="font-semibold">Parent emails:</span> {status.progress}
          {status.overrides_skipped > 0 && <span> · {status.overrides_skipped} manual override{status.overrides_skipped !== 1 ? "s" : ""} protected</span>}
          {(status.changed_campers || []).length > 0 && (
            <span className="block mt-0.5 text-rose-700">
              {status.changed_campers.slice(0, 5).map(c => c.name).join(", ")}
              {status.changed_campers.length > 5 ? ` +${status.changed_campers.length - 5} more` : ""}
            </span>
          )}
        </div>
      )}
      {status?.status === "failed" && (
        <div className="text-[11px] text-red-700 bg-red-50 px-2.5 py-1.5 rounded-md" data-testid="parent-email-sync-error">
          {status.progress}
        </div>
      )}
    </div>
  );
}


function AdminConflictsView({ token, onBack }) {
  const [selectedDate, setSelectedDate] = useState(() =>
    new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" })
  );
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resolving, setResolving] = useState({});
  const headers = { Authorization: `Bearer ${token}` };

  const fetchConflicts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/attendance/conflicts?date=${selectedDate}`, { headers });
      setData(res.data);
    } catch {
      toast.error("Failed to load conflicts");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  useEffect(() => { fetchConflicts(); }, [fetchConflicts]);

  const handleResolve = async (c, accept) => {
    const key = `${c.camper_id}:${accept}`;
    setResolving(prev => ({ ...prev, [key]: true }));
    try {
      await axios.post(
        `${API}/attendance/resolve-conflict`,
        { camper_id: c.camper_id, date: selectedDate, accept },
        { headers },
      );
      toast.success(`Resolved: kept ${accept.toUpperCase()} status for ${c.camper_name}`);
      await fetchConflicts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resolve failed");
    } finally {
      setResolving(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const byGroup = useMemo(() => {
    if (!data?.conflicts?.length) return [];
    const buckets = new Map();
    for (const c of data.conflicts) {
      const gc = c.group_code || "—";
      if (!buckets.has(gc)) buckets.set(gc, []);
      buckets.get(gc).push(c);
    }
    return [...buckets.entries()]
      .sort(([a], [b]) => a.localeCompare(b, "en", { numeric: true }))
      .map(([gc, items]) => ({
        group_code: gc,
        items: items.sort((a, b) => (a.camper_name || "").localeCompare(b.camper_name || "")),
      }));
  }, [data]);

  const total = data?.count ?? 0;

  return (
    <div className="p-4 max-w-5xl mx-auto space-y-4" data-testid="admin-conflicts-view">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="admin-conflicts-back">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            Admin Conflicts
            {total > 0 && (
              <Badge variant="outline" className="bg-red-100 text-red-800 border-red-300" data-testid="admin-conflicts-count">
                {total}
              </Badge>
            )}
            {(data?.not_marked ?? 0) > 0 && (
              <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300" data-testid="admin-conflicts-not-marked-count">
                {data.not_marked} not marked
              </Badge>
            )}
          </h2>
          <p className="text-xs text-muted-foreground">
            Every group&apos;s unresolved attendance conflict for the selected day, sorted by group.
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex items-end gap-4 flex-wrap">
            <div className="space-y-1">
              <Label className="text-xs">Date</Label>
              <Input
                type="date"
                value={selectedDate}
                onChange={e => setSelectedDate(e.target.value)}
                className="w-48"
                data-testid="admin-conflicts-date"
              />
            </div>
            <Button variant="outline" size="sm" onClick={fetchConflicts} disabled={loading} className="gap-2" data-testid="admin-conflicts-refresh">
              {loading ? "Loading…" : "Refresh"}
            </Button>
          </div>

          {loading && <p className="text-xs text-muted-foreground">Loading conflicts…</p>}

          {!loading && total === 0 && (
            <p className="text-sm text-muted-foreground py-8 text-center" data-testid="admin-conflicts-empty">
              No unresolved conflicts on {selectedDate}.
            </p>
          )}

          {!loading && total > 0 && (
            <div className="space-y-6">
              {byGroup.map(({ group_code, items }) => (
                <div key={group_code} className="space-y-2" data-testid={`admin-conflicts-group-${group_code}`}>
                  <div className="flex items-center gap-2 border-b pb-1">
                    <Badge variant="outline" className="bg-slate-100 font-mono text-[11px]">{group_code}</Badge>
                    <span className="text-xs text-muted-foreground">
                      {items.length} conflict{items.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {items.map(c => (
                      <div
                        key={c.camper_id}
                        className={`flex flex-wrap items-center gap-2 p-2.5 rounded-md ${c.not_marked ? "bg-amber-50 border border-amber-200" : "bg-red-50 border border-red-200"}`}
                        data-testid={`admin-conflict-row-${c.camper_id}`}
                      >
                        <AlertTriangle className={`w-4 h-4 flex-shrink-0 ${c.not_marked ? "text-amber-600" : "text-red-600"}`} />
                        <span className="font-semibold text-sm">{c.camper_name}</span>
                        <div className="flex flex-wrap gap-1 text-[10px]">
                          {c.not_marked && (
                            <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300" data-testid={`not-marked-badge-${c.camper_id}`}>
                              Not marked present or absent
                            </Badge>
                          )}
                          {c.app_status && <Badge variant="outline" className="bg-white">App: {c.app_status}</Badge>}
                          {c.bus_status && <Badge variant="outline" className="bg-white">Bus: {c.bus_status}</Badge>}
                          {c.counselor_status && <Badge variant="outline" className="bg-white">Group: {c.counselor_status}</Badge>}
                        </div>
                        <div className="ml-auto flex gap-1">
                          {c.app_status && (
                            <Button
                              size="sm" variant="outline"
                              disabled={!!resolving[`${c.camper_id}:app`]}
                              onClick={() => handleResolve(c, "app")}
                              className="h-7 px-2 text-[11px]"
                              data-testid={`resolve-app-${c.camper_id}`}
                            >
                              Keep App
                            </Button>
                          )}
                          {c.bus_status && (
                            <Button
                              size="sm" variant="outline"
                              disabled={!!resolving[`${c.camper_id}:bus`]}
                              onClick={() => handleResolve(c, "bus")}
                              className="h-7 px-2 text-[11px]"
                              data-testid={`resolve-bus-${c.camper_id}`}
                            >
                              Keep Bus
                            </Button>
                          )}
                          {c.counselor_status && (
                            <Button
                              size="sm" variant="outline"
                              disabled={!!resolving[`${c.camper_id}:counselor`]}
                              onClick={() => handleResolve(c, "counselor")}
                              className="h-7 px-2 text-[11px]"
                              data-testid={`resolve-counselor-${c.camper_id}`}
                            >
                              Keep Group
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


function AttendanceReportView({ token, onBack }) {
  const [selectedDate, setSelectedDate] = useState(() => {
    return new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
  });
  const [downloading, setDownloading] = useState(false);
  const [conflicts, setConflicts] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchConflicts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/attendance/conflicts?date=${selectedDate}`, { headers });
      setConflicts(res.data);
    } catch { /* silent */ }
  }, [selectedDate]);

  useEffect(() => { fetchConflicts(); }, [fetchConflicts]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API}/attendance/export?date=${selectedDate}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `attendance_report_${selectedDate}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Attendance report downloaded");
    } catch {
      toast.error("Failed to download report");
    }
    setDownloading(false);
  };

  return (
    <div className="p-4 max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="attendance-report-back">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h2 className="text-lg font-bold">Attendance Report</h2>
          <p className="text-xs text-muted-foreground">Download XLSX with each group as its own tab</p>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex items-end gap-4 flex-wrap">
            <div className="space-y-1">
              <Label className="text-xs">Report Date</Label>
              <Input
                type="date"
                value={selectedDate}
                onChange={e => setSelectedDate(e.target.value)}
                className="w-48"
                data-testid="attendance-report-date"
              />
            </div>
            <Button onClick={handleDownload} disabled={downloading} className="gap-2" data-testid="attendance-report-download">
              <Download className="w-4 h-4" />
              {downloading ? "Downloading..." : "Download XLSX"}
            </Button>
            <Button variant="outline" onClick={() => window.open("http://rrdcattendance.com", "_blank")} className="gap-2" data-testid="attendance-app-link">
              <ExternalLink className="w-4 h-4" />
              Open Attendance App
            </Button>
          </div>

          {conflicts && (
            <div className="pt-4 border-t">
              <h3 className="text-sm font-bold mb-2">
                Conflicts for {selectedDate}
                {conflicts.count > 0 && <Badge variant="outline" className="ml-2 bg-red-100 text-red-800 border-red-300">{conflicts.count}</Badge>}
              </h3>
              {conflicts.count === 0 ? (
                <p className="text-xs text-muted-foreground">No conflicts on this date</p>
              ) : (
                <div className="space-y-1">
                  {conflicts.conflicts.map(c => (
                    <div key={c.camper_id} className="flex items-center gap-3 text-xs p-2 rounded bg-red-50 border border-red-200">
                      <AlertTriangle className="w-3.5 h-3.5 text-red-600 flex-shrink-0" />
                      <span className="font-medium">{c.camper_name}</span>
                      <Badge variant="outline" className="text-[9px]">{c.group_code}</Badge>
                      <span className="text-muted-foreground">App: {c.app_status} | Bus: {c.bus_status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}



function TestingToolsView({ token }) {
  const [clearPassword, setClearPassword] = useState("");
  const [clearing, setClearing] = useState({});
  const [results, setResults] = useState({});
  const [clearDate, setClearDate] = useState("");
  const [clearDateResult, setClearDateResult] = useState(null);
  // Per-camper attendance clear
  const [clearCamperName, setClearCamperName] = useState("");
  const [clearCamperDate, setClearCamperDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [clearingCamperAttendance, setClearingCamperAttendance] = useState(false);
  const [clearCamperResult, setClearCamperResult] = useState(null);

  const handleClearCamperAttendance = async () => {
    const name = clearCamperName.trim();
    if (!name || !clearCamperDate) return;
    if (!window.confirm(`Clear ALL attendance + bus/counselor/notification records for "${name}" on ${clearCamperDate}? Other kids are not affected.`)) return;
    setClearingCamperAttendance(true);
    setClearCamperResult(null);
    try {
      const res = await axios.post(`${API}/admin/clear-attendance-for-camper`, {
        camper_id: name,
        date: clearCamperDate,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setClearCamperResult({ success: true, message: `${res.data.message} — wiped ${res.data.deleted_count} attendance + ${res.data.notifications_cleared} notification record(s).` });
      toast.success(res.data.message);
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to clear";
      setClearCamperResult({ success: false, error: msg });
      toast.error(msg);
    } finally {
      setClearingCamperAttendance(false);
    }
  };

  const handleClearAttendanceForDate = async () => {
    if (!clearPassword) { toast.error("Enter the password first"); return; }
    if (!clearDate) { toast.error("Pick a date first"); return; }
    if (!window.confirm(`Delete ALL attendance records (Present/Absent/Late/Dismissed) for ${clearDate}? This wipes marks from every source — Attendance App, Bus Mapping App, counselor sync, front desk — for that single date. Cannot be undone.`)) return;

    setClearing(prev => ({ ...prev, clear_date: true }));
    setClearDateResult(null);
    try {
      const res = await axios.post(`${API}/admin/clear-attendance-for-date`, {
        date: clearDate,
        password: clearPassword,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setClearDateResult({ success: true, deleted: res.data.deleted_count, notifs: res.data.notifications_cleared, date: res.data.date });
      toast.success(`Cleared ${res.data.deleted_count} attendance records + ${res.data.notifications_cleared} notifications for ${res.data.date}`);
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to clear";
      setClearDateResult({ success: false, error: msg });
      toast.error(msg);
    } finally {
      setClearing(prev => ({ ...prev, clear_date: false }));
    }
  };

  const collections = [
    { key: "buddy_sheets", label: "Buddy Sheets", icon: ClipboardList, color: "amber" },
    { key: "incident_reports", label: "Incident Reports", icon: AlertTriangle, color: "rose" },
    { key: "trip_forms", label: "Trip Forms", icon: Bus, color: "green" },
    { key: "evaluations", label: "Evaluations", icon: Star, color: "purple" },
    { key: "daily_attendance", label: "Attendance Records", icon: CheckCircle, color: "cyan" },
    { key: "attendance_notifications", label: "Attendance Notifications", icon: Clock, color: "violet" },
    { key: "lunch_orders", label: "Lunch Orders", icon: Utensils, color: "yellow" },
    { key: "swim_lessons", label: "Swim Lessons", icon: Waves, color: "sky" },
    { key: "color_war", label: "Color War Teams", icon: Flag, color: "fuchsia" },
    { key: "intramurals", label: "Mariner Intramurals", icon: Trophy, color: "blue" },
    { key: "mariner_hobbies", label: "Mariner Hobbies", icon: Star, color: "purple" },
    { key: "navigator_hobbies", label: "Navigator Hobbies", icon: Sparkles, color: "indigo" },
    { key: "navigator_intensives", label: "Navigator Intensives", icon: Sparkles, color: "fuchsia" },
    { key: "nav_intramurals", label: "Nav Intramurals (All)", icon: Users, color: "teal" },
    { key: "nav_intramurals_scores", label: "Nav Scores Only", icon: CalendarDays, color: "emerald" },
    { key: "nav_intramurals_playoffs", label: "Nav Playoffs Only", icon: Swords, color: "amber" },
    { key: "nav_intramurals_championship", label: "Nav Championship Only", icon: Trophy, color: "violet" },
    { key: "messages", label: "Messages", icon: MessageSquare, color: "blue" },
    { key: "notifications", label: "Notifications", icon: Bell, color: "orange" },
  ];

  const handleClear = async (collectionKey) => {
    if (!clearPassword) {
      toast.error("Enter the password first");
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ALL ${collections.find(c => c.key === collectionKey)?.label}? This cannot be undone.`)) return;

    setClearing(prev => ({ ...prev, [collectionKey]: true }));
    setResults(prev => ({ ...prev, [collectionKey]: null }));
    try {
      const res = await axios.post(`${API}/admin/clear-data/${collectionKey}`, { password: clearPassword }, { headers: { Authorization: `Bearer ${token}` } });
      setResults(prev => ({ ...prev, [collectionKey]: { success: true, count: res.data.deleted_count } }));
      toast.success(`${res.data.message} (${res.data.deleted_count} records)`);
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to clear";
      setResults(prev => ({ ...prev, [collectionKey]: { success: false, error: msg } }));
      toast.error(msg);
    } finally {
      setClearing(prev => ({ ...prev, [collectionKey]: false }));
    }
  };

  // Re-Sort Nav Teams handler
  const handleNavReSort = async () => {
    if (!clearPassword) { toast.error("Enter the password first"); return; }
    if (clearPassword !== "1993") { toast.error("Incorrect password"); return; }
    if (!window.confirm("This will re-sort all Navigator teams and override manual changes. Are you sure?")) return;
    setClearing(prev => ({ ...prev, nav_resort: true }));
    try {
      await axios.post(`${API}/nav-intramurals/sort`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Navigator teams re-sorted!");
      setResults(prev => ({ ...prev, nav_resort: { success: true, count: 0 } }));
    } catch (err) { toast.error(err.response?.data?.detail || "Re-sort failed"); }
    setClearing(prev => ({ ...prev, nav_resort: false }));
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-heading font-bold text-lg">Testing Tools</h2>
        <p className="text-sm text-muted-foreground">Clear test data from specific collections. Enter password to enable.</p>
      </div>

      <Card>
        <CardContent className="pt-4 space-y-4">
          <div className="flex items-center gap-3 max-w-xs">
            <Label htmlFor="clear-pw" className="text-sm font-medium whitespace-nowrap">Password:</Label>
            {/* Hidden decoy fields to defeat Chrome's autofill heuristics */}
            <input type="text" name="username" autoComplete="username" style={{ display: "none" }} aria-hidden="true" tabIndex={-1} />
            <input type="password" name="password" autoComplete="current-password" style={{ display: "none" }} aria-hidden="true" tabIndex={-1} />
            <Input
              id="clear-pw"
              type="password"
              name="clear-data-key"
              placeholder="Enter password"
              value={clearPassword}
              onChange={e => setClearPassword(e.target.value)}
              data-testid="clear-password-input"
              className="h-9"
              autoComplete="new-password"
              data-lpignore="true"
              data-1p-ignore="true"
              data-form-type="other"
            />
          </div>

          <div className="rounded-lg border-2 border-red-300 bg-red-50/50 p-3 space-y-2">
            <div className="flex items-start gap-2">
              <div className="p-1.5 rounded-md bg-red-100">
                <CheckCircle className="w-4 h-4 text-red-700" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-red-900">Clear Attendance for a Specific Date</p>
                <p className="text-xs text-red-800 leading-snug mt-0.5">
                  Wipes ALL Present/Absent/Late/Dismissed marks for one date, no matter which app wrote them
                  (Attendance App, Bus Mapping App, counselor sync, front desk). Use this when the Attendance App
                  says "Cleared 0 records" but stuck marks still appear on the bus app's iPad.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                type="date"
                value={clearDate}
                onChange={e => setClearDate(e.target.value)}
                className="h-9 max-w-[180px]"
                data-testid="clear-attendance-date-input"
              />
              <Button
                variant="destructive"
                size="sm"
                className="h-9 px-3 text-xs"
                disabled={clearing.clear_date || !clearPassword || !clearDate}
                onClick={handleClearAttendanceForDate}
                data-testid="clear-attendance-date-btn"
              >
                {clearing.clear_date ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : <Trash2 className="w-3 h-3 mr-1" />}
                Clear Attendance for {clearDate || "this date"}
              </Button>
            </div>
            {clearDateResult && (
              <p className={`text-xs font-medium ${clearDateResult.success ? "text-green-700" : "text-red-600"}`}>
                {clearDateResult.success
                  ? `Cleared ${clearDateResult.deleted} attendance + ${clearDateResult.notifs} notification records for ${clearDateResult.date}`
                  : clearDateResult.error}
              </p>
            )}
          </div>

          {/* Surgical per-camper clear — fixes stuck bus/counselor status for ONE kid only */}
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="clear-camper-attendance-section">
            <div className="flex items-start justify-between mb-2 gap-2">
              <div>
                <p className="text-sm font-semibold text-amber-900 mb-1">Clear ONE camper's attendance for a date</p>
                <p className="text-xs text-amber-800">
                  Use this when a single kid is stuck (e.g. showing Absent on the bus but Present everywhere else). Wipes all attendance + notification records for that camper on that date so every app re-syncs cleanly. Other kids unaffected.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <input
                type="text"
                placeholder="Camper name (e.g. Callen Fagan)"
                value={clearCamperName}
                onChange={(e) => setClearCamperName(e.target.value)}
                className="flex-1 min-w-[200px] px-3 py-2 border border-amber-300 rounded-md text-sm bg-white"
                data-testid="clear-camper-name-input"
              />
              <input
                type="date"
                value={clearCamperDate}
                onChange={(e) => setClearCamperDate(e.target.value)}
                className="px-3 py-2 border border-amber-300 rounded-md text-sm bg-white"
                data-testid="clear-camper-date-input"
              />
              <Button
                onClick={handleClearCamperAttendance}
                disabled={clearingCamperAttendance || !clearCamperName.trim() || !clearCamperDate}
                className="bg-amber-700 hover:bg-amber-800 text-white"
                data-testid="clear-camper-attendance-btn"
              >
                {clearingCamperAttendance ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : <Trash2 className="w-3 h-3 mr-1" />}
                Clear Camper
              </Button>
            </div>
            {clearCamperResult && (
              <p className={`text-xs font-medium mt-2 ${clearCamperResult.success ? "text-green-700" : "text-red-600"}`}>
                {clearCamperResult.message || clearCamperResult.error}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {collections.map(({ key, label, icon: Icon, color }) => (
              <div key={key} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-2">
                  <div className={`p-1.5 rounded-md bg-${color}-50`}>
                    <Icon className={`w-4 h-4 text-${color}-600`} />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{label}</p>
                    {results[key] && (
                      <p className={`text-xs ${results[key].success ? "text-green-600" : "text-red-500"}`}>
                        {results[key].success ? `Cleared ${results[key].count} records` : results[key].error}
                      </p>
                    )}
                  </div>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  className="h-8 px-3 text-xs"
                  disabled={clearing[key] || !clearPassword}
                  onClick={() => handleClear(key)}
                  data-testid={`clear-${key}-btn`}
                >
                  {clearing[key] ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : <Trash2 className="w-3 h-3 mr-1" />}
                  Clear All
                </Button>
              </div>
            ))}
            {/* Nav Re-Sort Teams button */}
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-md bg-teal-50">
                  <RefreshCw className="w-4 h-4 text-teal-600" />
                </div>
                <div>
                  <p className="text-sm font-medium">Nav Re-Sort Teams</p>
                  {results.nav_resort && (
                    <p className="text-xs text-green-600">Teams re-sorted!</p>
                  )}
                </div>
              </div>
              <Button
                size="sm"
                className="h-8 px-3 text-xs bg-teal-600 hover:bg-teal-700"
                disabled={clearing.nav_resort || !clearPassword}
                onClick={handleNavReSort}
                data-testid="clear-nav-resort-btn"
              >
                {clearing.nav_resort ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                Re-Sort
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


function ColorWarView({ token, isAdmin }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [staffUploading, setStaffUploading] = useState(false);
  const [pdfDialog, setPdfDialog] = useState(false);
  const [pdfEmail, setPdfEmail] = useState("");
  const [pdfEmailing, setPdfEmailing] = useState(false);
  const [whiteName, setWhiteName] = useState("");
  const [blueName, setBlueName] = useState("");
  const [cwVisible, setCwVisible] = useState(false);
  const [scores, setScores] = useState({ blue_name: "Blue Sonic", white_name: "White Mario", blue_score: "", white_score: "", winner: "", hidden: false });
  const [scoresSaving, setScoresSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/color-war/scores`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setScores(res.data))
      .catch(() => {});
  }, [token]);

  const handleSaveScores = async () => {
    setScoresSaving(true);
    try {
      await axios.put(`${API}/color-war/team-names`, { blue: scores.blue_name, white: scores.white_name }, { headers });
      await axios.put(`${API}/color-war/scores`, { blue: scores.blue_score, white: scores.white_score, winner: scores.winner, hidden: scores.hidden }, { headers });
      toast.success("Team names & scores updated");
    } catch { toast.error("Failed to save scores"); }
    setScoresSaving(false);
  };

  const handleToggleScoreBanner = async () => {
    const newHidden = !scores.hidden;
    try {
      await axios.put(`${API}/color-war/scores`, { blue: scores.blue_score, white: scores.white_score, winner: scores.winner, hidden: newHidden }, { headers });
      setScores(s => ({ ...s, hidden: newHidden }));
      toast.success(newHidden ? "Score banner HIDDEN from counselor dashboards" : "Score banner SHOWING on counselor dashboards");
    } catch { toast.error("Failed to toggle banner"); }
  };

  const sendChampionEmail = async (force) => {
    try {
      const res = await axios.post(`${API}/color-war/champion-email/send`, { confirm: true, force: !!force }, { headers });
      toast.info(`Champion announcement sending to ${res.data.recipient_count} parents in the background…`);
      for (;;) {
        await new Promise((r) => setTimeout(r, 2500));
        try {
          const j = await axios.get(`${API}/color-war/email-job/${res.data.job_id}`, { headers });
          if (j.data.status !== "running") {
            toast.success(`Champion announcement sent to ${j.data.sent_ok} parents 🏆${j.data.failed ? ` (${j.data.failed} failed)` : ""}`);
            break;
          }
        } catch { break; }
      }
    } catch (err) {
      const detail = err.response?.data?.detail || "Champion email failed";
      if (err.response?.status === 409 && !detail.includes("in progress")) {
        if (window.confirm("The champion email was already sent this season. Send it again to everyone?")) return sendChampionEmail(true);
      } else {
        toast.error(detail);
      }
    }
  };

  const handleSetWinner = async (team) => {
    const newWinner = scores.winner === team ? "" : team;
    try {
      await axios.put(`${API}/color-war/scores`, { blue: scores.blue_score, white: scores.white_score, winner: newWinner, hidden: scores.hidden }, { headers });
      setScores(s => ({ ...s, winner: newWinner }));
      if (newWinner) {
        const wName = newWinner === "blue" ? scores.blue_name : scores.white_name;
        toast.success(`${wName} crowned CHAMPIONS! Banner is now gold`);
        if (window.confirm(`👑 ${wName} are the champions!\n\nEmail the winner announcement to ALL parents now?`)) {
          await sendChampionEmail(false);
        }
      }
      else toast.success("Winner cleared — back to regular scoreboard");
    } catch { toast.error("Failed to update winner"); }
  };

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/color-war/status`, { headers });
      setStatus(res.data);
      setCwVisible(res.data.visible || false);
    } catch { toast.error("Failed to load Color War data"); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleAssign = async () => {
    if (!window.confirm("Assign Color War teams? This will randomly assign all eligible campers to Blue or White.")) return;
    setAssigning(true);
    try {
      const res = await axios.post(`${API}/color-war/assign`, {}, { headers });
      toast.success(res.data.message);
      fetchStatus();
    } catch (err) { toast.error(err.response?.data?.detail || "Assignment failed"); }
    setAssigning(false);
  };

  const handleApplyTeams = async () => {
    if (!window.confirm("Set Color War colors from the saved head-counselor Teams?\n\nTeam 1 → Blue, Team 2 → White\n(G15 flipped: Team 1 → White, Team 2 → Blue)\n\nThis overwrites colors for campers in groups with saved teams.")) return;
    try {
      const res = await axios.post(`${API}/color-war/apply-teams`, {}, { headers });
      toast.success(res.data.message);
      fetchStatus();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to apply Teams"); }
  };


  const handleStaffUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setStaffUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/color-war/staff/upload`, formData, { headers });
      toast.success(res.data.message);
      fetchStatus();
    } catch { toast.error("Staff upload failed"); }
    setStaffUploading(false);
    e.target.value = "";
  };

  const handleExportCSV = async () => {
    try {
      const res = await fetch(`${API}/color-war/export/csv`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "Color_War_Teams.xlsx"; a.click();
    } catch { toast.error("Export failed"); }
  };

  const handleExportPDF = (layout) => {
    if (!whiteName || !blueName) { toast.error("Enter both team names"); return; }
    const params = new URLSearchParams({ token, white_name: whiteName, blue_name: blueName, layout: layout || "one_page" });
    window.open(`${API}/color-war/export/pdf?${params.toString()}`, "_blank");
    setPdfDialog(false);
  };

  const handleEmailPDF = async (layout) => {
    if (!whiteName || !blueName) { toast.error("Enter both team names"); return; }
    if (!pdfEmail || !pdfEmail.includes("@")) { toast.error("Enter a valid email address"); return; }
    setPdfEmailing(true);
    try {
      const res = await axios.post(`${API}/color-war/export/pdf/email`,
        { email: pdfEmail, white_name: whiteName, blue_name: blueName, layout: layout || "one_page" }, { headers });
      toast.success(res.data.message);
    } catch (err) { toast.error(err.response?.data?.detail || "Email failed"); }
    setPdfEmailing(false);
  };

  const groups = status?.groups || {};
  const sortedGroups = Object.values(groups).sort((a, b) => (a.group_code || "").localeCompare(b.group_code || ""));

  if (loading) return <div className="text-center py-10 text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-heading font-bold text-lg">Color War</h2>
          <p className="text-sm text-muted-foreground">
            {status?.assigned ? `${status.camper_count} campers + ${status.staff_count} staff assigned` : "No teams assigned yet"}
            {status?.assigned && <> — <span className="text-blue-600 font-medium">Blue: {status.blue_total}</span> | <span className="font-medium">White: {status.white_total}</span></>}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {isAdmin && (
            <>
              <button
                onClick={async () => {
                  const newVal = !cwVisible;
                  try {
                    await axios.put(`${API}/color-war/visibility`, { visible: newVal }, { headers });
                    setCwVisible(newVal);
                    toast.success(newVal ? "Color War REVEALED to counselors" : "Color War HIDDEN from counselors");
                  } catch { toast.error("Failed to toggle"); }
                }}
                className={`flex items-center gap-1.5 h-8 px-3 text-xs font-bold rounded-md border-2 transition-all ${cwVisible ? "bg-green-100 border-green-500 text-green-800" : "bg-gray-100 border-gray-300 text-gray-600"}`}
                data-testid="cw-visibility-toggle"
              >
                {cwVisible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                {cwVisible ? "Visible" : "Hidden"}
              </button>
              <Button size="sm" className="h-8 text-xs bg-fuchsia-600 hover:bg-fuchsia-700" onClick={handleAssign} disabled={assigning} data-testid="cw-assign-btn">
                {assigning ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : <Flag className="w-3 h-3 mr-1" />}
                {assigning ? "Assigning..." : "Assign Teams"}
              </Button>
              <Button size="sm" variant="outline" className="h-8 text-xs border-blue-400 text-blue-700 hover:bg-blue-50" onClick={handleApplyTeams} data-testid="cw-apply-teams-btn">
                <ArrowRightLeft className="w-3 h-3 mr-1" /> From HC Teams
              </Button>
              <label className="inline-flex items-center h-8 px-3 text-xs border rounded-md cursor-pointer hover:bg-muted" data-testid="cw-staff-upload">
                <Upload className="w-3 h-3 mr-1" /> Staff CSV
                <input type="file" accept=".csv,.xlsx" className="hidden" onChange={handleStaffUpload} disabled={staffUploading} />
              </label>
            </>
          )}
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={handleExportCSV} data-testid="cw-export-csv">
            <Download className="w-3 h-3 mr-1" /> Excel
          </Button>
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setPdfDialog(true)} data-testid="cw-export-pdf-btn">
            <Download className="w-3 h-3 mr-1" /> PDF
          </Button>
        </div>
      </div>

      {isAdmin && (
        <Card className="p-4 border-2 border-fuchsia-200 bg-gradient-to-r from-blue-50 via-white to-fuchsia-50" data-testid="cw-scores-card">
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="w-4 h-4 text-fuchsia-600" />
            <h3 className="font-heading font-bold text-sm">Daily Team Scores</h3>
            <span className="text-xs text-muted-foreground">— shown on top of all counselor dashboards</span>
            <button
              onClick={handleToggleScoreBanner}
              className={`ml-auto flex items-center gap-1.5 h-8 px-3 text-xs font-bold rounded-md border-2 transition-all ${!scores.hidden ? "bg-green-100 border-green-500 text-green-800" : "bg-gray-100 border-gray-300 text-gray-600"}`}
              data-testid="cw-score-visibility-btn"
            >
              {!scores.hidden ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
              {!scores.hidden ? "Showing" : "Hidden"}
            </button>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs font-bold text-blue-700">Blue Team Name</Label>
              <Input
                value={scores.blue_name}
                onChange={(e) => setScores(s => ({ ...s, blue_name: e.target.value }))}
                placeholder="e.g. Blue Sonic"
                className="h-9 w-40 mt-1 font-bold text-blue-700"
                data-testid="cw-name-blue-input"
              />
            </div>
            <div>
              <Label className="text-xs font-bold text-blue-700">Score</Label>
              <Input
                value={scores.blue_score}
                onChange={(e) => setScores(s => ({ ...s, blue_score: e.target.value }))}
                placeholder="e.g. 250"
                className="h-9 w-24 mt-1 font-bold text-blue-700"
                data-testid="cw-score-blue-input"
              />
            </div>
            <div>
              <Label className="text-xs font-bold text-slate-700">White Team Name</Label>
              <Input
                value={scores.white_name}
                onChange={(e) => setScores(s => ({ ...s, white_name: e.target.value }))}
                placeholder="e.g. White Mario"
                className="h-9 w-40 mt-1 font-bold text-slate-700"
                data-testid="cw-name-white-input"
              />
            </div>
            <div>
              <Label className="text-xs font-bold text-slate-700">Score</Label>
              <Input
                value={scores.white_score}
                onChange={(e) => setScores(s => ({ ...s, white_score: e.target.value }))}
                placeholder="e.g. 250"
                className="h-9 w-24 mt-1 font-bold text-slate-700"
                data-testid="cw-score-white-input"
              />
            </div>
            <Button size="sm" className="h-9 text-xs bg-fuchsia-600 hover:bg-fuchsia-700" onClick={handleSaveScores} disabled={scoresSaving} data-testid="cw-score-save-btn">
              {scoresSaving ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
              Save
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">Team names carry into the banner and reveal emails — change them each year. Use Showing/Hidden to turn the banner on and off.</p>
          <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-fuchsia-100">
            <Crown className="w-4 h-4 text-amber-500" />
            <span className="text-xs font-bold">Declare Winner:</span>
            <button
              onClick={() => handleSetWinner("blue")}
              className={`h-8 px-3 text-xs font-bold rounded-md border-2 transition-all ${scores.winner === "blue" ? "bg-amber-400 border-amber-500 text-amber-950 shadow" : "bg-white border-blue-300 text-blue-700 hover:bg-blue-50"}`}
              data-testid="cw-winner-blue-btn"
            >
              {scores.winner === "blue" ? "👑 " : ""}{scores.blue_name}
            </button>
            <button
              onClick={() => handleSetWinner("white")}
              className={`h-8 px-3 text-xs font-bold rounded-md border-2 transition-all ${scores.winner === "white" ? "bg-amber-400 border-amber-500 text-amber-950 shadow" : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"}`}
              data-testid="cw-winner-white-btn"
            >
              {scores.winner === "white" ? "👑 " : ""}{scores.white_name}
            </button>
            <span className="text-[11px] text-muted-foreground">{scores.winner ? "Banner is gold — click again to undo" : "turns the counselor banner gold"}</span>
          </div>
        </Card>
      )}

      {isAdmin && status?.assigned && (
        <ColorWarRevealEmailPanel />
      )}

      {isAdmin && <ColorWarLeadershipPanel token={token} />}

      {/* Group list with expandable dropdowns */}
      <div className="space-y-2">
        {sortedGroups.map(g => (
          <Card key={g.group_code} className="overflow-hidden">
            <button
              className="w-full p-3 flex items-center justify-between text-left hover:bg-muted/50 transition-colors"
              onClick={() => setExpandedGroup(expandedGroup === g.group_code ? null : g.group_code)}
              data-testid={`cw-group-${g.group_code}`}
            >
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm">{g.group_code}</span>
                {g.group_name && <span className="text-xs text-muted-foreground">— {g.group_name}</span>}
                <Badge variant="outline" className="text-[10px]">{g.campers.length + g.staff.length}</Badge>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-blue-600 font-medium">{g.campers.filter(c => c.color_war === "blue").length + g.staff.filter(s => s.color_war === "blue").length} blue</span>
                <span className="text-xs text-gray-600 font-medium">{g.campers.filter(c => c.color_war === "white").length + g.staff.filter(s => s.color_war === "white").length} white</span>
                {expandedGroup === g.group_code ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </button>
            {expandedGroup === g.group_code && (
              <CardContent className="pt-0 pb-3 px-3">
                <div className="space-y-1">
                  {g.staff.map((s, i) => (
                    <div key={`staff-${i}`} className="flex items-center justify-between py-1 px-2 rounded bg-muted/30">
                      <span className="text-xs font-medium">{s.name} <span className="text-muted-foreground">(Counselor)</span></span>
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          const newTeam = s.color_war === "blue" ? "white" : "blue";
                          try {
                            const token = localStorage.getItem("token");
                            await axios.put(`${API}/color-war/flip`, { staff_name: s.name, team: newTeam }, { headers: { Authorization: `Bearer ${token}` } });
                            fetchStatus();
                          } catch { toast.error("Failed to flip team"); }
                        }}
                        className="cursor-pointer hover:scale-110 transition-transform"
                        title={`Click to switch to ${s.color_war === "blue" ? "white" : "blue"}`}
                        data-testid={`cw-flip-staff-${i}`}
                      >
                        <Badge className={`text-[10px] ${s.color_war === "blue" ? "bg-blue-600 hover:bg-gray-500" : "bg-gray-500 hover:bg-blue-600"}`}>{s.color_war}</Badge>
                      </button>
                    </div>
                  ))}
                  {(g.cits || []).map((c) => (
                    <div key={`cit-${c.id}`} className="flex items-center justify-between py-1 px-2 rounded bg-amber-50/60">
                      <span className="text-xs font-medium">{c.name} <span className="text-muted-foreground">(CIT)</span></span>
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          const newTeam = c.color_war === "blue" ? "white" : "blue";
                          try {
                            const token = localStorage.getItem("token");
                            await axios.put(`${API}/color-war/flip`, { camper_id: c.id, team: newTeam }, { headers: { Authorization: `Bearer ${token}` } });
                            fetchStatus();
                          } catch { toast.error("Failed to flip team"); }
                        }}
                        className="cursor-pointer hover:scale-110 transition-transform"
                        title={`Click to switch to ${c.color_war === "blue" ? "white" : "blue"}`}
                        data-testid={`cw-flip-cit-${c.id}`}
                      >
                        <Badge className={`text-[10px] ${c.color_war === "blue" ? "bg-blue-600 hover:bg-gray-500" : "bg-gray-500 hover:bg-blue-600"}`}>{c.color_war}</Badge>
                      </button>
                    </div>
                  ))}
                  {g.campers.map(c => (
                    <div key={c.id} className="flex items-center justify-between py-1 px-2 rounded">
                      <span className="text-xs">{c.name}</span>
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          const newTeam = c.color_war === "blue" ? "white" : "blue";
                          try {
                            const token = localStorage.getItem("token");
                            await axios.put(`${API}/color-war/flip`, { camper_id: c.id, team: newTeam }, { headers: { Authorization: `Bearer ${token}` } });
                            fetchStatus();
                          } catch { toast.error("Failed to flip team"); }
                        }}
                        className="cursor-pointer hover:scale-110 transition-transform"
                        title={`Click to switch to ${c.color_war === "blue" ? "white" : "blue"}`}
                        data-testid={`cw-flip-${c.id}`}
                      >
                        <Badge className={`text-[10px] ${c.color_war === "blue" ? "bg-blue-600 hover:bg-gray-500" : "bg-gray-500 hover:bg-blue-600"}`}>{c.color_war}</Badge>
                      </button>
                    </div>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        ))}
      </div>

      {/* PDF Dialog */}
      <Dialog open={pdfDialog} onOpenChange={setPdfDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Export Color War PDF</DialogTitle>
            <DialogDescription>Enter team names for the PDF report</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>White Team Name</Label>
              <Input value={whiteName} onChange={e => setWhiteName(e.target.value)} placeholder="e.g. The Polar Bears" data-testid="cw-pdf-white-name" />
            </div>
            <div>
              <Label>Blue Team Name</Label>
              <Input value={blueName} onChange={e => setBlueName(e.target.value)} placeholder="e.g. The Sharks" data-testid="cw-pdf-blue-name" />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => handleExportPDF("multi_page")} disabled={!whiteName || !blueName} data-testid="cw-pdf-export-multi-btn">
              <Download className="w-4 h-4 mr-1" /> Full Size (Multi-Page)
            </Button>
            <Button onClick={() => handleExportPDF("one_page")} disabled={!whiteName || !blueName} data-testid="cw-pdf-export-btn">
              <Download className="w-4 h-4 mr-1" /> One Page per Team
            </Button>
          </DialogFooter>
          <div className="border-t pt-3 mt-1 space-y-2">
            <Label className="text-xs font-bold">Or email the PDF (best on phones)</Label>
            <Input type="email" value={pdfEmail} onChange={e => setPdfEmail(e.target.value)} placeholder="you@example.com" data-testid="cw-pdf-email-input" />
            <div className="flex flex-col sm:flex-row gap-2">
              <Button variant="outline" size="sm" className="text-xs" onClick={() => handleEmailPDF("multi_page")} disabled={pdfEmailing || !whiteName || !blueName} data-testid="cw-pdf-email-multi-btn">
                <Mail className="w-3.5 h-3.5 mr-1" /> Email Full Size
              </Button>
              <Button variant="outline" size="sm" className="text-xs" onClick={() => handleEmailPDF("one_page")} disabled={pdfEmailing || !whiteName || !blueName} data-testid="cw-pdf-email-btn">
                <Mail className="w-3.5 h-3.5 mr-1" /> Email One Page
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function IntramuralsLanding({ token, onBack }) {
  const [subView, setSubView] = useState(null);
  const [playoffDiv, setPlayoffDiv] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  if (subView === "boys-teams") return <TeamSorterView division="boys" headers={headers} onBack={() => setSubView(null)} isEditable={true} />;
  if (subView === "girls-teams") return <TeamSorterView division="girls" headers={headers} onBack={() => setSubView(null)} isEditable={true} />;
  if (subView === "boys-schedule") return <ScorecardView headers={headers} onBack={() => setSubView(null)} isEditable={true} defaultTab="boys" />;
  if (subView === "girls-schedule") return <ScorecardView headers={headers} onBack={() => setSubView(null)} isEditable={true} defaultTab="girls" />;
  if (subView === "final-standings") return <FinalStandingsView headers={headers} onBack={() => setSubView(null)} />;
  if (subView === "playoffs" && playoffDiv) return <PlayoffsView division={playoffDiv} headers={headers} onBack={() => setPlayoffDiv(null)} isEditable={true} />;

  if (subView === "playoffs") {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setSubView(null)} className="px-2"><ArrowLeft className="w-4 h-4" /></Button>
          <h2 className="font-heading font-bold text-lg">Playoffs</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button onClick={() => setPlayoffDiv("boys")} className="p-5 rounded-2xl border-2 bg-blue-50 border-blue-200 hover:border-blue-400 hover:shadow-lg transition-all text-left" data-testid="admin-playoffs-boys">
            <span className="text-sm font-bold text-blue-800">Boys Playoffs</span>
          </button>
          <button onClick={() => setPlayoffDiv("girls")} className="p-5 rounded-2xl border-2 bg-rose-50 border-rose-200 hover:border-rose-400 hover:shadow-lg transition-all text-left" data-testid="admin-playoffs-girls">
            <span className="text-sm font-bold text-rose-800">Girls Playoffs</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onBack} className="px-2"><ArrowLeft className="w-4 h-4" /></Button>
          <h2 className="font-heading font-bold text-lg">Mariner Intramurals</h2>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {[
          { id: "boys-teams", label: "3rd & 4th Grade Boys", color: "blue" },
          { id: "girls-teams", label: "3rd & 4th Grade Girls", color: "rose" },
          { id: "boys-schedule", label: "Boys Schedule & Scorecard", color: "emerald" },
          { id: "girls-schedule", label: "Girls Schedule & Scorecard", color: "teal" },
          { id: "playoffs", label: "Playoffs", color: "amber" },
          { id: "final-standings", label: "Final Standings", color: "violet" },
        ].map(card => (
          <button key={card.id} onClick={() => setSubView(card.id)}
            className={`p-5 rounded-2xl border-2 bg-${card.color}-50 border-${card.color}-200 hover:border-${card.color}-400 hover:shadow-lg transition-all text-left`}
            data-testid={`admin-intramurals-${card.id}`}
          >
            <span className={`text-sm font-bold text-${card.color}-800`}>{card.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
